// i18n translations – RU (source), EN, ZH, ES
export type Language = 'ru' | 'en' | 'zh' | 'es'

export interface LanguageOption {
  code: Language
  label: string
  flag: string
  shortLabel: string
}

export const languages: LanguageOption[] = [
  { code: 'ru', label: 'Русский', flag: '🇷🇺', shortLabel: 'RU' },
  { code: 'en', label: 'English', flag: '🇬🇧', shortLabel: 'EN' },
  { code: 'zh', label: '中文', flag: '🇨🇳', shortLabel: '中文' },
  { code: 'es', label: 'Español', flag: '🇪🇸', shortLabel: 'ES' },
]

// Translation keys — Russian is the source of truth.
// EN/ZH/ES are stubs for now — fill in later.
const translations: Record<Language, Record<string, string>> = {
  ru: {
    // Header nav
    'nav.services': 'Услуги',
    'nav.astrologers': 'Астрологи',
    'nav.knowledge': 'Знания',
    'nav.pricing': 'Цены',
    'nav.marketplace': 'Маркетплейс',
    'nav.freeChart': 'Расчёт карты',
    'nav.freeChartFull': 'Бесплатный расчёт карты',
    'nav.login': 'Войти',
    'nav.cart': 'Корзина',

    // Services dropdown
    'services.natal': 'Натальная карта',
    'services.natal.desc': 'Глубокий анализ личности',
    'services.solar': 'Соляр (прогноз)',
    'services.solar.desc': 'Тенденции вашего года',
    'services.compatibility': 'Совместимость',
    'services.compatibility.desc': 'Синастрия для пар',
    'services.combo': 'Комбо: Натал + Соляр',
    'services.combo.desc': 'Полный анализ + прогноз на год',
    'services.forecast': 'Прогноз (Транзиты)',
    'services.forecast.desc': 'Ключевые периоды и даты',
    'services.consultation': 'Консультация',
    'services.consultation.desc': 'Живой разговор с астрологом',
    'services.marketplace': 'Маркетплейс',
    'services.marketplace.desc': 'Все услуги и консультации',

    // Knowledge dropdown
    'knowledge.interpretations': 'Трактовки',
    'knowledge.interpretations.desc': 'База знаний по астрологии',
    'knowledge.calendar': 'Астро-календарь',
    'knowledge.calendar.desc': 'Важные небесные события',
    'knowledge.blog': 'Блог',
    'knowledge.blog.desc': 'Статьи и прогнозы',
    'knowledge.faq': 'Вопросы и ответы',
    'knowledge.faq.desc': 'Помощь по платформе',

    // Profile dropdown
    'profile.dashboard': 'Личный кабинет',
    'profile.astrologer': 'Кабинет астролога',
    'profile.logout': 'Выйти',
    'profile.admin': 'Админ-панель',

    // Mobile menu sections
    'mobile.services': 'Услуги',
    'mobile.services.desc': 'Отчёты, прогнозы, консультации',
    'mobile.knowledge.desc': 'Календари, трактовки, блог',
    'mobile.platform': 'Платформа',
    'mobile.account': 'Аккаунт',
    'mobile.language': 'Язык',
    'mobile.client': 'Клиент',
    'mobile.astrologer': 'Астролог',

    // Footer
    'footer.services': 'Услуги',
    'footer.platform': 'Платформа',
    'footer.partners': 'Партнёрам',
    'footer.natal': 'Натальная карта',
    'footer.solar': 'Соляр (прогноз)',
    'footer.compatibility': 'Совместимость',
    'footer.pricingPackages': 'Цены и пакеты',
    'footer.astrologersCatalog': 'Каталог астрологов',
    'footer.interpretations': 'База трактовок',
    'footer.calendar': 'Астро-календарь',
    'footer.blog': 'Блог',
    'footer.forAstrologers': 'Для астрологов',
    'footer.advertising': 'Реклама на сайте',
    'footer.astrologerDashboard': 'Кабинет астролога',
    'footer.combo': 'Комбо-анализ',
    'footer.forecast': 'Прогноз транзитов',
    'footer.consultation': 'Консультация',
    'footer.marketplace': 'Маркетплейс',
    'footer.free': 'Бесплатное',
    'footer.astroCalendar': 'Астро-календарь',
    'footer.freeChart': 'Бесплатная карта',
    'footer.natalDemo': 'Натальная карта онлайн',
    'footer.reportPreview': 'Примеры отчётов',
    'footer.premiumReport': 'Premium отчёт',
    'footer.knowledge': 'Знания',
    'footer.faq': 'Вопросы и ответы',
    'footer.company': 'Компания',
    'footer.about': 'О нас',
    'footer.contact': 'Контакты',
    'footer.newsletter.title': 'Подпишитесь на астро-дайджест',
    'footer.newsletter.desc':
      'Еженедельные прогнозы, полезные статьи и эксклюзивные скидки.',
    'footer.newsletter.placeholder': 'Ваш email',
    'footer.newsletter.button': 'Подписаться',
    'footer.newsletter.success': 'Подписка оформлена! Ждите наши письма.',
    'footer.securePayment': 'Безопасная оплата',
    'footer.copyright': '© {year} Astriqo. Все права защищены.',
    'footer.privacy': 'Политика конфиденциальности',
    'footer.terms': 'Условия использования',
    'footer.offer': 'Публичная оферта',
    'footer.description':
      'Платформа для глубокого самопознания через профессиональную астрологию. Авто-отчёты и консультации с проверенными специалистами.',

    // Common UI
    'ui.learnMore': 'Подробнее',
    'ui.select': 'Выбрать',
    'ui.buy': 'Купить',
    'ui.profile': 'Профиль',
    'ui.order': 'Заказать',
    'ui.search': 'Поиск',
    'ui.filter': 'Фильтры',
    'ui.reset': 'Сбросить',
    'ui.back': 'Назад',
    'ui.next': 'Далее',
    'ui.close': 'Закрыть',
    'ui.loading': 'Загрузка...',

    // Legal Pages
    'legal.lastUpdated': 'Последнее обновление: 15 мая 2026 г.',
    'legal.backToHome': 'Главная',
    'legal.contactUs': 'Есть вопросы? Свяжитесь с нами',

    // Privacy Policy
    'legal.privacy.title': 'Политика конфиденциальности',
    'legal.privacy.s1.title': '1. Общие положения',
    'legal.privacy.s1.p1':
      'Настоящая Политика конфиденциальности (далее — «Политика») определяет порядок обработки и защиты персональных данных пользователей сервиса Astriqo (далее — «Сервис»). Мы с уважением относимся к конфиденциальности ваших данных и прилагаем все усилия для их защиты.',
    'legal.privacy.s2.title': '2. Какие данные мы собираем',
    'legal.privacy.s2.p1':
      'Для предоставления астрологических услуг мы собираем следующие данные:',
    'legal.privacy.s2.l1':
      'Регистрационные данные: имя, адрес электронной почты, номер телефона.',
    'legal.privacy.s2.l2':
      'Астрологические данные: дата, точное время и место рождения (необходимы для расчета натальной карты).',
    'legal.privacy.s2.l3':
      'Технические данные: IP-адрес, тип браузера, данные файлов cookie.',
    'legal.privacy.s3.title': '3. Использование данных',
    'legal.privacy.s3.p1':
      'Мы используем ваши данные исключительно для следующих целей:',
    'legal.privacy.s3.l1': 'Генерация персональных астрологических отчетов.',
    'legal.privacy.s3.l2': 'Организация консультаций с астрологами платформы.',
    'legal.privacy.s3.l3': 'Улучшение качества работы Сервиса.',
    'legal.privacy.s3.l4':
      'Отправка важных уведомлений (например, о готовности отчета).',
    'legal.privacy.s4.title': '4. Передача данных третьим лицам',
    'legal.privacy.s4.p1':
      'Мы не продаем и не передаем ваши персональные данные третьим лицам для маркетинговых целей. Ваши астрологические данные (дата, время, место рождения) могут быть переданы выбранному вами астрологу исключительно для проведения оплаченной консультации.',
    'legal.privacy.s5.title': '5. Безопасность данных',
    'legal.privacy.s5.p1':
      'Мы используем современные методы шифрования и защиты данных для предотвращения несанкционированного доступа. Все платежи обрабатываются через защищенные шлюзы сертифицированных платежных систем.',
    'legal.privacy.s6.title': '6. Ваши права',
    'legal.privacy.s6.p1': 'Вы имеете право в любой момент:',
    'legal.privacy.s6.l1':
      'Запросить копию всех ваших данных, хранящихся в Сервисе.',
    'legal.privacy.s6.l2':
      'Отредактировать или удалить свои данные через настройки Личного кабинета.',
    'legal.privacy.s6.l3':
      'Отозвать согласие на обработку персональных данных.',
    'legal.privacy.s7.title': '7. Контакты',
    'legal.privacy.s7.p1':
      'Если у вас есть вопросы по поводу настоящей Политики конфиденциальности, пожалуйста, свяжитесь с нами по адресу: privacy@astriqo.com.',

    // Terms of Service
    'legal.terms.title': 'Пользовательское соглашение',
    'legal.terms.s1.title': '1. Предмет соглашения',
    'legal.terms.s1.p1':
      'Настоящее Пользовательское соглашение (далее — «Соглашение») регулирует отношения между сервисом Astriqo (далее — «Сервис») и пользователем сети Интернет (далее — «Пользователь»), возникающие при использовании Сервиса.',
    'legal.terms.s2.title': '2. Характер предоставляемых услуг',
    'legal.terms.s2.p1':
      'Сервис предоставляет информационно-развлекательные услуги в сфере астрологии. Все генерируемые отчеты, прогнозы и консультации носят рекомендательный характер. Сервис не несет ответственности за решения, принятые Пользователем на основе полученной информации.',
    'legal.terms.s2.p2':
      'Важно: Астрологические консультации и отчеты не заменяют профессиональную медицинскую, психологическую, юридическую или финансовую помощь.',
    'legal.terms.s3.title': '3. Регистрация и аккаунт',
    'legal.terms.s3.p1':
      'Для доступа к полному функционалу Сервиса Пользователь должен пройти процедуру регистрации. Пользователь обязуется предоставить достоверную информацию о себе и несет ответственность за сохранность своих данных для входа (логин и пароль).',
    'legal.terms.s4.title': '4. Оплата и возврат средств',
    'legal.terms.s4.p1':
      'Оплата услуг (Premium-отчеты, консультации) производится на условиях 100% предоплаты. Поскольку автоматические отчеты являются цифровым контентом, доступ к которому предоставляется мгновенно, возврат средств за них по умолчанию не производится (в соответствии с законодательством о защите прав потребителей).',
    'legal.terms.s4.p2':
      'Возврат средств за консультацию с астрологом возможен при отмене не позднее чем за 24 часа до назначенного времени.',
    'legal.terms.s5.title': '5. Правила поведения на платформе',
    'legal.terms.s5.p1': 'Пользователю запрещается:',
    'legal.terms.s5.l1':
      'Использовать нецензурную лексику и оскорбления при общении с астрологами или службой поддержки.',
    'legal.terms.s5.l2':
      'Копировать, распространять или перепродавать материалы Сервиса (отчеты, статьи) без письменного разрешения.',
    'legal.terms.s5.l3':
      'Использовать автоматизированные скрипты (боты) для сбора информации с Сервиса.',
    'legal.terms.s6.title': '6. Ограничение ответственности',
    'legal.terms.s6.p1':
      'Сервис предоставляется «как есть» (as is). Мы не гарантируем, что Сервис будет соответствовать вашим требованиям, будет предоставляться непрерывно, быстро, надежно и без ошибок.',
    'legal.terms.s7.title': '7. Изменение условий',
    'legal.terms.s7.p1':
      'Администрация Сервиса оставляет за собой право вносить изменения в настоящее Соглашение в любой момент. Продолжение использования Сервиса после внесения изменений означает согласие Пользователя с новой редакцией Соглашения.',

    // Public Offer
    'legal.offer.title': 'Публичная оферта',
    'legal.offer.s1.title': '1. Общие положения',
    'legal.offer.s1.p1':
      'Настоящий документ является публичной офертой сервиса Astriqo (далее — «Исполнитель») и содержит все существенные условия предоставления информационно-консультационных услуг.',
    'legal.offer.s2.title': '2. Предмет оферты',
    'legal.offer.s2.p1':
      'Исполнитель обязуется оказать Заказчику услуги по предоставлению доступа к астрологическим отчетам и консультациям на платформе Astriqo, а Заказчик обязуется оплатить эти услуги.',
    'legal.offer.s3.title': '3. Порядок оказания услуг',
    'legal.offer.s3.p1':
      'Услуги оказываются в электронном виде через личный кабинет Заказчика на сайте. Доступ к цифровым товарам (отчетам) предоставляется автоматически после оплаты.',
    'legal.offer.s4.title': '4. Стоимость и порядок расчетов',
    'legal.offer.s4.p1':
      'Стоимость услуг указывается на сайте. Оплата производится безналичным путем через интегрированные платежные системы.',
    'legal.offer.s5.title': '5. Права и обязанности сторон',
    'legal.offer.s5.p1':
      'Исполнитель обязуется предоставлять услуги качественно и в срок. Заказчик обязуется предоставлять достоверные данные для расчетов (дата, время, место рождения).',
    'legal.offer.s6.title': '6. Ответственность сторон',
    'legal.offer.s6.p1':
      'Стороны несут ответственность за неисполнение обязательств в соответствии с действующим законодательством.',
    'legal.offer.s7.title': '7. Форс-мажор',
    'legal.offer.s7.p1':
      'Стороны освобождаются от ответственности за частичное или полное неисполнение обязательств по настоящему Договору, если это неисполнение явилось следствием обстоятельств непреодолимой силы.',
    'legal.offer.s8.title': '8. Разрешение споров',
    'legal.offer.s8.p1':
      'Все споры и разногласия решаются путем переговоров. При недостижении согласия спор передается на рассмотрение в суд.',

    // Encyclopedia page
    'enc.badge': 'База знаний',
    'enc.title': 'Астрологическая энциклопедия',
    'enc.subtitle':
      'Всё об астрологии: от основ до продвинутых техник. Образовательные статьи, трактовки натальной карты и описание наших услуг — {count} статей.',
    'enc.searchPlaceholder': 'Например: Венера в Тельце, Асцендент во Льве...',
    'enc.popular': 'Популярное:',
    'enc.results': 'Результаты ({count})',
    'enc.resetFilters': 'Сбросить фильтры',
    'enc.notFound': 'Ничего не найдено',
    'enc.notFoundHint': 'Попробуйте изменить запрос или выбрать другую букву.',
    'enc.showCategories': 'Показать все категории',
    'enc.readMore': 'Читать далее',
    'enc.learnMore': 'Подробнее',
    'enc.articles': 'статей',
    'enc.backToList': 'Назад к списку',
    'enc.alphabetTitle': 'Алфавитный указатель',
    'enc.keyFacts': 'Ключевые факты',
    'enc.commonMyths': 'Частые мифы',
    'enc.astrologerTip': 'Совет астролога',
    'enc.strengths': 'Сильные стороны',
    'enc.challenges': 'Сложности (в минусе)',
    'enc.relatedArticles': 'Связанные статьи',
    'enc.defaultTip':
      'Энергия этого положения требует осознанного подхода. Старайтесь опираться на сильные стороны и отслеживать проявление негативных качеств. Проработка начинается с осознания своих реакций.',
    'enc.ctaEncTitle': 'Примените эти знания на практике',
    'enc.ctaIntTitle': 'Узнайте, как это работает в вашей карте',
    'enc.ctaEncDesc':
      'Астрология лучше всего познаётся на собственном примере. Закажите персональный отчёт и узнайте всё о себе.',
    'enc.ctaIntDesc':
      'Трактовка одного положения — это лишь часть картины. Закажите полный авто-отчёт, чтобы увидеть, как планеты взаимодействуют друг с другом именно у вас.',
    'enc.defaultCta': 'Рассчитать натальную карту',
    'enc.basicsTitle': 'Основы астрологии',
    'enc.basicsCard': 'Базовые понятия',
    'enc.basicsDesc':
      'Что такое астрология, натальная карта, знаки зодиака, планеты и дома. Идеально для начинающих.',
    'enc.advancedCard': 'Продвинутые темы',
    'enc.advancedDesc':
      'Транзиты, соляр, синастрия, лунные узлы и ретроградные планеты. Для тех, кто хочет знать больше.',
    'enc.interpretationsTitle': 'Справочник трактовок',
    'enc.planetsSignsCard': 'Планеты в знаках',
    'enc.planetsSignsDesc':
      'Как проявляются энергии 10 планет (от Солнца до Плутона) в 12 знаках зодиака. Базовые кирпичики личности.',
    'enc.ascendantCard': 'Асцендент в знаках',
    'enc.ascendantDesc':
      'Ваша социальная маска, первое впечатление и фильтр восприятия мира. Как вас видят окружающие.',
    'enc.housesCard': 'Планеты в домах',
    'enc.housesDesc':
      'В каких сферах жизни планеты реализуют свой потенциал. Солнце, Луна, Меркурий, Венера и Марс в 12 домах.',
    'enc.aspectsCard': 'Аспекты',
    'enc.aspectsDesc':
      'Взаимодействие планет между собой: соединения, тригоны, квадратуры, оппозиции и секстили.',
    'enc.servicesTitle': 'Наши услуги',
    'enc.popularSearch1': 'Что такое натальная карта',
    'enc.popularSearch2': 'Солнце во Льве',
    'enc.popularSearch3': 'Аспекты в астрологии',

    // Free Chart Page
    'chart.title': 'Расчёт астрологической карты',
    'chart.subtitle':
      'Выберите услугу, введите данные рождения и получите точный расчёт положений планет или подробный PDF-отчёт.',
    'chart.breadcrumb': 'Расчёт карты',
    'chart.rating': '4.9 рейтинг',
    'chart.calculations': '50 000+ расчётов',
    'chart.freeBeta': 'Бесплатный Beta-расчёт',
    'chart.step.service': 'Услуга',
    'chart.step.package': 'Пакет',
    'chart.step.data': 'Данные',
    'chart.step.result': 'Результат',
    'chart.step1.title': 'Выберите услугу',
    'chart.step1.subtitle': 'Какой расчёт вы хотите выполнить?',
    'chart.popular': 'Популярное',
    'chart.new': 'Новинка',
    'chart.fromPrice': 'от 2 990 ₽',
    'chart.freePremium': 'Бесплатно + Premium',
    'chart.step2.titleConsultation': 'Выберите формат',
    'chart.step2.titleReport': 'Выберите тариф',
    'chart.step2.forService': 'Для услуги «{name}»',
    'chart.tier.free.title': 'Beta (базовый)',
    'chart.tier.free.badge': 'Бесплатно',
    'chart.tier.free.desc':
      'Краткий расчёт основных показателей без текстовой расшифровки',
    'chart.tier.pro.title': 'Premium (разовый)',
    'chart.tier.pro.badge': 'Рекомендуем',
    'chart.tier.pro.desc':
      'Полный PDF-отчёт на 20-40 страниц с подробным анализом и рекомендациями',
    'chart.tier.package.title': 'Premium (пакет)',
    'chart.tier.package.badge': 'Выгодно',
    'chart.tier.package.desc':
      'Пакет отчётов со скидкой. Можно использовать для любых услуг платформы',
    'chart.tier.package.fromPrice': 'от 1 590 ₽/шт',
    'chart.bundle.hit': 'Хит',
    'chart.bundle.reports': '{count} отчётов',
    'chart.consult.short.title': 'Короткая консультация',
    'chart.consult.short.desc': '30 минут • 1 конкретный вопрос',
    'chart.consult.short.badge': 'Экспресс',
    'chart.consult.standard.title': 'Стандартная консультация',
    'chart.consult.standard.desc': '60 минут • Полный разбор карты',
    'chart.consult.standard.badge': 'Рекомендуем',
    'chart.consult.extended.title': 'Расширенная консультация',
    'chart.consult.extended.desc': '90 минут • Запись + подробные рекомендации',
    'chart.consult.extended.badge': 'Максимум',
    'chart.step3.title': 'Введите данные',
    'chart.step3.subtitle': 'Для расчёта услуги «{name}»',
    'chart.field.name': 'Имя (необязательно)',
    'chart.field.namePlaceholder': 'Например, Анна',
    'chart.field.date': 'Дата рождения *',
    'chart.field.time': 'Время рождения *',
    'chart.field.timeHint': 'Если не знаете, укажите 12:00',
    'chart.field.city': 'Город рождения *',
    'chart.field.cityPlaceholder': 'Например, Москва',
    'chart.field.person1': 'Первый партнер',
    'chart.field.person2': 'Второй партнер',
    'chart.field.forecastYear': 'Год прогноза *',
    'chart.field.forecastPeriod': 'Период прогноза *',
    'chart.period.3months': '3 месяца',
    'chart.period.6months': '6 месяцев',
    'chart.period.1year': '1 год',
    'chart.btn.calculate': 'Рассчитать',
    'chart.btn.book': 'Записаться',
    'chart.btn.back': 'Назад',
    'chart.btn.next': 'Далее',
    'chart.calculating.title': 'Анализируем данные...',
    'chart.calculating.subtitle': 'Рассчитываем точные положения планет',
    'chart.result.fullReport': 'Полный отчёт',
    'chart.result.basicReport': 'Базовый отчёт',
    'chart.result.newCalc': 'Новый расчёт',
    'chart.result.planets': 'Положения планет',
    'chart.result.planet': 'Планета',
    'chart.result.sign': 'Знак',
    'chart.result.degree': 'Градус',
    'chart.result.briefInterpretation': 'Краткая расшифровка',
    'chart.result.wantFull': 'Хотите полный анализ?',
    'chart.result.fullDesc':
      'Получите подробный PDF-отчёт на 30-40 страниц с глубокой расшифровкой всех показателей, кармических задач и персональными рекомендациями.',
    'chart.result.getPremium': 'Получить Premium отчёт',
    'chart.result.questions': 'Остались вопросы?',
    'chart.result.consultDesc': 'Запишитесь на живую консультацию с астрологом',
    'chart.result.chooseAstrologer': 'Выбрать астролога',
    'chart.trust.accurate.title': 'Точные расчёты',
    'chart.trust.accurate.desc':
      'Используем профессиональные алгоритмы Swiss Ephemeris для максимальной точности.',
    'chart.trust.privacy.title': 'Конфиденциальность',
    'chart.trust.privacy.desc':
      'Ваши данные рождения защищены и используются только для расчёта карты.',
    'chart.trust.instant.title': 'Мгновенный результат',
    'chart.trust.instant.desc':
      'Получите базовый расчёт и положения планет за несколько секунд.',
    'chart.planet.sun': 'Солнце',
    'chart.planet.moon': 'Луна',
    'chart.planet.mercury': 'Меркурий',
    'chart.planet.venus': 'Венера',
    'chart.planet.mars': 'Марс',
    'chart.planet.jupiter': 'Юпитер',
    'chart.planet.saturn': 'Сатурн',
    'chart.sign.aries': 'Овен',
    'chart.sign.taurus': 'Телец',
    'chart.sign.gemini': 'Близнецы',
    'chart.sign.pisces': 'Рыбы',
    'chart.sign.sagittarius': 'Стрелец',
    'chart.sign.capricorn': 'Козерог',

    // Checkout page
    'checkout.title': 'Оформление заказа',
    'checkout.breadcrumb.home': 'Главная',
    'checkout.breadcrumb.marketplace': 'Маркетплейс',
    'checkout.breadcrumb.title': 'Оформление заказа',
    'checkout.step.product': 'Товар',
    'checkout.step.checkout': 'Оформление',
    'checkout.step.payment': 'Оплата',
    'checkout.addons.title': 'Дополнительные сервисы',
    'checkout.addons.subtitle': 'Улучшите свой заказ дополнительными опциями',
    'checkout.addons.reports': 'Отчёты',
    'checkout.addons.consultations': 'Консультации',
    'checkout.addons.packages': 'Пакеты',
    'checkout.addons.natal': 'Натальная карта Premium',
    'checkout.addons.compatibility': 'Совместимость Premium',
    'checkout.addons.solar': 'Соляр Premium',
    'checkout.addons.forecast': 'Прогноз (Транзиты) Premium',
    'checkout.addons.combo': 'Комбо: Натал + Соляр',
    'checkout.addons.consult30': 'Консультация 30 мин',
    'checkout.addons.consult60': 'Консультация 60 мин',
    'checkout.addons.consult90': 'Консультация 90 мин',
    'checkout.addons.pkg20': 'Пакет 20 отчётов',
    'checkout.addons.pkg50': 'Пакет 50 отчётов',
    'checkout.addons.pkg100': 'Пакет 100 отчётов',
    'checkout.auth.title': 'Войдите для оформления заказа',
    'checkout.auth.desc':
      'Для получения отчётов и сохранения данных необходима регистрация. Ваши данные рождения и отчёты будут доступны в личном кабинете.',
    'checkout.auth.login': 'Войти',
    'checkout.auth.register': 'Регистрация',
    'checkout.auth.hint': 'Быстрая регистрация через email или соцсети',
    'checkout.payment.title': 'Способ оплаты',
    'checkout.payment.card': 'Банковская карта',
    'checkout.payment.sbp': 'СБП',
    'checkout.card.number': 'Номер карты',
    'checkout.card.expiry': 'Срок действия',
    'checkout.card.cvc': 'CVC',
    'checkout.card.name': 'Имя на карте',
    'checkout.sbp.hint': 'Отсканируйте QR-код в приложении вашего банка',
    'checkout.sbp.qr': 'QR-код для оплаты',
    'checkout.contact.title': 'Контактные данные',
    'checkout.contact.name': 'Имя',
    'checkout.contact.namePlaceholder': 'Ваше имя',
    'checkout.contact.email': 'Email',
    'checkout.contact.phone': 'Телефон (необязательно)',
    'checkout.contact.phonePlaceholder': '+7 (999) 123-45-67',
    'checkout.contact.privacy':
      'Ваши данные надежно защищены и не передаются третьим лицам',
    'checkout.order.title': 'Ваш заказ',
    'checkout.order.addMore': 'Добавить ещё услугу',
    'checkout.order.addonsLabel': 'Доп. сервисы',
    'checkout.order.empty': 'Корзина пуста',
    'checkout.order.autoGeneration': 'Автоматическая генерация',
    'checkout.promo.placeholder': 'Промокод (напр. ASTRO10)',
    'checkout.promo.apply': 'Применить',
    'checkout.promo.applied': '✓',
    'checkout.promo.success': 'Промокод ASTRO10 применён! Скидка 10%',
    'checkout.total.service': 'Основная услуга',
    'checkout.total.services': 'Услуги ({count})',
    'checkout.total.addons': 'Доп. сервисы ({count})',
    'checkout.total.discount': 'Скидка',
    'checkout.total.total': 'Итого',
    'checkout.payment.hint':
      'Все товары в корзине оплачиваются одним платежом. Вы получите доступ ко всем услугам сразу после оплаты.',
    'checkout.payment.hintBold': 'одним платежом',
    'checkout.agree.text': 'Я согласен с',
    'checkout.agree.terms': 'условиями оферты',
    'checkout.agree.and': 'и',
    'checkout.agree.privacy': 'политикой конфиденциальности',
    'checkout.pay.processing': 'Обработка...',
    'checkout.pay.loginRequired': 'Войдите для оплаты',
    'checkout.pay.button': 'Оплатить {price}',
    'checkout.trust.ssl': 'Безопасная оплата. 256-bit SSL шифрование.',
    'checkout.trust.refund': 'Гарантия возврата средств в течение 14 дней',
    'checkout.trust.noSave': 'Данные карты не сохраняются на сервере',
    'checkout.mobile.total': 'Итого',
    'checkout.mobile.pay': 'Оплатить',
    'checkout.login.required': 'Необходимо войти или зарегистрироваться',
    'checkout.agree.required': 'Примите условия для продолжения',
    'checkout.success': 'Оплата прошла успешно! Заказ оформлен.',
    'checkout.successMobile': 'Оплата прошла успешно!',
    'checkout.back': 'Назад',
    'checkout.delete': 'Удалить',

    // Auth page
    'auth.login.title': 'С возвращением',
    'auth.login.desc': 'Войдите, чтобы продолжить работу',
    'auth.register.title': 'Создать аккаунт',
    'auth.register.desc': 'Присоединяйтесь к платформе Astriqo',
    'auth.tab.login': 'Вход',
    'auth.tab.register': 'Регистрация',
    'auth.name': 'Имя',
    'auth.name.placeholder': 'Ваше имя',
    'auth.email': 'Email',
    'auth.email.placeholder': 'mail@example.com',
    'auth.password': 'Пароль',
    'auth.password.placeholder': '••••••••',
    'auth.forgot': 'Забыли пароль?',
    'auth.agree.text': 'Я согласен с',
    'auth.agree.terms': 'условиями использования',
    'auth.agree.and': 'и',
    'auth.agree.privacy': 'политикой конфиденциальности',
    'auth.btn.login': 'Войти',
    'auth.btn.register': 'Зарегистрироваться',
    'auth.social.title': 'Или продолжить через',
    'auth.success.login': 'Успешный вход!',
    'auth.success.register': 'Регистрация прошла успешно!',

    // Order Confirmation
    'order.success.title': 'Оплата прошла успешно!',
    'order.success.desc':
      'Спасибо за ваш заказ. Мы отправили чек и детали заказа на вашу электронную почту.',
    'order.details.number': 'Номер заказа',
    'order.details.date': 'Дата заказа',
    'order.details.title': 'Детали заказа',
    'order.details.astrologer': 'Астролог: {name}',
    'order.details.total': 'Итого оплачено',
    'order.next.title': 'Что дальше?',

    'order.next.pkg.1.title': 'Пакет активирован',
    'order.next.pkg.1.desc':
      'Отчёты добавлены в ваш личный кабинет. Вы можете использовать их в любое время.',
    'order.next.pkg.2.title': 'Создайте первый отчёт',
    'order.next.pkg.2.desc':
      'Перейдите в раздел услуг, выберите нужный анализ и примените отчёт из пакета при оформлении.',

    'order.next.report.1.title': 'Отчёт формируется',
    'order.next.report.1.desc':
      'Наша система анализирует ваши данные. Это займет несколько минут.',
    'order.next.report.2.title': 'Проверьте почту',
    'order.next.report.2.desc':
      'Мы отправим PDF-файл на ваш email, а также сохраним его в личном кабинете.',

    'order.next.consult.1.title': 'Связь с астрологом',
    'order.next.consult.1.desc':
      'В течение 24 часов {name} напишет вам в чат платформы для согласования времени звонка.',
    'order.next.consult.2.title': 'Подготовка данных',
    'order.next.consult.2.desc':
      'Пожалуйста, подготовьте точное время и место вашего рождения — это необходимо для построения карты.',

    'order.btn.dashboard': 'Перейти в личный кабинет',
    'order.btn.catalog': 'Вернуться в каталог',

    // Admin Analytics Dashboard
    'admin.analytics.title': 'Аналитика платформы',
    'admin.analytics.subtitle': 'Обзор ключевых показателей и метрик',
    'admin.analytics.editing': 'Режим редактирования',
    'admin.analytics.overview': 'Обзор',
    'admin.analytics.saveDashboard': 'Сохранить макет',
    'admin.analytics.customize': 'Настроить дашборд',
    'admin.analytics.layoutSaved': 'Макет дашборда сохранен',
    'admin.analytics.hiddenWidgets': 'Скрытые виджеты',
    'admin.analytics.emptyTitle': 'Дашборд пуст',
    'admin.analytics.emptyDesc': 'Все виджеты аналитики скрыты.',
    'admin.analytics.period.7d': '7 дней',
    'admin.analytics.period.30d': '30 дней',
    'admin.analytics.period.3m': '3 месяца',
    'admin.analytics.period.1y': 'Год',
    'admin.analytics.size.25': '25%',
    'admin.analytics.size.50': '50%',
    'admin.analytics.size.75': '75%',
    'admin.analytics.size.100': '100%',

    // Widget titles
    'admin.widget.tracking': 'Счётчики и интеграции',
    'admin.widget.tracking.desc':
      'Добавьте коды аналитики для отслеживания трафика',
    'admin.widget.tracking.connected': 'Подключено: {count} из {total}',
    'admin.widget.tracking.active': '{count} активно',
    'admin.widget.tracking.check': 'Проверить подключение',
    'admin.widget.tracking.hint':
      'Коды сохраняются автоматически. Включите переключатель, чтобы активировать счётчик.',
    'admin.widget.tracking.noActive':
      'Нет активных счётчиков. Введите ID и включите переключатель.',
    'admin.widget.tracking.saved': 'Сохранено: {names}',
    'admin.widget.tracking.enterFirst': 'Сначала введите ID счётчика',
    'admin.widget.tracking.disabled': '{name} отключён',
    'admin.widget.tracking.enabled': '{name} подключён',

    'admin.widget.kpi': 'Ключевые показатели (KPI)',
    'admin.widget.kpi.revenue': 'Общая выручка',
    'admin.widget.kpi.users': 'Активных пользователей',
    'admin.widget.kpi.orders': 'Всего заказов',
    'admin.widget.kpi.conversion': 'Конверсия в заказ',
    'admin.widget.kpi.toFinance': 'В финансы',
    'admin.widget.kpi.toUsers': 'Пользователи',
    'admin.widget.kpi.toOrders': 'Все заказы',

    'admin.widget.revenue': 'Выручка по месяцам',
    'admin.widget.revenue.desc': 'Динамика за последние полгода',
    'admin.widget.revenue.detail': 'Детализация',
    'admin.widget.revenue.period': '+67.7% за период',

    'admin.widget.funnel': 'Воронка конверсии',
    'admin.widget.funnel.desc': 'Путь пользователя',
    'admin.widget.funnel.visitors': 'Посетители',
    'admin.widget.funnel.registrations': 'Регистрации',
    'admin.widget.funnel.checkout': 'Начали оформление',
    'admin.widget.funnel.payments': 'Успешные оплаты',

    'admin.widget.traffic': 'Источники трафика',
    'admin.widget.traffic.desc': 'Откуда приходят пользователи',
    'admin.widget.traffic.tooltip':
      'Данные собираются автоматически из подключенных счётчиков и UTM-меток заказов.',
    'admin.widget.traffic.settings': 'Настройки трекинга',

    'admin.widget.geo': 'География',
    'admin.widget.geo.desc': 'Топ городов',
    'admin.widget.geo.tooltip':
      'Данные собираются автоматически на основе IP-адресов посетителей и данных из заказов.',

    'admin.widget.services': 'Заказы по услугам',
    'admin.widget.services.desc': 'Распределение по типам',
    'admin.widget.services.all': 'Все услуги',

    'admin.widget.subs': 'Подписки',
    'admin.widget.subs.desc': 'Распределение пользователей',
    'admin.widget.subs.manage': 'Управление',
    'admin.widget.subs.pro': 'Pro тариф',
    'admin.widget.subs.plus': 'Plus тариф',
    'admin.widget.subs.free': 'Бесплатный',
    'admin.widget.subs.ltv': 'Средний чек (LTV)',

    'admin.widget.astrologers': 'Топ астрологов',
    'admin.widget.astrologers.desc': 'Лидеры по количеству заказов',
    'admin.widget.astrologers.catalog': 'Каталог астрологов',
    'admin.widget.astrologers.col.rank': '#',
    'admin.widget.astrologers.col.name': 'Астролог',
    'admin.widget.astrologers.col.spec': 'Специализация',
    'admin.widget.astrologers.col.rating': 'Рейтинг',
    'admin.widget.astrologers.col.orders': 'Заказов',
    'admin.widget.astrologers.col.revenue': 'Выручка',

    'admin.widget.metrics': 'Быстрые метрики',
    'admin.widget.metrics.avgTime': 'Ср. время на сайте',
    'admin.widget.metrics.bounceRate': 'Показатель отказов',
    'admin.widget.metrics.repeatPurchase': 'Повторные покупки',
    'admin.widget.metrics.nps': 'NPS (Лояльность)',
    'admin.widget.metrics.vsPrev': 'к пр. периоду',

    'admin.widget.realtime': 'Онлайн сейчас',
    'admin.widget.realtime.users': 'Пользователей онлайн',
    'admin.widget.realtime.views': '{count} просмотров/мин',
    'admin.widget.realtime.tooltip':
      'Обновляется в реальном времени на основе активных сессий из счётчиков.',

    'admin.widget.alerts': 'Уведомления',
    'admin.widget.alerts.desc': 'Важные события и аномалии',
    'admin.widget.alerts.tooltip':
      'Алерты генерируются автоматически при обнаружении аномалий в метриках и важных событиях.',
    'admin.widget.alerts.new': '{count} новых',
    'admin.widget.alerts.bounceUp': 'Рост отказов на мобильных',
    'admin.widget.alerts.bounceUpDesc':
      'Показатель отказов на мобильных вырос на 8% за последние 3 дня',
    'admin.widget.alerts.revenueRecord': 'Рекорд выручки',
    'admin.widget.alerts.revenueRecordDesc':
      'Вчерашняя выручка (87 400 ₽) — максимум за последний месяц',
    'admin.widget.alerts.trafficSpike': 'Всплеск трафика из Telegram',
    'admin.widget.alerts.trafficSpikeDesc':
      'Переходы из Telegram выросли на 340% — проверьте источник',
    'admin.widget.alerts.slowResponse': 'Долгие ответы астрологов',
    'admin.widget.alerts.slowResponseDesc':
      'Среднее время ответа выросло до 18 часов (норма: 12ч)',
    'admin.widget.alerts.hoursAgo': '{count} часа назад',
    'admin.widget.alerts.dayAgo': '1 день назад',

    'admin.widget.devices': 'Устройства',
    'admin.widget.devices.desc': 'Типы устройств',
    'admin.widget.devices.tooltip':
      'Данные из Яндекс.Метрики / Google Analytics. Требуется подключение счётчика.',
    'admin.widget.devices.mobile': 'Мобильные',
    'admin.widget.devices.desktop': 'Десктоп',
    'admin.widget.devices.tablet': 'Планшеты',

    'admin.widget.pages': 'Популярные страницы',
    'admin.widget.pages.desc': 'Топ по просмотрам за период',
    'admin.widget.pages.tooltip':
      'Данные из подключенных счётчиков аналитики (Яндекс.Метрика / GA).',
    'admin.widget.pages.seo': 'SEO-отчёт',

    'admin.widget.content': 'Контент-аналитика',
    'admin.widget.content.desc': 'Статистика блога',
    'admin.widget.content.tooltip':
      'Данные из внутренней CMS и счётчиков аналитики. Просмотры и комментарии обновляются автоматически.',
    'admin.widget.content.manage': 'Управление блогом',
    'admin.widget.content.views': 'Просмотров',
    'admin.widget.content.avgRead': 'Ср. чтение',

    'admin.widget.reviews': 'Отзывы и рейтинг',
    'admin.widget.reviews.desc': 'Обратная связь пользователей',
    'admin.widget.reviews.tooltip':
      'Данные из отзывов пользователей на платформе. Рейтинг обновляется автоматически.',
    'admin.widget.reviews.moderation': 'Модерация',
    'admin.widget.reviews.count': '{count} отзывов',
    'admin.widget.reviews.positive': 'Позитивных',
    'admin.widget.reviews.neutral': 'Нейтральных',
    'admin.widget.reviews.negative': 'Негативных',

    'admin.widget.email': 'Email-рассылки',
    'admin.widget.email.desc': 'Статистика кампаний',
    'admin.widget.email.tooltip':
      'Данные из сервиса email-рассылок. Статистика обновляется после каждой отправки.',
    'admin.widget.email.manage': 'Рассылки',
    'admin.widget.email.opens': 'Открытия',
    'admin.widget.email.clicks': 'Клики',
    'admin.widget.email.sent': 'Отправлено: {count}',
    'admin.widget.email.unsubs': 'Отписки: {count}',
    'admin.widget.email.openRate': '{rate}% откр.',
    'admin.widget.email.clickRate': '{rate}% клик',

    'admin.widget.cac': 'Стоимость привлечения (CAC)',
    'admin.widget.cac.tooltip':
      'CAC — средняя стоимость привлечения одного платящего клиента через каждый канал.',
    'admin.widget.cac.avg': 'Средний CAC: {value} ₽',
    'admin.widget.cac.marketing': 'Маркетинг',
    'admin.widget.cac.free': 'Бесплатно',
    'admin.widget.cac.orders': '{count} заказов',

    // Astrologer Dashboard — Shell
    'astrodash.title': 'Кабинет астролога',
    'astrodash.group.main': 'Главное',
    'astrodash.group.products': 'Продукты',
    'astrodash.group.business': 'Бизнес',
    'astrodash.group.settings': 'Настройки',
    'astrodash.tab.overview': 'Обзор',
    'astrodash.tab.orders': 'Заказы',
    'astrodash.tab.consultations': 'Консультации',
    'astrodash.tab.chat': 'Чат с клиентами',
    'astrodash.tab.services': 'Услуги',
    'astrodash.tab.marketplace': 'Курсы',
    'astrodash.tab.calendar': 'Расписание',
    'astrodash.tab.finances': 'Финансы',
    'astrodash.tab.promotion': 'Продвижение',
    'astrodash.tab.reviews': 'Отзывы',
    'astrodash.tab.stats': 'Статистика',
    'astrodash.tab.profile': 'Публичный профиль',
    'astrodash.tab.settings': 'Настройки и безопасность',
    'astrodash.tab.subscription': 'Подписка',
    'astrodash.logout': 'Выйти',
    'astrodash.backToSite': 'Вернуться на сайт',
    'astrodash.menu': 'Меню',
    'astrodash.welcome': 'Привет, {name}!',
    'astrodash.role': 'Астролог',

    // Astrologer Dashboard — Overview Tab
    'astrodash.overview.title': 'Обзор',
    'astrodash.overview.subtitle': 'Ключевые показатели за сегодня',
    'astrodash.overview.todayIncome': 'Доход сегодня',
    'astrodash.overview.newOrders': 'Новые заказы',
    'astrodash.overview.activeConsultations': 'Активные консультации',
    'astrodash.overview.profileViews': 'Просмотры профиля',
    'astrodash.overview.rating': 'Рейтинг',
    'astrodash.overview.position': 'Позиция в каталоге',
    'astrodash.overview.quickActions': 'Быстрые действия',
    'astrodash.overview.recentOrders': 'Последние заказы',
    'astrodash.overview.viewAll': 'Смотреть все',
    'astrodash.overview.noOrders': 'Заказов пока нет',
    'astrodash.overview.schedule': 'Расписание на сегодня',
    'astrodash.overview.noSchedule': 'На сегодня записей нет',
    'astrodash.overview.tips': 'Советы по улучшению',
    'astrodash.overview.fillProfile': 'Заполните профиль на 100%',
    'astrodash.overview.addServices': 'Добавьте больше услуг',
    'astrodash.overview.enablePromo': 'Включите продвижение',

    // Astrologer Dashboard — Promotion Tab
    'astrodash.promo.title': 'Продвижение профиля',
    'astrodash.promo.subtitle': 'Увеличивайте заказы и доход',
    'astrodash.promo.budget': 'Рекламный бюджет',
    'astrodash.promo.available': 'доступно',
    'astrodash.promo.spentMonth': 'Потрачено за месяц',
    'astrodash.promo.remaining': 'Остаток',
    'astrodash.promo.topUp': 'Пополнить баланс',
    'astrodash.promo.hint':
      'Реклама списывается с рекламного бюджета. Если средств не хватает — пополните баланс.',
    'astrodash.promo.tariff': 'Тариф',
    'astrodash.promo.position': 'Позиция',
    'astrodash.promo.views': 'Просмотры',
    'astrodash.promo.conversion': 'Конверсия',
    'astrodash.promo.adIncome': 'Доход с рекламы',
    'astrodash.promo.adOrders': 'Заказы с рекламы',
    'astrodash.promo.howToIncrease': 'Как увеличить доход',
    'astrodash.promo.proAdvice':
      'Перейдите на тариф PRO и получите приоритет в каталоге. По статистике, PRO-астрологи получают на 40% больше заказов.',
    'astrodash.promo.upgradePro': 'Перейти на PRO',
    'astrodash.promo.enableAds': 'Включить рекламу',
    'astrodash.promo.launchAd': 'Запустить рекламу',
    'astrodash.promo.boostProfile': 'Поднять профиль',
    'astrodash.promo.enableMailing': 'Включить рассылку',
    'astrodash.promo.upgradeTariff': 'Улучшить тариф',
    'astrodash.promo.myAdOrders': 'Мои рекламные заказы',
    'astrodash.promo.howAdsWork': 'Как работает реклама?',
    'astrodash.promo.hideHelp': 'Скрыть справку',
    'astrodash.promo.newOrder': 'Новый заказ',
    'astrodash.promo.step1.title': 'Оформление и оплата',
    'astrodash.promo.step1.desc':
      'Выберите формат, загрузите материалы и оплатите с рекламного баланса.',
    'astrodash.promo.step2.title': 'Модерация (1-12 часов)',
    'astrodash.promo.step2.desc':
      'Администратор проверит материалы. Результат придет в системные уведомления (колокольчик).',
    'astrodash.promo.step3.title': 'Запуск и статистика',
    'astrodash.promo.step3.desc':
      'После одобрения реклама запустится автоматически. Отслеживайте просмотры и клики здесь.',
    'astrodash.promo.service': 'Услуга',
    'astrodash.promo.type': 'Тип',
    'astrodash.promo.status': 'Статус',
    'astrodash.promo.period': 'Период',
    'astrodash.promo.cost': 'Стоимость',
    'astrodash.promo.result': 'Результат',
    'astrodash.promo.clicks': 'кликов',
    'astrodash.promo.orders': 'заказов',
    'astrodash.promo.banner': 'Баннер',
    'astrodash.promo.mailing': 'Рассылка',
    'astrodash.promo.pin': 'Закреп',
    'astrodash.promo.statusActive': 'Активен',
    'astrodash.promo.statusCompleted': 'Завершён',
    'astrodash.promo.statusModeration': 'На модерации',
    'astrodash.promo.statusPayment': 'Ожидает оплаты',
    'astrodash.promo.statusApproved': 'Одобрен',
    'astrodash.promo.statusRejected': 'Отклонён',
    'astrodash.promo.onModeration': 'Заказ на модерации',
    'astrodash.promo.awaitingPayment': 'Ожидает оплаты',
    'astrodash.promo.rejected': 'Заказ отклонён',
    'astrodash.promo.checkingAd':
      'Ваша реклама проверяется администратором. Обычно это занимает 1–12 часов.',
    'astrodash.promo.moderationPassed':
      'Модерация пройдена! Оплатите заказ для запуска рекламы.',
    'astrodash.promo.moderationFailed':
      'Модерация не пройдена. Исправьте замечания и отправьте повторно.',
    'astrodash.promo.rejectionReason': 'Причина отклонения',
    'astrodash.promo.stepCreated': 'Оформлен',
    'astrodash.promo.stepModeration': 'Модерация',
    'astrodash.promo.stepPayment': 'Оплата',
    'astrodash.promo.stepLaunch': 'Запуск',
    'astrodash.promo.inProgress': 'В процессе...',
    'astrodash.promo.awaitingPaymentShort': 'Ожидает оплаты',
    'astrodash.promo.awaiting': 'Ожидается',
    'astrodash.promo.paid': 'Оплачено',
    'astrodash.promo.payBtn': 'Оплатить {price} ₽',
    'astrodash.promo.fixAndResend': 'Исправить и отправить',
    'astrodash.promo.repeat': 'Повторить',
    'astrodash.promo.extend': 'Продлить',
    'astrodash.promo.income': 'Доход',
    'astrodash.promo.createAd': 'Создать рекламу',
    'astrodash.promo.selectSlot':
      'Выберите рекламный слот. Каждый формат показывается в разных местах сайта.',
    'astrodash.promo.format': 'Формат',
    'astrodash.promo.materials': 'Материалы',
    'astrodash.promo.payment': 'Оплата',
    'astrodash.promo.next': 'Далее',
    'astrodash.promo.back': 'Назад',
    'astrodash.promo.reach': 'Охват',
    'astrodash.promo.requirements': 'Требования',
    'astrodash.promo.adTitle': 'Заголовок рекламы',
    'astrodash.promo.adTitlePlaceholder':
      'Например: Профессиональный разбор натальной карты',
    'astrodash.promo.adDesc': 'Описание',
    'astrodash.promo.adDescPlaceholder': 'Кратко опишите ваше предложение',
    'astrodash.promo.adLink': 'Ссылка',
    'astrodash.promo.adImage': 'Загрузить изображение',
    'astrodash.promo.replaceImage': 'Заменить изображение',
    'astrodash.promo.orderSummary': 'Сводка заказа',
    'astrodash.promo.total': 'К оплате',
    'astrodash.promo.adBalance': 'Рекламный баланс',
    'astrodash.promo.insufficientFunds':
      'Недостаточно средств. Пополните баланс на {amount} ₽',
    'astrodash.promo.remainingAfter': 'Остаток после оплаты',
    'astrodash.promo.payAndSubmit': 'Оплатить и отправить',
    'astrodash.promo.orderPaid':
      'Заказ оплачен и отправлен на модерацию. Вы получите уведомление о статусе.',
    'astrodash.promo.cancel': 'Отмена',

    // Common astrologer dashboard
    'astrodash.common.save': 'Сохранить',
    'astrodash.common.cancel': 'Отмена',
    'astrodash.common.delete': 'Удалить',
    'astrodash.common.edit': 'Редактировать',
    'astrodash.common.add': 'Добавить',
    'astrodash.common.search': 'Поиск',
    'astrodash.common.filter': 'Фильтр',
    'astrodash.common.reset': 'Сбросить',
    'astrodash.common.noData': 'Нет данных',
    'astrodash.common.loading': 'Загрузка...',
    'astrodash.common.confirm': 'Подтвердить',
    'astrodash.common.close': 'Закрыть',
    'astrodash.common.viewAll': 'Смотреть все',
    'astrodash.common.today': 'Сегодня',
    'astrodash.common.week': 'Неделя',
    'astrodash.common.month': 'Месяц',
    'astrodash.common.year': 'Год',
    'astrodash.common.from': 'От',
    'astrodash.common.to': 'До',
    'astrodash.common.total': 'Итого',
    'astrodash.common.status': 'Статус',
    'astrodash.common.actions': 'Действия',
    'astrodash.common.details': 'Подробнее',
    'astrodash.common.back': 'Назад',
    'astrodash.common.next': 'Далее',

    // ═══════════════════════════════════════════
    // User Dashboard (Personal Account)
    // ═══════════════════════════════════════════

    // Sidebar / Navigation
    'userdash.group.main': 'ОСНОВНОЕ',
    'userdash.group.astrology': 'МОЯ АСТРОЛОГИЯ',
    'userdash.group.purchases': 'ПОКУПКИ',
    'userdash.group.account': 'АККАУНТ',
    'userdash.tab.overview': 'Обзор',
    'userdash.tab.orders': 'Мои заказы',
    'userdash.tab.chat': 'Сообщения',
    'userdash.tab.reports': 'Мои отчёты',
    'userdash.tab.profile': 'Мой астропрофиль',
    'userdash.tab.consultations': 'Мои консультации',
    'userdash.tab.calendar': 'Мой календарь',
    'userdash.tab.purchases': 'Мои покупки',
    'userdash.tab.favorites': 'Избранное',
    'userdash.tab.credits': 'Баланс и оплаты',
    'userdash.tab.subscriptions': 'Подписка',
    'userdash.tab.settings': 'Настройки',
    'userdash.tab.security': 'Безопасность',
    'userdash.tab.support': 'Правила и поддержка',
    'userdash.logout': 'Выйти',
    'userdash.user': 'Пользователь',
    'userdash.sectionInDev': 'Раздел в разработке',

    // Overview Tab
    'userdash.overview.welcome': 'Добро пожаловать, {name}!',
    'userdash.overview.todayMoon':
      'Сегодня Луна в гармоничном аспекте с вашим Солнцем — отличный день для новых начинаний.',
    'userdash.overview.orderService': 'Заказать услугу',
    'userdash.overview.myReports': 'Мои отчёты',
    'userdash.overview.kpiOrders': 'Заказы',
    'userdash.overview.kpiReports': 'Отчёты',
    'userdash.overview.kpiSessions': 'Сессии',
    'userdash.overview.kpiMessages': 'Сообщения',
    'userdash.overview.nextSession': 'Ближайшая сессия',
    'userdash.overview.latestReports': 'Последние отчёты',
    'userdash.overview.messages': 'Сообщения',
    'userdash.overview.myAstroProfile': 'Мой астропрофиль',
    'userdash.overview.recentPurchases': 'Недавние покупки',
    'userdash.overview.all': 'Все',
    'userdash.overview.allProfiles': 'Все профили',
    'userdash.overview.awaiting': 'Ожидает',
    'userdash.overview.videoCall': 'Видеозвонок',
    'userdash.overview.joinCall': 'Войти в звонок',
    'userdash.overview.chatBtn': 'Чат',
    'userdash.overview.inProgress': 'В работе',
    'userdash.overview.open': 'Открыть',
    'userdash.overview.downloadPdf': 'Скачать PDF',
    'userdash.overview.downloading': 'Скачивание отчёта...',
    'userdash.overview.primary': 'Основной',
    'userdash.overview.use': 'Использовать',
    'userdash.overview.profileSelected': 'Профиль выбран для нового заказа',
    'userdash.overview.goToServices': 'Переход к выбору услуг',
    'userdash.overview.waitForLink':
      'Ожидайте ссылку на звонок за 10 минут до начала',
    'userdash.overview.guest': 'Гость',
    'userdash.overview.natalChart': 'Натальная карта',
    'userdash.overview.solar': 'Соляр 2024',
    'userdash.overview.compatibility': 'Совместимость',
    'userdash.overview.inProcessDate': 'В процессе',
    'userdash.overview.videoCourse': 'Видеокурс',
    'userdash.overview.pdfGuide': 'PDF Гайд',
    'userdash.overview.opening': 'Открытие: {title}',
    'userdash.overview.synastry': 'Синастрия (Совместимость)',
    'userdash.overview.basicsAstrology': 'Основы астрологии',
    'userdash.overview.lunarNodes': 'Лунные узлы и карма',
    'userdash.overview.myProfile': 'Мой профиль',
    'userdash.overview.tomorrow': 'Завтра в 14:00',

    // Calendar Tab
    'userdash.calendar.title': 'Мой астро-календарь',
    'userdash.calendar.today': 'Сегодня',
    'userdash.calendar.forecast': 'Прогноз дня',
    'userdash.calendar.transits': 'Транзиты',
    'userdash.calendar.advice': 'Совет дня',
    'userdash.calendar.proOnly': 'Доступно в подписке',
    'userdash.calendar.good': 'Благоприятный',
    'userdash.calendar.neutral': 'Нейтральный',
    'userdash.calendar.hard': 'Сложный',
    'userdash.calendar.month.0': 'Январь',
    'userdash.calendar.month.1': 'Февраль',
    'userdash.calendar.month.2': 'Март',
    'userdash.calendar.month.3': 'Апрель',
    'userdash.calendar.month.4': 'Май',
    'userdash.calendar.month.5': 'Июнь',
    'userdash.calendar.month.6': 'Июль',
    'userdash.calendar.month.7': 'Август',
    'userdash.calendar.month.8': 'Сентябрь',
    'userdash.calendar.month.9': 'Октябрь',
    'userdash.calendar.month.10': 'Ноябрь',
    'userdash.calendar.month.11': 'Декабрь',
    'userdash.calendar.weekday.0': 'Пн',
    'userdash.calendar.weekday.1': 'Вт',
    'userdash.calendar.weekday.2': 'Ср',
    'userdash.calendar.weekday.3': 'Чт',
    'userdash.calendar.weekday.4': 'Пт',
    'userdash.calendar.weekday.5': 'Сб',
    'userdash.calendar.weekday.6': 'Вс',
    'userdash.calendar.selectDay': 'Выберите день для просмотра прогноза',
    'userdash.calendar.subscribe': 'Оформить подписку',
    'userdash.calendar.legend': 'Легенда',

    // Support Tab
    'userdash.support.title': 'Помощь',
    'userdash.support.subtitle': 'Ответы на вопросы и связь с поддержкой',
    'userdash.support.faq': 'Частые вопросы',
    'userdash.support.contactTitle': 'Написать в поддержку',
    'userdash.support.contactPlaceholder': 'Опишите вашу проблему...',
    'userdash.support.send': 'Отправить',
    'userdash.support.sent': 'Сообщение отправлено в поддержку',
    'userdash.support.docs': 'Документы',
    'userdash.support.platformRules': 'Правила платформы',
    'userdash.support.refundPolicy': 'Политика возвратов',
    'userdash.support.privacy': 'Конфиденциальность',
    'userdash.support.faq1q': 'Как проходит консультация с астрологом?',
    'userdash.support.faq1a':
      'Консультация проходит в выбранном вами формате: видео-звонок, аудио-звонок или текстовый чат. В назначенное время вы подключаетесь по ссылке в разделе «Мои консультации».',
    'userdash.support.faq2q': 'Как вернуть деньги за заказ?',
    'userdash.support.faq2a':
      'Если астролог ещё не начал работу — отмените заказ в разделе «Мои заказы» с полным возвратом. Если работа начата — возврат рассматривается индивидуально через поддержку.',
    'userdash.support.faq3q': 'Как добавить профиль родственника?',
    'userdash.support.faq3a':
      'Перейдите в «Мой астропрофиль» → «Добавить профиль». Сохраните данные близких для быстрого оформления заказов.',
    'userdash.support.faq4q': 'Как работает баланс?',
    'userdash.support.faq4a':
      'Баланс — ваш кошелёк в рублях. Пополняйте картой, Apple Pay или Google Pay. При оплате средства списываются автоматически. Если не хватает — можно доплатить.',
    'userdash.support.chatSupport': 'Чат с поддержкой',

    // Settings Tab
    'userdash.settings.title': 'Настройки',
    'userdash.settings.subtitle': 'Управление профилем и предпочтениями',
    'userdash.settings.profile': 'Профиль',
    'userdash.settings.region': 'Регион',
    'userdash.settings.notifications': 'Уведомления',
    'userdash.settings.data': 'Данные',
    'userdash.settings.name': 'Имя',
    'userdash.settings.email': 'Email',
    'userdash.settings.phone': 'Телефон',
    'userdash.settings.save': 'Сохранить',
    'userdash.settings.saved': 'Настройки сохранены',
    'userdash.settings.changeAvatar': 'Изменить фото',
    'userdash.settings.removeAvatar': 'Удалить фото',
    'userdash.settings.timezone': 'Часовой пояс',
    'userdash.settings.language': 'Язык',
    'userdash.settings.currency': 'Валюта',
    'userdash.settings.emailNotif': 'Email-уведомления',
    'userdash.settings.pushNotif': 'Push-уведомления',
    'userdash.settings.smsNotif': 'SMS-уведомления',
    'userdash.settings.orderUpdates': 'Обновления заказов',
    'userdash.settings.promoOffers': 'Акции и предложения',
    'userdash.settings.astroEvents': 'Астрологические события',
    'userdash.settings.exportData': 'Экспорт данных',
    'userdash.settings.deleteAccount': 'Удалить аккаунт',
    'userdash.settings.deleteWarning':
      'Это действие необратимо. Все ваши данные будут удалены.',
    'userdash.settings.activityLog': 'Журнал активности',
    'userdash.settings.loginAction': 'Вход в аккаунт',
    'userdash.settings.emailChange': 'Изменение email',
    'userdash.settings.orderPayment': 'Оплата заказа',
    'userdash.settings.passwordChange': 'Смена пароля',

    // Security Tab
    'userdash.security.title': 'Безопасность',
    'userdash.security.subtitle': 'Управление паролем, 2FA и сессиями',
    'userdash.security.status': 'Статус безопасности',
    'userdash.security.password': 'Пароль',
    'userdash.security.changePassword': 'Изменить пароль',
    'userdash.security.currentPassword': 'Текущий пароль',
    'userdash.security.newPassword': 'Новый пароль',
    'userdash.security.confirmPassword': 'Подтвердите пароль',
    'userdash.security.updatePassword': 'Обновить пароль',
    'userdash.security.passwordChanged': 'Пароль успешно изменён',
    'userdash.security.twoFactor': 'Двухфакторная аутентификация',
    'userdash.security.twoFactorDesc': 'Дополнительная защита вашего аккаунта',
    'userdash.security.enable2fa': 'Включить 2FA',
    'userdash.security.disable2fa': 'Отключить 2FA',
    'userdash.security.enabled': 'Включена',
    'userdash.security.disabled': 'Отключена',
    'userdash.security.enable': 'Включить',
    'userdash.security.disable': 'Отключить',
    'userdash.security.chooseMethod': 'Выберите способ',
    'userdash.security.smsDesc': 'Код подтверждения по SMS',
    'userdash.security.backupCodes': 'Резервные коды',
    'userdash.security.hide': 'Скрыть',
    'userdash.security.show': 'Показать',
    'userdash.security.copy': 'Копировать',
    'userdash.security.download': 'Скачать',
    'userdash.security.codesCopied': 'Коды скопированы',
    'userdash.security.codesDownloaded': 'Коды скачаны',
    'userdash.security.sessions': 'Активные сессии',
    'userdash.security.activeSessions': 'Активные сессии',
    'userdash.security.currentSession': 'Текущая сессия',
    'userdash.security.endSession': 'Завершить',
    'userdash.security.notMe': 'Это не я',
    'userdash.security.endAllSessions': 'Завершить все сессии',
    'userdash.security.sessionEnded': 'Сессия завершена',
    'userdash.security.sessionEndedNotMe':
      'Сессия завершена, проверьте безопасность',
    'userdash.security.allSessionsEnded': 'Все сессии завершены',
    'userdash.security.notifications': 'Уведомления безопасности',
    'userdash.security.loginAlert': 'Вход в аккаунт',
    'userdash.security.loginAlertDesc': 'Уведомлять о новых входах',
    'userdash.security.passwordAlert': 'Смена пароля',
    'userdash.security.passwordAlertDesc': 'Уведомлять при изменении пароля',
    'userdash.security.now': 'Сейчас',
    'userdash.security.weak': 'Слабый',
    'userdash.security.medium': 'Средний',
    'userdash.security.good': 'Хороший',
    'userdash.security.strong': 'Сильный',
    'userdash.security.veryStrong': 'Очень сильный',
    'userdash.security.passwordUpdated': 'Пароль обновлён',
    'userdash.security.2faEnabled': '2FA включена',
    'userdash.security.2faEnabledVia': '2FA включена через {method}',
    'userdash.security.2faDisabled': '2FA отключена',
    'userdash.security.emailVerified': 'Email подтверждён',
    'userdash.security.phoneVerified': 'Телефон подтверждён',
    'userdash.security.enterCurrentPw': 'Введите текущий пароль',
    'userdash.security.pwRequirements': 'Пароль не соответствует требованиям',
    'userdash.security.pwMismatch': 'Пароли не совпадают',
    'userdash.security.pwUpdated': 'Пароль успешно обновлён',
    'userdash.security.minChars': 'Минимум 8 символов',
    'userdash.security.letters': 'Буквы верхнего и нижнего регистра',
    'userdash.security.digits': 'Цифры',
    'userdash.security.match': 'Пароли совпадают',

    // Chat Tab
    'userdash.chat.title': 'Сообщения',
    'userdash.chat.search': 'Поиск по имени или заказу...',
    'userdash.chat.typeMessage': 'Написать сообщение...',
    'userdash.chat.online': 'В сети',
    'userdash.chat.offline': 'Не в сети',
    'userdash.chat.noChats': 'Нет чатов',
    'userdash.chat.noMessages': 'Нет сообщений',
    'userdash.chat.noMessagesDesc': 'Начните диалог с астрологом',
    'userdash.chat.send': 'Отправить',
    'userdash.chat.astrologer': 'Астролог',
    'userdash.chat.system': 'Система',
    'userdash.chat.support': 'Поддержка',
    'userdash.chat.orderCreated': 'Заказ создан',
    'userdash.chat.paymentReceived': 'Оплата получена',
    'userdash.chat.reportReady': 'Отчёт готов',
    'userdash.chat.sessionScheduled': 'Сессия запланирована',
    'userdash.chat.selectChat': 'Выберите чат',
    'userdash.chat.orderInfo': 'Информация о заказе',
    'userdash.chat.videoCallBtn': 'Видеозвонок',
    'userdash.chat.audioCallBtn': 'Аудиозвонок',
    'userdash.chat.fileAttached': 'Файл «{name}» прикреплён',
    'userdash.chat.statusInProgress': 'В работе',
    'userdash.chat.statusCompleted': 'Завершён',
    'userdash.chat.statusWaiting': 'Ожидание',
    'userdash.chat.goToCatalog': 'Перейти в каталог астрологов',
    'userdash.chat.findAstrologer': 'Найти астролога',
    'userdash.chat.roleAstrologer': 'Астролог',
    'userdash.chat.roleSystem': 'Техподдержка',
    'userdash.chat.audioCall': 'Аудиозвонок',
    'userdash.chat.videoCall': 'Видеозвонок',
    'userdash.chat.info': 'Информация',
    'userdash.chat.waitForLink': 'Ожидайте ссылку на звонок',
    'userdash.chat.joinCall': 'Войти в звонок',
    'userdash.chat.downloading': 'Загрузка...',
    'userdash.chat.download': 'Скачать',
    'userdash.chat.goToOrder': 'Перейти к заказу {id}',
    'userdash.chat.openOrder': 'Открыть заказ',
    'userdash.chat.today': 'Сегодня',
    'userdash.chat.downloadingFile': 'Загрузка файла «{name}»...',
    'userdash.chat.emoji': 'Эмодзи',
    'userdash.chat.attachFile': 'Прикрепить файл',

    // Profile Tab
    'userdash.profile.title': 'Мой астропрофиль',
    'userdash.profile.subtitle': 'Управление астрологическими профилями',
    'userdash.profile.addProfile': 'Добавить профиль',
    'userdash.profile.addingNew': 'Создание нового профиля...',
    'userdash.profile.editProfile': 'Редактировать',
    'userdash.profile.deleteProfile': 'Удалить профиль',
    'userdash.profile.birthDate': 'Дата рождения',
    'userdash.profile.birthTime': 'Время рождения',
    'userdash.profile.birthPlace': 'Место рождения',
    'userdash.profile.name': 'Имя',
    'userdash.profile.relation': 'Кто это',
    'userdash.profile.sun': 'Солнце',
    'userdash.profile.moon': 'Луна',
    'userdash.profile.ascendant': 'Асцендент',
    'userdash.profile.element': 'Стихия',
    'userdash.profile.save': 'Сохранить',
    'userdash.profile.cancel': 'Отмена',
    'userdash.profile.saved': 'Профиль сохранён',
    'userdash.profile.deleted': 'Профиль удалён',
    'userdash.profile.primaryProfile': 'Основной профиль',
    'userdash.profile.primary': 'Основной',
    'userdash.profile.orderReport': 'Заказать отчёт',
    'userdash.profile.orderConsultation': 'Заказать консультацию',
    'userdash.profile.me': 'Я',
    'userdash.profile.statusReady': 'Готов',
    'userdash.profile.statusScheduled': 'Запланирован',
    'userdash.profile.statusInProgress': 'В работе',
    'userdash.profile.date': 'Дата',
    'userdash.profile.time': 'Время',
    'userdash.profile.place': 'Место',
    'userdash.profile.selectedForOrder': 'Профиль «{name}» выбран для заказа',
    'userdash.profile.use': 'Использовать',
    'userdash.profile.madePrimary': '«{name}» теперь основной профиль',
    'userdash.profile.makePrimary': 'Сделать основным',
    'userdash.profile.astroData': 'Астрологические данные',
    'userdash.profile.shortChar': 'Краткая характеристика',
    'userdash.profile.charText': '{sign} — {description}',
    'userdash.profile.editData': 'Редактировать данные',
    'userdash.profile.editHint': 'Измените данные рождения для пересчёта карты',
    'userdash.profile.orderServiceFor': 'Заказать услугу для {name}',
    'userdash.profile.orderingService': 'Оформление услуги для {name}...',
    'userdash.profile.history': 'История заказов',
    'userdash.profile.allOrders': 'Все заказы',
    'userdash.profile.noHistory': 'Нет истории заказов',

    // Orders Tab
    'userdash.orders.title': 'Мои заказы',
    'userdash.orders.subtitle': 'История и управление заказами',
    'userdash.orders.search': 'Поиск заказов...',
    'userdash.orders.all': 'Все',
    'userdash.orders.new': 'Новые',
    'userdash.orders.inProgress': 'В работе',
    'userdash.orders.waitingData': 'Ожидает данных',
    'userdash.orders.waitingConsultation': 'Ожидает консультации',
    'userdash.orders.completed': 'Завершённые',
    'userdash.orders.cancelled': 'Отменённые',
    'userdash.orders.dispute': 'Спор',
    'userdash.orders.refunded': 'Возврат',
    'userdash.orders.paid': 'Оплачено',
    'userdash.orders.pending': 'Ожидает оплаты',
    'userdash.orders.details': 'Подробнее',
    'userdash.orders.downloadReport': 'Скачать отчёт',
    'userdash.orders.openChat': 'Открыть чат',
    'userdash.orders.cancelOrder': 'Отменить заказ',
    'userdash.orders.orderCancelled': 'Заказ отменён',
    'userdash.orders.noOrders': 'Заказы не найдены',
    'userdash.orders.noOrdersDesc':
      'Попробуйте изменить фильтры или поисковый запрос',
    'userdash.orders.resetFilters': 'Сбросить фильтры',
    'userdash.orders.video': 'Видео',
    'userdash.orders.audio': 'Аудио',
    'userdash.orders.chatFormat': 'Чат',
    'userdash.orders.text': 'Текст',
    'userdash.orders.history': 'История заказа',
    'userdash.orders.files': 'Файлы',
    'userdash.orders.notes': 'Заметки',
    'userdash.orders.findAstrologer': 'Найти астролога',
    'userdash.orders.download': 'Скачать',
    'userdash.orders.chat': 'Чат',
    'userdash.orders.downloadStarted': 'Загрузка файла {name} началась',
    'userdash.orders.cancelRequested': 'Запрос на отмену заказа отправлен',
    'userdash.orders.disputeOpened': 'Спор по заказу открыт',
    'userdash.orders.orderNumber': 'Заказ {id}',
    'userdash.orders.placedOn': 'Оформлен: {date}',
    'userdash.orders.chatWithAstrologer': 'Написать астрологу',
    'userdash.orders.orderInfo': 'Информация о заказе',
    'userdash.orders.service': 'Услуга',
    'userdash.orders.format': 'Формат',
    'userdash.orders.price': 'Стоимость',
    'userdash.orders.deadline': 'Срок выполнения',
    'userdash.orders.executor': 'Исполнитель',
    'userdash.orders.astrologerProfile': 'Профиль астролога',
    'userdash.orders.filesAndReports': 'Файлы и отчёты',
    'userdash.orders.orderHistory': 'История заказа',
    'userdash.orders.payment': 'Оплата',
    'userdash.orders.status': 'Статус',
    'userdash.orders.paymentPaid': 'Оплачено',
    'userdash.orders.paymentPending': 'Ожидает оплаты',
    'userdash.orders.paymentRefunded': 'Возврат средств',
    'userdash.orders.total': 'Итого',
    'userdash.orders.openDispute': 'Открыть спор',

    // Reports Tab
    'userdash.reports.title': 'Мои отчёты',
    'userdash.reports.subtitle': 'Все ваши астрологические отчёты',
    'userdash.reports.search': 'Поиск отчётов...',
    'userdash.reports.natal': 'Натальная карта',
    'userdash.reports.solar': 'Соляр',
    'userdash.reports.compatibility': 'Совместимость',
    'userdash.reports.forecast': 'Прогноз',
    'userdash.reports.combo': 'Комбо',
    'userdash.reports.generating': 'Генерируется...',
    'userdash.reports.ready': 'Готов',
    'userdash.reports.error': 'Ошибка',
    'userdash.reports.download': 'Скачать',
    'userdash.reports.view': 'Просмотр',
    'userdash.reports.rate': 'Оценить',
    'userdash.reports.pages': 'страниц',
    'userdash.reports.pagesShort': 'стр.',
    'userdash.reports.noReports': 'Нет отчётов',
    'userdash.reports.orderNew': 'Заказать отчёт',
    'userdash.reports.all': 'Все',
    'userdash.reports.sort': 'Сортировка',
    'userdash.reports.byDate': 'По дате',
    'userdash.reports.byType': 'По типу',
    'userdash.reports.reviewThanks': 'Спасибо за отзыв!',
    'userdash.reports.readyCount': '{count} готовых',
    'userdash.reports.pagesCount': '{count} страниц',
    'userdash.reports.rateReport': 'Оцените отчёт',
    'userdash.reports.getDiscount': 'получите скидку 10%',
    'userdash.reports.sortNew': 'Сначала новые',
    'userdash.reports.sortOld': 'Сначала старые',
    'userdash.reports.rateReportTitle': 'Оценить отчёт',
    'userdash.reports.downloadingPdf': 'Скачивание PDF...',
    'userdash.reports.downloadPdf': 'Скачать PDF',
    'userdash.reports.open': 'Открыть',
    'userdash.reports.retryStarted': 'Повторная генерация запущена',
    'userdash.reports.retry': 'Повторить',
    'userdash.reports.whatDidYouLike': 'Что вам понравилось?',
    'userdash.reports.whatToImprove': 'Что можно улучшить?',
    'userdash.reports.send': 'Отправить',
    'userdash.reports.discountActivated': 'Скидка 10% активирована!',
    'userdash.reports.nothingFound': 'Ничего не найдено',
    'userdash.reports.tryChangingQuery': 'Попробуйте изменить запрос',
    'userdash.reports.orderFirstReport': 'Закажите свой первый отчёт',
    'userdash.reports.orderReport': 'Заказать отчёт',
    'userdash.reports.showMore': 'Показать ещё',
    'userdash.reports.rated': 'Оценено',

    // Credits Tab
    'userdash.credits.title': 'Баланс и оплаты',
    'userdash.credits.subtitle': 'Управление балансом и история транзакций',
    'userdash.credits.balance': 'Баланс',
    'userdash.credits.topUp': 'Пополнить',
    'userdash.credits.payService': 'Оплатить услугу',
    'userdash.credits.history': 'История операций',
    'userdash.credits.topupType': 'Пополнение',
    'userdash.credits.paymentType': 'Оплата',
    'userdash.credits.refundType': 'Возврат',
    'userdash.credits.success': 'Успешно',
    'userdash.credits.pendingStatus': 'В обработке',
    'userdash.credits.failed': 'Ошибка',
    'userdash.credits.refundedStatus': 'Возвращено',
    'userdash.credits.noTransactions': 'Нет транзакций',
    'userdash.credits.amount': 'Сумма',
    'userdash.credits.method': 'Способ оплаты',
    'userdash.credits.card': 'Банковская карта',
    'userdash.credits.applePay': 'Apple Pay',
    'userdash.credits.googlePay': 'Google Pay',
    'userdash.credits.balanceMethod': 'Баланс',
    'userdash.credits.topUpBalance': 'Пополнить баланс',
    'userdash.credits.enterAmount': 'Введите сумму',
    'userdash.credits.pay': 'Оплатить',
    'userdash.credits.topUpSuccess': 'Баланс пополнен',
    'userdash.credits.securePayment': 'Безопасная оплата',
    'userdash.credits.txConsultation': 'Консультация: Синастрия',
    'userdash.credits.txTopup': 'Пополнение баланса',
    'userdash.credits.txReportSolar': 'Отчёт: Соляр 2025',
    'userdash.credits.txReportNatal': 'Отчёт: Натальная карта',
    'userdash.credits.txRefund': 'Возврат за отменённый заказ',
    'userdash.credits.txConsultationBusiness': 'Консультация: Бизнес-прогноз',
    'userdash.credits.txTopupError': 'Пополнение (ошибка)',
    'userdash.credits.txCourse': 'Курс: Основы астрологии',
    'userdash.credits.methodBalance': 'Баланс',
    'userdash.credits.methodToBalance': 'На баланс',
    'userdash.credits.methodCard': 'Банковская карта',
    'userdash.credits.methodFast': 'Мгновенно',
    'userdash.credits.statusSuccess': 'Успешно',
    'userdash.credits.statusPending': 'В обработке',
    'userdash.credits.statusFailed': 'Ошибка',
    'userdash.credits.statusRefunded': 'Возвращено',
    'userdash.credits.filterAll': 'Все',
    'userdash.credits.filterTopup': 'Пополнения',
    'userdash.credits.filterPayment': 'Оплаты',
    'userdash.credits.filterRefund': 'Возвраты',
    'userdash.credits.chooseAmount': 'Выберите сумму',
    'userdash.credits.otherAmount': 'Другая сумма',
    'userdash.credits.nextChooseMethod': 'Далее: выбор способа оплаты',
    'userdash.credits.paymentMethod': 'Способ оплаты',
    'userdash.credits.next': 'Далее',
    'userdash.credits.cardDetails': 'Данные карты',
    'userdash.credits.cardNumber': 'Номер карты',
    'userdash.credits.cardExpiry': 'Срок действия',
    'userdash.credits.cardName': 'Имя на карте',
    'userdash.credits.saveCard': 'Сохранить карту для будущих оплат',
    'userdash.credits.secureData': 'Данные защищены шифрованием',
    'userdash.credits.payAmount': 'Оплатить {amount} ₽',
    'userdash.credits.smsConfirm': 'Подтверждение по SMS',
    'userdash.credits.enterSms': 'Введите код из SMS',
    'userdash.credits.sentTo': 'Отправлен на',
    'userdash.credits.resendIn': 'Повторить через {seconds} сек',
    'userdash.credits.codeResent': 'Код отправлен повторно',
    'userdash.credits.resendCode': 'Отправить код повторно',
    'userdash.credits.confirm': 'Подтвердить',
    'userdash.credits.processing': 'Обработка платежа...',
    'userdash.credits.doNotClose': 'Не закрывайте страницу',
    'userdash.credits.paymentSuccess': 'Оплата прошла успешно!',
    'userdash.credits.balanceToppedUp': 'Баланс пополнен на {amount} ₽',
    'userdash.credits.receipt': 'Чек',
    'userdash.credits.operation': 'Операция',
    'userdash.credits.topupBalance': 'Пополнение баланса',
    'userdash.credits.date': 'Дата',
    'userdash.credits.transaction': 'Транзакция',
    'userdash.credits.status': 'Статус',
    'userdash.credits.receiptSent': 'Чек отправлен на email',
    'userdash.credits.receiptToEmail': 'Отправить чек на email',
    'userdash.credits.done': 'Готово',
    'userdash.credits.sortNew': 'Сначала новые',
    'userdash.credits.sortOld': 'Сначала старые',
    'userdash.credits.dateTime': 'Дата и время',
    'userdash.credits.order': 'Заказ',
    'userdash.credits.receiptSentId': 'Чек #{id} отправлен на email',
    'userdash.credits.sendReceipt': 'Отправить чек',
    'userdash.credits.noOperations': 'Нет операций',
    'userdash.credits.noOperationsDesc': 'История операций пока пуста',
    'userdash.credits.toppedUp': 'Пополнено',
    'userdash.credits.spent': 'Потрачено',
    'userdash.credits.refunds': 'Возвраты',

    // Subscriptions Tab
    'userdash.subscriptions.title': 'Подписка',
    'userdash.subscriptions.subtitle': 'Управление вашей подпиской',
    'userdash.subscriptions.choosePlan': 'Выберите подходящий тариф',
    'userdash.subscriptions.plan': 'Тариф',
    'userdash.subscriptions.planFree': 'Бесплатный',
    'userdash.subscriptions.nextBilling': 'Следующее списание',
    'userdash.subscriptions.daysLeft': 'Осталось {count} дней',
    'userdash.subscriptions.reportsUsed': 'Использовано отчётов',
    'userdash.subscriptions.reportsRemaining': 'Осталось отчётов',
    'userdash.subscriptions.of': 'из',
    'userdash.subscriptions.benefits': 'Преимущества',
    'userdash.subscriptions.reportsPerMonth': '3 отчёта/мес',
    'userdash.subscriptions.freeReports': 'Бесплатные авто-отчёты',
    'userdash.subscriptions.consultDiscount': '−20% консультации',
    'userdash.subscriptions.discountAstrologers': 'Скидка на астрологов',
    'userdash.subscriptions.pause': 'Приостановить',
    'userdash.subscriptions.resume': 'Возобновить',
    'userdash.subscriptions.cancel': 'Отменить подписку',
    'userdash.subscriptions.cancelWarning':
      'Вы потеряете все преимущества подписки',
    'userdash.subscriptions.confirmCancel': 'Подтвердить отмену',
    'userdash.subscriptions.paused': 'Приостановлена',
    'userdash.subscriptions.active': 'Активна',
    'userdash.subscriptions.resumed': 'Подписка возобновлена',
    'userdash.subscriptions.cancelled': 'Подписка отменена',
    'userdash.subscriptions.noSubscription': 'У вас нет активной подписки',
    'userdash.subscriptions.subscribe': 'Оформить подписку',
    'userdash.subscriptions.savings': 'Экономия',
    'userdash.subscriptions.perMonth': '/мес',
    'userdash.subscriptions.month': 'мес',
    'userdash.subscriptions.popular': 'Популярный',
    'userdash.subscriptions.currentPlan': 'Текущий тариф',
    'userdash.subscriptions.planActivated': 'Тариф «{plan}» активирован!',
    'userdash.subscriptions.socialProofBold': '2 400+ пользователей',
    'userdash.subscriptions.socialProofText': 'уже оформили подписку',
    'userdash.subscriptions.benefitReports': 'Бесплатные отчёты',
    'userdash.subscriptions.benefitReportsDesc': 'До 10 авто-отчётов в месяц',
    'userdash.subscriptions.benefitDiscount': 'Скидки на услуги',
    'userdash.subscriptions.benefitDiscountDesc':
      'До 30% на консультации и отчёты',
    'userdash.subscriptions.benefitArticles': 'Эксклюзивные статьи',
    'userdash.subscriptions.benefitArticlesDesc': 'Доступ к PRO-контенту',
    'userdash.subscriptions.benefitCalendar': 'PRO-календарь',
    'userdash.subscriptions.benefitCalendarDesc':
      'Персональные транзиты и прогнозы',
    'userdash.subscriptions.featureBasicCalendar': 'Базовый календарь',
    'userdash.subscriptions.featureGeneralForecasts': 'Общие прогнозы',
    'userdash.subscriptions.feature1Profile': '1 астропрофиль',
    'userdash.subscriptions.feature3Reports': '3 отчёта/мес',
    'userdash.subscriptions.feature20Discount': '−20% на консультации',
    'userdash.subscriptions.featureProCalendar': 'PRO-календарь',
    'userdash.subscriptions.featureProArticles': 'PRO-статьи',
    'userdash.subscriptions.feature5Profiles': '5 астропрофилей',
    'userdash.subscriptions.feature10Reports': '10 отчётов/мес',
    'userdash.subscriptions.feature30Discount': '−30% на консультации',
    'userdash.subscriptions.featurePrioritySupport': 'Приоритетная поддержка',
    'userdash.subscriptions.featureAllPro': 'Все PRO-функции',
    'userdash.subscriptions.featureUnlimitedProfiles': 'Безлимит профилей',
    'userdash.subscriptions.mySubscription': 'Моя подписка',
    'userdash.subscriptions.manageStats': 'Управление и статистика',
    'userdash.subscriptions.monthlySubscription': 'Ежемесячная подписка',
    'userdash.subscriptions.timeLeft': 'Осталось',
    'userdash.subscriptions.days': 'дней',
    'userdash.subscriptions.youAlreadyGot': 'Вы уже получили',
    'userdash.subscriptions.savedOnReports': 'Сэкономлено на отчётах',
    'userdash.subscriptions.savedOnDiscounts': 'Сэкономлено на скидках',
    'userdash.subscriptions.freeReportsThisMonth':
      'Бесплатные отчёты в этом месяце',
    'userdash.subscriptions.usedOfTotal': '{used} из {total} использовано',
    'userdash.subscriptions.left': 'осталось',
    'userdash.subscriptions.freeReportsUpdate': 'Обновление {date}',
    'userdash.subscriptions.management': 'Управление подпиской',
    'userdash.subscriptions.comingSoon': 'Скоро',
    'userdash.subscriptions.managementDesc':
      'Изменение тарифа, приостановка и отмена',
    'userdash.subscriptions.changePlan': 'Сменить тариф',
    'userdash.subscriptions.renewEarly': 'Продлить досрочно',
    'userdash.subscriptions.cancelSubscription': 'Отменить подписку',
    'userdash.subscriptions.areYouSure': 'Вы уверены?',
    'userdash.subscriptions.unusedReports': 'Неиспользованные отчёты сгорят',
    'userdash.subscriptions.discountLost': 'Скидки на услуги будут потеряны',
    'userdash.subscriptions.proFeaturesLost': 'PRO-функции станут недоступны',
    'userdash.subscriptions.youSavedTotal': 'Вы сэкономили всего',
    'userdash.subscriptions.keepSubscription': 'Оставить подписку',
    'userdash.subscriptions.cancelledNotice':
      'Подписка будет отменена в конце периода',
    'userdash.subscriptions.cancelAnyway': 'Всё равно отменить',
    'userdash.subscriptions.totalSavedHighlight': 'Вы сэкономили',
    'userdash.subscriptions.averageSavings': 'Средняя экономия в месяц',

    // Favorites Tab
    'userdash.favorites.title': 'Избранное',
    'userdash.favorites.subtitle': 'Сохранённые астрологи, услуги и товары',
    'userdash.favorites.astrologers': 'Астрологи',
    'userdash.favorites.services': 'Услуги',
    'userdash.favorites.products': 'Товары',
    'userdash.favorites.courses': 'Курсы',
    'userdash.favorites.search': 'Поиск в избранном...',
    'userdash.favorites.sort': 'Сортировка',
    'userdash.favorites.recent': 'Недавние',
    'userdash.favorites.byRating': 'По рейтингу',
    'userdash.favorites.byPrice': 'По цене',
    'userdash.favorites.remove': 'Убрать из избранного',
    'userdash.favorites.removed': 'Удалено из избранного',
    'userdash.favorites.empty': 'Пока пусто',
    'userdash.favorites.emptyDesc':
      'Добавляйте астрологов и услуги в избранное',
    'userdash.favorites.book': 'Записаться',
    'userdash.favorites.order': 'Заказать',
    'userdash.favorites.onlineNow': 'Онлайн',
    'userdash.favorites.sortRecent': 'По дате добавления',
    'userdash.favorites.sortRating': 'По рейтингу',
    'userdash.favorites.sortPrice': 'По цене',
    'userdash.favorites.removedFromFavorites': 'Удалено из избранного',
    'userdash.favorites.today': 'Сегодня',
    'userdash.favorites.yesterday': 'Вчера',
    'userdash.favorites.daysAgo': '{count} дн. назад',
    'userdash.favorites.weeksAgo': '{count} нед. назад',
    'userdash.favorites.saveToReturn': 'Сохраняйте, чтобы вернуться позже',
    'userdash.favorites.savedItems': '{count} сохранённых',
    'userdash.favorites.online': 'В сети',
    'userdash.favorites.noAstrologers': 'Нет сохранённых астрологов',
    'userdash.favorites.noServices': 'Нет сохранённых услуг',
    'userdash.favorites.noProducts': 'Нет сохранённых товаров',
    'userdash.favorites.noCourses': 'Нет сохранённых курсов',
    'userdash.favorites.findAstrologer': 'Найти астролога',
    'userdash.favorites.toCatalog': 'Перейти в каталог',
    'userdash.favorites.inactive': 'Не в сети',
    'userdash.favorites.reviews': 'отзывов',
    'userdash.favorites.chatOpened': 'Чат открыт',
    'userdash.favorites.write': 'Написать',
    'userdash.favorites.profile': 'Профиль',
    'userdash.favorites.video': 'Видео',
    'userdash.favorites.text': 'Текст',
    'userdash.favorites.details': 'Подробнее',
    'userdash.favorites.type': 'Тип',
    'userdash.favorites.buy': 'Купить',
    'userdash.favorites.lessons': 'уроков',
    'userdash.favorites.showMore': 'Показать ещё',
    'userdash.favorites.left': 'ещё',
    'userdash.favorites.showing': 'Показано',
    'userdash.favorites.of': 'из',

    // Purchases Tab
    'userdash.purchases.title': 'Мои покупки',
    'userdash.purchases.subtitle': 'Курсы, гайды и другие материалы',
    'userdash.purchases.search': 'Поиск покупок...',
    'userdash.purchases.sort': 'Сортировка',
    'userdash.purchases.course': 'Курс',
    'userdash.purchases.pdf': 'PDF',
    'userdash.purchases.marathon': 'Марафон',
    'userdash.purchases.recording': 'Запись',
    'userdash.purchases.checklist': 'Чек-лист',
    'userdash.purchases.accessGranted': 'Доступ открыт',
    'userdash.purchases.accessPending': 'Ожидает доступа',
    'userdash.purchases.progress': 'Прогресс',
    'userdash.purchases.open': 'Открыть',
    'userdash.purchases.download': 'Скачать',
    'userdash.purchases.review': 'Оставить отзыв',
    'userdash.purchases.reviewed': 'Отзыв оставлен',
    'userdash.purchases.noPurchases': 'Нет покупок',
    'userdash.purchases.noPurchasesDesc': 'У вас пока нет купленных материалов',
    'userdash.purchases.goToMarketplace': 'Перейти в маркетплейс',
    'userdash.purchases.lastOpened': 'Последнее открытие',
    'userdash.purchases.opening': 'Открытие: {title}',
    'userdash.purchases.continue': 'Продолжить',
    'userdash.purchases.start': 'Начать',
    'userdash.purchases.thanksForReview': 'Спасибо за отзыв!',
    'userdash.purchases.filterAll': 'Все',
    'userdash.purchases.filterCourses': 'Курсы',
    'userdash.purchases.filterPdf': 'PDF-гайды',
    'userdash.purchases.filterRecords': 'Записи',
    'userdash.purchases.filterMarathons': 'Марафоны',
    'userdash.purchases.statusAll': 'Все статусы',
    'userdash.purchases.statusInProgress': 'В процессе',
    'userdash.purchases.statusCompleted': 'Завершённые',
    'userdash.purchases.statusNew': 'Новые',
    'userdash.purchases.toCatalog': 'Перейти в каталог',
    'userdash.purchases.materialsCount': 'материалов',
    'userdash.purchases.continueLearning': 'Продолжить обучение',
    'userdash.purchases.sortNew': 'Сначала новые',
    'userdash.purchases.sortOld': 'Сначала старые',
    'userdash.purchases.pending': 'Ожидает доступа',
    'userdash.purchases.author': 'Автор',
    'userdash.purchases.bought': 'Куплено',
    'userdash.purchases.price': 'Стоимость',
    'userdash.purchases.opened': 'Открыто',
    'userdash.purchases.notOpenedYet': 'Ещё не открыто',
    'userdash.purchases.supportRequestSent': 'Запрос в поддержку отправлен',
    'userdash.purchases.checkAccessStatus': 'Проверить статус доступа',
    'userdash.purchases.downloading': 'Загрузка...',
    'userdash.purchases.leaveReview': 'Оставить отзыв',
    'userdash.purchases.rateMaterial': 'Оцените материал',
    'userdash.purchases.send': 'Отправить',
    'userdash.purchases.nothingFound': 'Ничего не найдено',
    'userdash.purchases.noMaterials': 'Нет материалов',
    'userdash.purchases.tryChangingQuery': 'Попробуйте изменить запрос',
    'userdash.purchases.tryAnotherFilter': 'Попробуйте другой фильтр',
    'userdash.purchases.showMore': 'Показать ещё',
    'userdash.purchases.left': 'ещё',
    'userdash.purchases.showing': 'Показано',
    'userdash.purchases.of': 'из',
    'userdash.purchases.inProgressCount': 'В процессе',
    'userdash.purchases.completedCount': 'Завершено',

    // Consultations Tab
    'userdash.consultations.title': 'Мои консультации',
    'userdash.consultations.subtitle': 'Предстоящие и прошедшие сессии',
    'userdash.consultations.upcoming': 'Предстоящие',
    'userdash.consultations.completed': 'Завершённые',
    'userdash.consultations.cancelled': 'Отменённые',
    'userdash.consultations.rescheduled': 'Перенесённые',
    'userdash.consultations.all': 'Все',
    'userdash.consultations.video': 'Видео',
    'userdash.consultations.audio': 'Аудио',
    'userdash.consultations.chatFormat': 'Чат',
    'userdash.consultations.joinCall': 'Войти в звонок',
    'userdash.consultations.reschedule': 'Перенести',
    'userdash.consultations.cancelSession': 'Отменить',
    'userdash.consultations.sessionCancelled': 'Консультация отменена',
    'userdash.consultations.noConsultations': 'Нет консультаций',
    'userdash.consultations.bookNew': 'Записаться на консультацию',
    'userdash.consultations.paid': 'Оплачено',
    'userdash.consultations.pendingPayment': 'Ожидает оплаты',
    'userdash.consultations.minutesLeft': 'Через {count} мин',
    'userdash.consultations.duration': 'Длительность',

    // Common user dashboard
    'userdash.common.save': 'Сохранить',
    'userdash.common.cancel': 'Отмена',
    'userdash.common.delete': 'Удалить',
    'userdash.common.edit': 'Редактировать',
    'userdash.common.close': 'Закрыть',
    'userdash.common.back': 'Назад',
    'userdash.common.search': 'Поиск',
    'userdash.common.filter': 'Фильтр',
    'userdash.common.sort': 'Сортировка',
    'userdash.common.noData': 'Нет данных',
    'userdash.common.loading': 'Загрузка...',
  },

  // STUBS — copy Russian for now, translate later
  en: {
    'nav.services': 'Services',
    'nav.astrologers': 'Astrologers',
    'nav.knowledge': 'Knowledge',
    'nav.pricing': 'Pricing',
    'nav.marketplace': 'Marketplace',
    'nav.freeChart': 'Free Chart',
    'nav.freeChartFull': 'Free Birth Chart',
    'nav.login': 'Log in',
    'nav.cart': 'Cart',
    'services.natal': 'Birth Chart',
    'services.natal.desc': 'Deep personality analysis',
    'services.solar': 'Solar Return',
    'services.solar.desc': 'Your year ahead',
    'services.compatibility': 'Compatibility',
    'services.compatibility.desc': 'Synastry for couples',
    'services.combo': 'Combo: Birth + Solar',
    'services.combo.desc': 'Full analysis + year forecast',
    'services.forecast': 'Forecast (Transits)',
    'services.forecast.desc': 'Key periods & dates',
    'services.consultation': 'Consultation',
    'services.consultation.desc': 'Live talk with astrologer',
    'services.marketplace': 'Marketplace',
    'services.marketplace.desc': 'All services & consultations',
    'knowledge.interpretations': 'Interpretations',
    'knowledge.interpretations.desc': 'Astrology knowledge base',
    'knowledge.calendar': 'Astro Calendar',
    'knowledge.calendar.desc': 'Key celestial events',
    'knowledge.blog': 'Blog',
    'knowledge.blog.desc': 'Articles & forecasts',
    'profile.dashboard': 'My Dashboard',
    'profile.astrologer': 'Astrologer Panel',
    'profile.logout': 'Log out',
    'profile.admin': 'Admin Panel',
    'mobile.services': 'Services',
    'mobile.services.desc': 'Reports, forecasts, consultations',
    'mobile.knowledge.desc': 'Calendars, interpretations, blog',
    'mobile.platform': 'Platform',
    'mobile.account': 'Account',
    'mobile.language': 'Language',
    'mobile.client': 'Client',
    'mobile.astrologer': 'Astrologer',
    'footer.services': 'Services',
    'footer.platform': 'Platform',
    'footer.partners': 'Partners',
    'footer.natal': 'Birth Chart',
    'footer.solar': 'Solar Return',
    'footer.compatibility': 'Compatibility',
    'footer.pricingPackages': 'Pricing & Packages',
    'footer.astrologersCatalog': 'Astrologer Catalog',
    'footer.interpretations': 'Interpretations',
    'footer.calendar': 'Astro Calendar',
    'footer.blog': 'Blog',
    'footer.forAstrologers': 'For Astrologers',
    'footer.advertising': 'Advertise',
    'footer.astrologerDashboard': 'Astrologer Panel',
    'footer.combo': 'Combo Analysis',
    'footer.forecast': 'Transit Forecast',
    'footer.consultation': 'Consultation',
    'footer.marketplace': 'Marketplace',
    'footer.free': 'Free Tools',
    'footer.astroCalendar': 'Astro Calendar',
    'footer.freeChart': 'Free Chart',
    'footer.natalDemo': 'Birth Chart Online',
    'footer.reportPreview': 'Report Samples',
    'footer.premiumReport': 'Premium Report',
    'footer.knowledge': 'Knowledge',
    'footer.faq': 'FAQ',
    'footer.company': 'Company',
    'footer.about': 'About Us',
    'footer.contact': 'Contact',
    'footer.newsletter.title': 'Subscribe to Astro Digest',
    'footer.newsletter.desc':
      'Weekly forecasts, useful articles and exclusive discounts.',
    'footer.newsletter.placeholder': 'Your email',
    'footer.newsletter.button': 'Subscribe',
    'footer.newsletter.success': 'Subscribed! Expect our emails.',
    'footer.securePayment': 'Secure Payment',
    'footer.copyright': '© {year} Astriqo. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.offer': 'Public Offer',
    'footer.description':
      'A platform for deep self-discovery through professional astrology. Auto-reports and consultations with verified experts.',
    'knowledge.faq': 'FAQ',
    'knowledge.faq.desc': 'Platform help',
    'ui.learnMore': 'Learn more',
    'ui.select': 'Select',
    'ui.buy': 'Buy',
    'ui.profile': 'Profile',
    'ui.order': 'Order',
    'ui.search': 'Search',
    'ui.filter': 'Filters',
    'ui.reset': 'Reset',
    'ui.back': 'Back',
    'ui.next': 'Next',
    'ui.close': 'Close',
    'ui.loading': 'Loading...',

    // Legal Pages
    'legal.lastUpdated': 'Last updated: May 15, 2026',
    'legal.backToHome': 'Home',
    'legal.contactUs': 'Have questions? Contact us',

    // Privacy Policy
    'legal.privacy.title': 'Privacy Policy',
    'legal.privacy.s1.title': '1. General Provisions',
    'legal.privacy.s1.p1':
      'This Privacy Policy (hereinafter referred to as the "Policy") determines the procedure for processing and protecting the personal data of users of the Astriqo service (hereinafter referred to as the "Service"). We respect the privacy of your data and make every effort to protect it.',
    'legal.privacy.s2.title': '2. What data we collect',
    'legal.privacy.s2.p1':
      'To provide astrological services, we collect the following data:',
    'legal.privacy.s2.l1':
      'Registration data: name, email address, phone number.',
    'legal.privacy.s2.l2':
      'Astrological data: date, exact time, and place of birth (necessary for calculating the natal chart).',
    'legal.privacy.s2.l3':
      'Technical data: IP address, browser type, cookie data.',
    'legal.privacy.s3.title': '3. Use of data',
    'legal.privacy.s3.p1':
      'We use your data exclusively for the following purposes:',
    'legal.privacy.s3.l1': 'Generation of personal astrological reports.',
    'legal.privacy.s3.l2':
      'Organization of consultations with platform astrologers.',
    'legal.privacy.s3.l3': 'Improving the quality of the Service.',
    'legal.privacy.s3.l4':
      'Sending important notifications (e.g., about report readiness).',
    'legal.privacy.s4.title': '4. Data transfer to third parties',
    'legal.privacy.s4.p1':
      'We do not sell or transfer your personal data to third parties for marketing purposes. Your astrological data (date, time, place of birth) may be transferred to your chosen astrologer solely for the purpose of conducting a paid consultation.',
    'legal.privacy.s5.title': '5. Data security',
    'legal.privacy.s5.p1':
      'We use modern encryption and data protection methods to prevent unauthorized access. All payments are processed through secure gateways of certified payment systems.',
    'legal.privacy.s6.title': '6. Your rights',
    'legal.privacy.s6.p1': 'You have the right at any time to:',
    'legal.privacy.s6.l1':
      'Request a copy of all your data stored in the Service.',
    'legal.privacy.s6.l2':
      'Edit or delete your data through your Personal Account settings.',
    'legal.privacy.s6.l3':
      'Withdraw consent to the processing of personal data.',
    'legal.privacy.s7.title': '7. Contacts',
    'legal.privacy.s7.p1':
      'If you have any questions about this Privacy Policy, please contact us at: privacy@astriqo.com.',

    // Terms of Service
    'legal.terms.title': 'Terms of Service',
    'legal.terms.s1.title': '1. Subject of the agreement',
    'legal.terms.s1.p1':
      'This User Agreement (hereinafter referred to as the "Agreement") regulates the relationship between the Astriqo service (hereinafter referred to as the "Service") and the Internet user (hereinafter referred to as the "User") arising from the use of the Service.',
    'legal.terms.s2.title': '2. Nature of services provided',
    'legal.terms.s2.p1':
      'The Service provides informational and entertainment services in the field of astrology. All generated reports, forecasts, and consultations are advisory in nature. The Service is not responsible for decisions made by the User based on the information received.',
    'legal.terms.s2.p2':
      'Important: Astrological consultations and reports do not replace professional medical, psychological, legal, or financial assistance.',
    'legal.terms.s3.title': '3. Registration and account',
    'legal.terms.s3.p1':
      'To access the full functionality of the Service, the User must complete the registration procedure. The User undertakes to provide reliable information about themselves and is responsible for the safety of their login data (login and password).',
    'legal.terms.s4.title': '4. Payment and refunds',
    'legal.terms.s4.p1':
      'Payment for services (Premium reports, consultations) is made on a 100% prepayment basis. Since automatic reports are digital content, access to which is provided instantly, refunds for them are not provided by default (in accordance with consumer protection legislation).',
    'legal.terms.s4.p2':
      'Refunds for a consultation with an astrologer are possible upon cancellation no later than 24 hours before the scheduled time.',
    'legal.terms.s5.title': '5. Rules of conduct on the platform',
    'legal.terms.s5.p1': 'The User is prohibited from:',
    'legal.terms.s5.l1':
      'Using obscene language and insults when communicating with astrologers or support.',
    'legal.terms.s5.l2':
      'Copying, distributing, or reselling Service materials (reports, articles) without written permission.',
    'legal.terms.s5.l3':
      'Using automated scripts (bots) to collect information from the Service.',
    'legal.terms.s6.title': '6. Limitation of liability',
    'legal.terms.s6.p1':
      'The Service is provided "as is". We do not guarantee that the Service will meet your requirements, will be provided continuously, quickly, reliably, and without errors.',
    'legal.terms.s7.title': '7. Changes to terms',
    'legal.terms.s7.p1':
      'The Service Administration reserves the right to make changes to this Agreement at any time. Continued use of the Service after changes are made means the User agrees to the new version of the Agreement.',

    // Public Offer
    'legal.offer.title': 'Public Offer',
    'legal.offer.s1.title': '1. General Provisions',
    'legal.offer.s1.p1':
      'This document is a public offer of the Astriqo service (hereinafter referred to as the "Contractor") and contains all the essential conditions for the provision of information and consulting services.',
    'legal.offer.s2.title': '2. Subject of the offer',
    'legal.offer.s2.p1':
      'The Contractor undertakes to provide the Customer with services for providing access to astrological reports and consultations on the Astriqo platform, and the Customer undertakes to pay for these services.',
    'legal.offer.s3.title': '3. Procedure for providing services',
    'legal.offer.s3.p1':
      "Services are provided electronically through the Customer's personal account on the site. Access to digital goods (reports) is provided automatically after payment.",
    'legal.offer.s4.title': '4. Cost and payment procedure',
    'legal.offer.s4.p1':
      'The cost of services is indicated on the site. Payment is made by bank transfer through integrated payment systems.',
    'legal.offer.s5.title': '5. Rights and obligations of the parties',
    'legal.offer.s5.p1':
      'The Contractor undertakes to provide services efficiently and on time. The Customer undertakes to provide reliable data for calculations (date, time, place of birth).',
    'legal.offer.s6.title': '6. Liability of the parties',
    'legal.offer.s6.p1':
      'The parties are liable for failure to fulfill obligations in accordance with current legislation.',
    'legal.offer.s7.title': '7. Force majeure',
    'legal.offer.s7.p1':
      'The parties are released from liability for partial or complete failure to fulfill obligations under this Agreement if this failure was the result of force majeure circumstances.',
    'legal.offer.s8.title': '8. Dispute resolution',
    'legal.offer.s8.p1':
      'All disputes and disagreements are resolved through negotiations. If an agreement is not reached, the dispute is submitted to the court.',

    // Encyclopedia page
    'enc.badge': 'Knowledge Base',
    'enc.title': 'Astrology Encyclopedia',
    'enc.subtitle':
      'Everything about astrology: from basics to advanced techniques. Educational articles, natal chart interpretations and our services — {count} articles.',
    'enc.searchPlaceholder': 'E.g.: Venus in Taurus, Ascendant in Leo...',
    'enc.popular': 'Popular:',
    'enc.results': 'Results ({count})',
    'enc.resetFilters': 'Reset filters',
    'enc.notFound': 'Nothing found',
    'enc.notFoundHint':
      'Try changing your query or selecting a different letter.',
    'enc.showCategories': 'Show all categories',
    'enc.readMore': 'Read more',
    'enc.learnMore': 'Learn more',
    'enc.articles': 'articles',
    'enc.backToList': 'Back to list',
    'enc.alphabetTitle': 'Alphabetical Index',
    'enc.keyFacts': 'Key Facts',
    'enc.commonMyths': 'Common Myths',
    'enc.astrologerTip': "Astrologer's Tip",
    'enc.strengths': 'Strengths',
    'enc.challenges': 'Challenges',
    'enc.relatedArticles': 'Related Articles',
    'enc.defaultTip':
      'The energy of this placement requires a conscious approach. Lean into your strengths and watch for negative patterns. Growth begins with self-awareness.',
    'enc.ctaEncTitle': 'Apply this knowledge in practice',
    'enc.ctaIntTitle': 'See how this works in your chart',
    'enc.ctaEncDesc':
      'Astrology is best learned through your own example. Order a personal report and discover everything about yourself.',
    'enc.ctaIntDesc':
      'One placement is just part of the picture. Order a full report to see how planets interact in your unique chart.',
    'enc.defaultCta': 'Calculate your birth chart',
    'enc.basicsTitle': 'Astrology Basics',
    'enc.basicsCard': 'Basic Concepts',
    'enc.basicsDesc':
      'What is astrology, birth chart, zodiac signs, planets and houses. Perfect for beginners.',
    'enc.advancedCard': 'Advanced Topics',
    'enc.advancedDesc':
      'Transits, solar return, synastry, lunar nodes and retrograde planets. For those who want to know more.',
    'enc.interpretationsTitle': 'Interpretation Reference',
    'enc.planetsSignsCard': 'Planets in Signs',
    'enc.planetsSignsDesc':
      'How the energies of 10 planets (Sun to Pluto) manifest in 12 zodiac signs. The building blocks of personality.',
    'enc.ascendantCard': 'Ascendant in Signs',
    'enc.ascendantDesc':
      'Your social mask, first impression and filter of perception. How others see you.',
    'enc.housesCard': 'Planets in Houses',
    'enc.housesDesc':
      'In which areas of life planets realize their potential. Sun, Moon, Mercury, Venus and Mars in 12 houses.',
    'enc.aspectsCard': 'Aspects',
    'enc.aspectsDesc':
      'Planetary interactions: conjunctions, trines, squares, oppositions and sextiles.',
    'enc.servicesTitle': 'Our Services',
    'enc.popularSearch1': 'What is a birth chart',
    'enc.popularSearch2': 'Sun in Leo',
    'enc.popularSearch3': 'Aspects in astrology',

    // Free Chart Page
    'chart.title': 'Astrological Chart Calculator',
    'chart.subtitle':
      'Choose a service, enter your birth data and get an accurate calculation of planet positions or a detailed PDF report.',
    'chart.breadcrumb': 'Chart Calculator',
    'chart.rating': '4.9 rating',
    'chart.calculations': '50,000+ calculations',
    'chart.freeBeta': 'Free Beta calculation',
    'chart.step.service': 'Service',
    'chart.step.package': 'Package',
    'chart.step.data': 'Data',
    'chart.step.result': 'Result',
    'chart.step1.title': 'Choose a service',
    'chart.step1.subtitle': 'What calculation would you like to perform?',
    'chart.popular': 'Popular',
    'chart.new': 'New',
    'chart.fromPrice': 'from 2,990 ₽',
    'chart.freePremium': 'Free + Premium',
    'chart.step2.titleConsultation': 'Choose a format',
    'chart.step2.titleReport': 'Choose a plan',
    'chart.step2.forService': 'For service «{name}»',
    'chart.tier.free.title': 'Beta (basic)',
    'chart.tier.free.badge': 'Free',
    'chart.tier.free.desc':
      'Brief calculation of key indicators without text interpretation',
    'chart.tier.pro.title': 'Premium (one-time)',
    'chart.tier.pro.badge': 'Recommended',
    'chart.tier.pro.desc':
      'Full PDF report of 20-40 pages with detailed analysis and recommendations',
    'chart.tier.package.title': 'Premium (bundle)',
    'chart.tier.package.badge': 'Best value',
    'chart.tier.package.desc':
      'Discounted report bundle. Can be used for any platform services',
    'chart.tier.package.fromPrice': 'from 1,590 ₽/ea',
    'chart.bundle.hit': 'Hit',
    'chart.bundle.reports': '{count} reports',
    'chart.consult.short.title': 'Short consultation',
    'chart.consult.short.desc': '30 min • 1 specific question',
    'chart.consult.short.badge': 'Express',
    'chart.consult.standard.title': 'Standard consultation',
    'chart.consult.standard.desc': '60 min • Full chart reading',
    'chart.consult.standard.badge': 'Recommended',
    'chart.consult.extended.title': 'Extended consultation',
    'chart.consult.extended.desc':
      '90 min • Recording + detailed recommendations',
    'chart.consult.extended.badge': 'Maximum',
    'chart.step3.title': 'Enter your data',
    'chart.step3.subtitle': 'To calculate service «{name}»',
    'chart.field.name': 'Name (optional)',
    'chart.field.namePlaceholder': 'E.g., Anna',
    'chart.field.date': 'Date of birth *',
    'chart.field.time': 'Time of birth *',
    'chart.field.timeHint': 'If unknown, enter 12:00',
    'chart.field.city': 'City of birth *',
    'chart.field.cityPlaceholder': 'E.g., Moscow',
    'chart.field.person1': 'First partner',
    'chart.field.person2': 'Second partner',
    'chart.field.forecastYear': 'Forecast year *',
    'chart.field.forecastPeriod': 'Forecast period *',
    'chart.period.3months': '3 months',
    'chart.period.6months': '6 months',
    'chart.period.1year': '1 year',
    'chart.btn.calculate': 'Calculate',
    'chart.btn.book': 'Book now',
    'chart.btn.back': 'Back',
    'chart.btn.next': 'Next',
    'chart.calculating.title': 'Analyzing data...',
    'chart.calculating.subtitle': 'Calculating precise planet positions',
    'chart.result.fullReport': 'Full report',
    'chart.result.basicReport': 'Basic report',
    'chart.result.newCalc': 'New calculation',
    'chart.result.planets': 'Planet positions',
    'chart.result.planet': 'Planet',
    'chart.result.sign': 'Sign',
    'chart.result.degree': 'Degree',
    'chart.result.briefInterpretation': 'Brief interpretation',
    'chart.result.wantFull': 'Want a full analysis?',
    'chart.result.fullDesc':
      'Get a detailed PDF report of 30-40 pages with in-depth interpretation of all indicators, karmic tasks and personal recommendations.',
    'chart.result.getPremium': 'Get Premium report',
    'chart.result.questions': 'Have questions?',
    'chart.result.consultDesc': 'Book a live consultation with an astrologer',
    'chart.result.chooseAstrologer': 'Choose astrologer',
    'chart.trust.accurate.title': 'Accurate calculations',
    'chart.trust.accurate.desc':
      'We use professional Swiss Ephemeris algorithms for maximum accuracy.',
    'chart.trust.privacy.title': 'Privacy',
    'chart.trust.privacy.desc':
      'Your birth data is protected and used only for chart calculation.',
    'chart.trust.instant.title': 'Instant result',
    'chart.trust.instant.desc':
      'Get the basic calculation and planet positions in seconds.',
    'chart.planet.sun': 'Sun',
    'chart.planet.moon': 'Moon',
    'chart.planet.mercury': 'Mercury',
    'chart.planet.venus': 'Venus',
    'chart.planet.mars': 'Mars',
    'chart.planet.jupiter': 'Jupiter',
    'chart.planet.saturn': 'Saturn',
    'chart.sign.aries': 'Aries',
    'chart.sign.taurus': 'Taurus',
    'chart.sign.gemini': 'Gemini',
    'chart.sign.pisces': 'Pisces',
    'chart.sign.sagittarius': 'Sagittarius',
    'chart.sign.capricorn': 'Capricorn',

    // Checkout page
    'checkout.title': 'Checkout',
    'checkout.breadcrumb.home': 'Home',
    'checkout.breadcrumb.marketplace': 'Marketplace',
    'checkout.breadcrumb.title': 'Checkout',
    'checkout.step.product': 'Product',
    'checkout.step.checkout': 'Checkout',
    'checkout.step.payment': 'Payment',
    'checkout.addons.title': 'Additional Services',
    'checkout.addons.subtitle': 'Enhance your order with extra options',
    'checkout.addons.reports': 'Reports',
    'checkout.addons.consultations': 'Consultations',
    'checkout.addons.packages': 'Packages',
    'checkout.addons.natal': 'Birth Chart Premium',
    'checkout.addons.compatibility': 'Compatibility Premium',
    'checkout.addons.solar': 'Solar Return Premium',
    'checkout.addons.forecast': 'Forecast (Transits) Premium',
    'checkout.addons.combo': 'Combo: Birth + Solar',
    'checkout.addons.consult30': 'Consultation 30 min',
    'checkout.addons.consult60': 'Consultation 60 min',
    'checkout.addons.consult90': 'Consultation 90 min',
    'checkout.addons.pkg20': 'Bundle of 20 reports',
    'checkout.addons.pkg50': 'Bundle of 50 reports',
    'checkout.addons.pkg100': 'Bundle of 100 reports',
    'checkout.auth.title': 'Sign in to place your order',
    'checkout.auth.desc':
      'Registration is required to receive reports and save your data. Your birth data and reports will be available in your dashboard.',
    'checkout.auth.login': 'Sign in',
    'checkout.auth.register': 'Register',
    'checkout.auth.hint': 'Quick sign-up via email or social networks',
    'checkout.payment.title': 'Payment method',
    'checkout.payment.card': 'Bank card',
    'checkout.payment.sbp': 'SBP',
    'checkout.card.number': 'Card number',
    'checkout.card.expiry': 'Expiry date',
    'checkout.card.cvc': 'CVC',
    'checkout.card.name': 'Name on card',
    'checkout.sbp.hint': 'Scan the QR code in your bank app',
    'checkout.sbp.qr': 'QR code for payment',
    'checkout.contact.title': 'Contact details',
    'checkout.contact.name': 'Name',
    'checkout.contact.namePlaceholder': 'Your name',
    'checkout.contact.email': 'Email',
    'checkout.contact.phone': 'Phone (optional)',
    'checkout.contact.phonePlaceholder': '+1 (999) 123-4567',
    'checkout.contact.privacy':
      'Your data is securely protected and not shared with third parties',
    'checkout.order.title': 'Your order',
    'checkout.order.addMore': 'Add another service',
    'checkout.order.addonsLabel': 'Add-ons',
    'checkout.order.empty': 'Cart is empty',
    'checkout.order.autoGeneration': 'Automatic generation',
    'checkout.promo.placeholder': 'Promo code (e.g. ASTRO10)',
    'checkout.promo.apply': 'Apply',
    'checkout.promo.applied': '✓',
    'checkout.promo.success': 'Promo code ASTRO10 applied! 10% discount',
    'checkout.total.service': 'Main service',
    'checkout.total.services': 'Services ({count})',
    'checkout.total.addons': 'Add-ons ({count})',
    'checkout.total.discount': 'Discount',
    'checkout.total.total': 'Total',
    'checkout.payment.hint':
      'All items in the cart are paid in a single payment. You will get access to all services immediately after payment.',
    'checkout.payment.hintBold': 'single payment',
    'checkout.agree.text': 'I agree to the',
    'checkout.agree.terms': 'terms of service',
    'checkout.agree.and': 'and',
    'checkout.agree.privacy': 'privacy policy',
    'checkout.pay.processing': 'Processing...',
    'checkout.pay.loginRequired': 'Sign in to pay',
    'checkout.pay.button': 'Pay {price}',
    'checkout.trust.ssl': 'Secure payment. 256-bit SSL encryption.',
    'checkout.trust.refund': '14-day money-back guarantee',
    'checkout.trust.noSave': 'Card details are not stored on the server',
    'checkout.mobile.total': 'Total',
    'checkout.mobile.pay': 'Pay',
    'checkout.login.required': 'Please sign in or register',
    'checkout.agree.required': 'Please accept the terms to continue',
    'checkout.success': 'Payment successful! Order placed.',
    'checkout.successMobile': 'Payment successful!',
    'checkout.back': 'Back',
    'checkout.delete': 'Delete',

    // Auth page
    'auth.login.title': 'Welcome back',
    'auth.login.desc': 'Sign in to continue',
    'auth.register.title': 'Create an account',
    'auth.register.desc': 'Join the Astriqo platform',
    'auth.tab.login': 'Sign in',
    'auth.tab.register': 'Register',
    'auth.name': 'Name',
    'auth.name.placeholder': 'Your name',
    'auth.email': 'Email',
    'auth.email.placeholder': 'mail@example.com',
    'auth.password': 'Password',
    'auth.password.placeholder': '••••••••',
    'auth.forgot': 'Forgot password?',
    'auth.agree.text': 'I agree to the',
    'auth.agree.terms': 'terms of use',
    'auth.agree.and': 'and',
    'auth.agree.privacy': 'privacy policy',
    'auth.btn.login': 'Sign in',
    'auth.btn.register': 'Register',
    'auth.social.title': 'Or continue with',
    'auth.success.login': 'Successfully signed in!',
    'auth.success.register': 'Registration successful!',

    // Order Confirmation
    'order.success.title': 'Payment successful!',
    'order.success.desc':
      'Thank you for your order. We have sent the receipt and order details to your email.',
    'order.details.number': 'Order number',
    'order.details.date': 'Order date',
    'order.details.title': 'Order details',
    'order.details.astrologer': 'Astrologer: {name}',
    'order.details.total': 'Total paid',
    'order.next.title': "What's next?",

    'order.next.pkg.1.title': 'Package activated',
    'order.next.pkg.1.desc':
      'Reports have been added to your dashboard. You can use them at any time.',
    'order.next.pkg.2.title': 'Create your first report',
    'order.next.pkg.2.desc':
      'Go to the services section, choose the desired analysis, and apply a report from the package during checkout.',

    'order.next.report.1.title': 'Report is generating',
    'order.next.report.1.desc':
      'Our system is analyzing your data. This will take a few minutes.',
    'order.next.report.2.title': 'Check your email',
    'order.next.report.2.desc':
      'We will send the PDF file to your email and also save it in your dashboard.',

    'order.next.consult.1.title': 'Astrologer contact',
    'order.next.consult.1.desc':
      'Within 24 hours, {name} will write to you in the platform chat to agree on a call time.',
    'order.next.consult.2.title': 'Data preparation',
    'order.next.consult.2.desc':
      'Please prepare the exact time and place of your birth — this is necessary to build the chart.',

    'order.btn.dashboard': 'Go to dashboard',
    'order.btn.catalog': 'Return to catalog',

    // Admin Analytics Dashboard
    'admin.analytics.title': 'Platform Analytics',
    'admin.analytics.subtitle': 'Overview of key metrics and indicators',
    'admin.analytics.editing': 'Edit mode',
    'admin.analytics.overview': 'Overview',
    'admin.analytics.saveDashboard': 'Save layout',
    'admin.analytics.customize': 'Customize dashboard',
    'admin.analytics.layoutSaved': 'Dashboard layout saved',
    'admin.analytics.hiddenWidgets': 'Hidden widgets',
    'admin.analytics.emptyTitle': 'Dashboard is empty',
    'admin.analytics.emptyDesc': 'All analytics widgets are hidden.',
    'admin.analytics.period.7d': '7 days',
    'admin.analytics.period.30d': '30 days',
    'admin.analytics.period.3m': '3 months',
    'admin.analytics.period.1y': 'Year',
    'admin.widget.tracking': 'Counters & Integrations',
    'admin.widget.tracking.desc': 'Add analytics codes to track traffic',
    'admin.widget.tracking.connected': 'Connected: {count} of {total}',
    'admin.widget.tracking.active': '{count} active',
    'admin.widget.tracking.check': 'Check connection',
    'admin.widget.tracking.hint':
      'Codes are saved automatically. Toggle the switch to activate a counter.',
    'admin.widget.tracking.noActive':
      'No active counters. Enter the ID and toggle the switch.',
    'admin.widget.tracking.saved': 'Saved: {names}',
    'admin.widget.tracking.enterFirst': 'Enter the counter ID first',
    'admin.widget.tracking.disabled': '{name} disabled',
    'admin.widget.tracking.enabled': '{name} connected',
    'admin.widget.kpi': 'Key Performance Indicators (KPI)',
    'admin.widget.kpi.revenue': 'Total revenue',
    'admin.widget.kpi.users': 'Active users',
    'admin.widget.kpi.orders': 'Total orders',
    'admin.widget.kpi.conversion': 'Order conversion',
    'admin.widget.revenue': 'Revenue by month',
    'admin.widget.revenue.desc': 'Trends over the last six months',
    'admin.widget.revenue.detail': 'Details',
    'admin.widget.revenue.period': '+67.7% for the period',
    'admin.widget.funnel': 'Conversion funnel',
    'admin.widget.funnel.desc': 'User journey',
    'admin.widget.funnel.visitors': 'Visitors',
    'admin.widget.funnel.registrations': 'Registrations',
    'admin.widget.funnel.checkout': 'Started checkout',
    'admin.widget.funnel.payments': 'Successful payments',
    'admin.widget.traffic': 'Traffic sources',
    'admin.widget.traffic.desc': 'Where users come from',
    'admin.widget.traffic.tooltip':
      'Data is collected automatically from connected counters and UTM tags in orders.',
    'admin.widget.traffic.settings': 'Tracking settings',
    'admin.widget.geo': 'Geography',
    'admin.widget.geo.desc': 'Top cities',
    'admin.widget.geo.tooltip':
      'Data is collected automatically based on visitor IP addresses and order data.',
    'admin.widget.services': 'Orders by service',
    'admin.widget.services.all': 'All services',
    'admin.widget.subs': 'Subscriptions',
    'admin.widget.subs.manage': 'Manage',
    'admin.widget.subs.pro': 'Pro plan',
    'admin.widget.subs.plus': 'Plus plan',
    'admin.widget.subs.free': 'Free',
    'admin.widget.subs.ltv': 'Average check (LTV)',
    'admin.widget.astrologers': 'Top astrologers',
    'admin.widget.astrologers.catalog': 'Astrologer catalog',
    'admin.widget.metrics.avgTime': 'Avg. time on site',
    'admin.widget.metrics.bounceRate': 'Bounce rate',
    'admin.widget.metrics.repeatPurchase': 'Repeat purchases',
    'admin.widget.metrics.nps': 'NPS (Loyalty)',
    'admin.widget.metrics.vsPrev': 'vs prev. period',
    'admin.widget.realtime': 'Online now',
    'admin.widget.realtime.users': 'Users online',
    'admin.widget.realtime.views': '{count} views/min',
    'admin.widget.realtime.tooltip':
      'Updated in real time based on active sessions from counters.',
    'admin.widget.alerts': 'Notifications',
    'admin.widget.alerts.desc': 'Important events and anomalies',
    'admin.widget.alerts.new': '{count} new',
    'admin.widget.devices': 'Devices',
    'admin.widget.devices.desc': 'Device types',
    'admin.widget.devices.tooltip':
      'Data from Yandex.Metrica / Google Analytics. Requires counter connection.',
    'admin.widget.devices.mobile': 'Mobile',
    'admin.widget.devices.desktop': 'Desktop',
    'admin.widget.devices.tablet': 'Tablets',
    'admin.widget.pages': 'Popular pages',
    'admin.widget.pages.desc': 'Top by views for the period',
    'admin.widget.pages.seo': 'SEO report',
    'admin.widget.content': 'Content analytics',
    'admin.widget.content.desc': 'Blog statistics',
    'admin.widget.content.manage': 'Manage blog',
    'admin.widget.content.views': 'Views',
    'admin.widget.content.avgRead': 'Avg. read',
    'admin.widget.reviews': 'Reviews & rating',
    'admin.widget.reviews.desc': 'User feedback',
    'admin.widget.reviews.moderation': 'Moderation',
    'admin.widget.reviews.count': '{count} reviews',
    'admin.widget.reviews.positive': 'Positive',
    'admin.widget.reviews.neutral': 'Neutral',
    'admin.widget.reviews.negative': 'Negative',
    'admin.widget.email': 'Email campaigns',
    'admin.widget.email.desc': 'Campaign statistics',
    'admin.widget.email.manage': 'Campaigns',
    'admin.widget.email.opens': 'Opens',
    'admin.widget.email.clicks': 'Clicks',
    'admin.widget.email.sent': 'Sent: {count}',
    'admin.widget.email.unsubs': 'Unsubscribes: {count}',
    'admin.widget.email.openRate': '{rate}% open',
    'admin.widget.email.clickRate': '{rate}% click',
    'admin.widget.cac': 'Customer Acquisition Cost (CAC)',
    'admin.widget.cac.tooltip':
      'CAC — average cost of acquiring one paying customer through each channel.',
    'admin.widget.cac.avg': 'Average CAC: {value} ₽',
    'admin.widget.cac.marketing': 'Marketing',
    'admin.widget.cac.free': 'Free',
    'admin.widget.cac.orders': '{count} orders',

    // Astrologer Dashboard — Shell
    'astrodash.title': 'Astrologer Dashboard',
    'astrodash.group.main': 'Main',
    'astrodash.group.products': 'Products',
    'astrodash.group.business': 'Business',
    'astrodash.group.settings': 'Settings',
    'astrodash.tab.overview': 'Overview',
    'astrodash.tab.orders': 'Orders',
    'astrodash.tab.consultations': 'Consultations',
    'astrodash.tab.chat': 'Client Chat',
    'astrodash.tab.services': 'Services',
    'astrodash.tab.marketplace': 'Courses',
    'astrodash.tab.calendar': 'Schedule',
    'astrodash.tab.finances': 'Finances',
    'astrodash.tab.promotion': 'Promotion',
    'astrodash.tab.reviews': 'Reviews',
    'astrodash.tab.stats': 'Statistics',
    'astrodash.tab.profile': 'Public Profile',
    'astrodash.tab.settings': 'Settings & Security',
    'astrodash.tab.subscription': 'Subscription',
    'astrodash.logout': 'Log Out',
    'astrodash.backToSite': 'Back to Site',
    'astrodash.menu': 'Menu',
    'astrodash.welcome': 'Hello, {name}!',
    'astrodash.role': 'Astrologer',

    // Astrologer Dashboard — Overview Tab
    'astrodash.overview.title': 'Overview',
    'astrodash.overview.subtitle': 'Key metrics for today',
    'astrodash.overview.todayIncome': "Today's Income",
    'astrodash.overview.newOrders': 'New Orders',
    'astrodash.overview.activeConsultations': 'Active Consultations',
    'astrodash.overview.profileViews': 'Profile Views',
    'astrodash.overview.rating': 'Rating',
    'astrodash.overview.position': 'Catalog Position',
    'astrodash.overview.quickActions': 'Quick Actions',
    'astrodash.overview.recentOrders': 'Recent Orders',
    'astrodash.overview.viewAll': 'View All',
    'astrodash.overview.noOrders': 'No orders yet',
    'astrodash.overview.schedule': "Today's Schedule",
    'astrodash.overview.noSchedule': 'No appointments for today',
    'astrodash.overview.tips': 'Improvement Tips',
    'astrodash.overview.fillProfile': 'Complete your profile to 100%',
    'astrodash.overview.addServices': 'Add more services',
    'astrodash.overview.enablePromo': 'Enable promotion',

    // Astrologer Dashboard — Promotion Tab
    'astrodash.promo.title': 'Profile Promotion',
    'astrodash.promo.subtitle': 'Increase orders and income',
    'astrodash.promo.budget': 'Ad Budget',
    'astrodash.promo.available': 'available',
    'astrodash.promo.spentMonth': 'Spent this month',
    'astrodash.promo.remaining': 'Remaining',
    'astrodash.promo.topUp': 'Top Up Balance',
    'astrodash.promo.hint':
      'Ads are charged from the ad budget. If funds are insufficient, top up your balance.',
    'astrodash.promo.tariff': 'Plan',
    'astrodash.promo.position': 'Position',
    'astrodash.promo.views': 'Views',
    'astrodash.promo.conversion': 'Conversion',
    'astrodash.promo.adIncome': 'Ad Income',
    'astrodash.promo.adOrders': 'Ad Orders',
    'astrodash.promo.howToIncrease': 'How to Increase Income',
    'astrodash.promo.proAdvice':
      'Upgrade to PRO and get catalog priority. Statistics show PRO astrologers receive 40% more orders.',
    'astrodash.promo.upgradePro': 'Upgrade to PRO',
    'astrodash.promo.enableAds': 'Enable Ads',
    'astrodash.promo.launchAd': 'Launch Ad',
    'astrodash.promo.boostProfile': 'Boost Profile',
    'astrodash.promo.enableMailing': 'Enable Mailing',
    'astrodash.promo.upgradeTariff': 'Upgrade Plan',
    'astrodash.promo.myAdOrders': 'My Ad Orders',
    'astrodash.promo.howAdsWork': 'How do ads work?',
    'astrodash.promo.hideHelp': 'Hide help',
    'astrodash.promo.newOrder': 'New Order',
    'astrodash.promo.step1.title': 'Submission & Payment',
    'astrodash.promo.step1.desc':
      'Choose a format, upload materials, and pay from your ad balance.',
    'astrodash.promo.step2.title': 'Moderation (1-12 hours)',
    'astrodash.promo.step2.desc':
      'Admin will review materials. Results will appear in system notifications.',
    'astrodash.promo.step3.title': 'Launch & Analytics',
    'astrodash.promo.step3.desc':
      'After approval, ads launch automatically. Track views and clicks here.',
    'astrodash.promo.service': 'Service',
    'astrodash.promo.type': 'Type',
    'astrodash.promo.status': 'Status',
    'astrodash.promo.period': 'Period',
    'astrodash.promo.cost': 'Cost',
    'astrodash.promo.result': 'Result',
    'astrodash.promo.clicks': 'clicks',
    'astrodash.promo.orders': 'orders',
    'astrodash.promo.banner': 'Banner',
    'astrodash.promo.mailing': 'Mailing',
    'astrodash.promo.pin': 'Pin',
    'astrodash.promo.statusActive': 'Active',
    'astrodash.promo.statusCompleted': 'Completed',
    'astrodash.promo.statusModeration': 'Under Review',
    'astrodash.promo.statusPayment': 'Awaiting Payment',
    'astrodash.promo.statusApproved': 'Approved',
    'astrodash.promo.statusRejected': 'Rejected',
    'astrodash.promo.onModeration': 'Order Under Review',
    'astrodash.promo.awaitingPayment': 'Awaiting Payment',
    'astrodash.promo.rejected': 'Order Rejected',
    'astrodash.promo.checkingAd':
      'Your ad is being reviewed by admin. This usually takes 1-12 hours.',
    'astrodash.promo.moderationPassed':
      'Moderation passed! Pay the order to launch your ad.',
    'astrodash.promo.moderationFailed':
      'Moderation failed. Fix the issues and resubmit.',
    'astrodash.promo.rejectionReason': 'Rejection reason',
    'astrodash.promo.stepCreated': 'Created',
    'astrodash.promo.stepModeration': 'Moderation',
    'astrodash.promo.stepPayment': 'Payment',
    'astrodash.promo.stepLaunch': 'Launch',
    'astrodash.promo.inProgress': 'In progress...',
    'astrodash.promo.awaitingPaymentShort': 'Awaiting payment',
    'astrodash.promo.awaiting': 'Pending',
    'astrodash.promo.paid': 'Paid',
    'astrodash.promo.payBtn': 'Pay {price} ₽',
    'astrodash.promo.fixAndResend': 'Fix & Resubmit',
    'astrodash.promo.repeat': 'Repeat',
    'astrodash.promo.extend': 'Extend',
    'astrodash.promo.income': 'Income',
    'astrodash.promo.createAd': 'Create Ad',
    'astrodash.promo.selectSlot':
      'Choose an ad slot. Each format is displayed in different parts of the site.',
    'astrodash.promo.format': 'Format',
    'astrodash.promo.materials': 'Materials',
    'astrodash.promo.payment': 'Payment',
    'astrodash.promo.next': 'Next',
    'astrodash.promo.back': 'Back',
    'astrodash.promo.reach': 'Reach',
    'astrodash.promo.requirements': 'Requirements',
    'astrodash.promo.adTitle': 'Ad Title',
    'astrodash.promo.adTitlePlaceholder':
      'E.g.: Professional natal chart analysis',
    'astrodash.promo.adDesc': 'Description',
    'astrodash.promo.adDescPlaceholder': 'Briefly describe your offer',
    'astrodash.promo.adLink': 'Link',
    'astrodash.promo.adImage': 'Upload Image',
    'astrodash.promo.replaceImage': 'Replace Image',
    'astrodash.promo.orderSummary': 'Order Summary',
    'astrodash.promo.total': 'Total',
    'astrodash.promo.adBalance': 'Ad Balance',
    'astrodash.promo.insufficientFunds':
      'Insufficient funds. Top up by {amount} ₽',
    'astrodash.promo.remainingAfter': 'Remaining after payment',
    'astrodash.promo.payAndSubmit': 'Pay & Submit',
    'astrodash.promo.orderPaid':
      'Order paid and sent for moderation. You will receive a status notification.',
    'astrodash.promo.cancel': 'Cancel',

    // Common astrologer dashboard
    'astrodash.common.save': 'Save',
    'astrodash.common.cancel': 'Cancel',
    'astrodash.common.delete': 'Delete',
    'astrodash.common.edit': 'Edit',
    'astrodash.common.add': 'Add',
    'astrodash.common.search': 'Search',
    'astrodash.common.filter': 'Filter',
    'astrodash.common.reset': 'Reset',
    'astrodash.common.noData': 'No data',
    'astrodash.common.loading': 'Loading...',
    'astrodash.common.confirm': 'Confirm',
    'astrodash.common.close': 'Close',
    'astrodash.common.viewAll': 'View All',
    'astrodash.common.today': 'Today',
    'astrodash.common.week': 'Week',
    'astrodash.common.month': 'Month',
    'astrodash.common.year': 'Year',
    'astrodash.common.from': 'From',
    'astrodash.common.to': 'To',
    'astrodash.common.total': 'Total',
    'astrodash.common.status': 'Status',
    'astrodash.common.actions': 'Actions',
    'astrodash.common.details': 'Details',
    'astrodash.common.back': 'Back',
    'astrodash.common.next': 'Next',
  },

  zh: {
    'nav.services': '服务',
    'nav.astrologers': '占星师',
    'nav.knowledge': '知识',
    'nav.pricing': '价格',
    'nav.marketplace': '服务市场',
    'nav.freeChart': '免费星盘',
    'nav.freeChartFull': '免费计算星盘',
    'nav.login': '登录',
    'nav.cart': '购物车',
    'services.natal': '本命盘',
    'services.natal.desc': '深度性格分析',
    'services.solar': '太阳回归',
    'services.solar.desc': '您的年度趋势',
    'services.compatibility': '合盘',
    'services.compatibility.desc': '情侣合盘分析',
    'services.combo': '组合：本命盘+太阳回归',
    'services.combo.desc': '完整分析+年度预测',
    'services.forecast': '预测（行运）',
    'services.forecast.desc': '关键时期与日期',
    'services.consultation': '咨询',
    'services.consultation.desc': '与占星师实时对话',
    'services.marketplace': '服务市场',
    'services.marketplace.desc': '所有服务与咨询',
    'knowledge.interpretations': '解读',
    'knowledge.interpretations.desc': '占星知识库',
    'knowledge.calendar': '星历',
    'knowledge.calendar.desc': '重要天象事件',
    'knowledge.blog': '博客',
    'knowledge.blog.desc': '文章与预测',
    'profile.dashboard': '我的面板',
    'profile.astrologer': '占星师面板',
    'profile.logout': '退出',
    'profile.admin': '管理面板',
    'mobile.services': '服务',
    'mobile.services.desc': '报告、预测、咨询',
    'mobile.knowledge.desc': '日历、解读、博客',
    'mobile.platform': '平台',
    'mobile.account': '账户',
    'mobile.language': '语言',
    'mobile.client': '客户',
    'mobile.astrologer': '占星师',
    'footer.services': '服务',
    'footer.platform': '平台',
    'footer.partners': '合作伙伴',
    'footer.natal': '本命盘',
    'footer.solar': '太阳回归',
    'footer.compatibility': '合盘',
    'footer.pricingPackages': '价格与套餐',
    'footer.astrologersCatalog': '占星师目录',
    'footer.interpretations': '解读库',
    'footer.calendar': '星历',
    'footer.blog': '博客',
    'footer.forAstrologers': '占星师入驻',
    'footer.advertising': '广告合作',
    'footer.astrologerDashboard': '占星师面板',
    'footer.combo': '组合分析',
    'footer.forecast': '行运预测',
    'footer.consultation': '咨询',
    'footer.marketplace': '服务市场',
    'footer.free': '免费工具',
    'footer.astroCalendar': '星历',
    'footer.freeChart': '免费星盘',
    'footer.natalDemo': '在线本命盘',
    'footer.reportPreview': '报告示例',
    'footer.premiumReport': '高级报告',
    'footer.knowledge': '知识',
    'footer.faq': '常见问题',
    'footer.company': '公司',
    'footer.about': '关于我们',
    'footer.contact': '联系我们',
    'footer.newsletter.title': '订阅星象周报',
    'footer.newsletter.desc': '每周预测、实用文章和专属折扣。',
    'footer.newsletter.placeholder': '您的邮箱',
    'footer.newsletter.button': '订阅',
    'footer.newsletter.success': '订阅成功！请留意我们的邮件。',
    'footer.securePayment': '安全支付',
    'footer.copyright': '© {year} Astriqo. 保留所有权利。',
    'footer.privacy': '隐私政策',
    'footer.terms': '使用条款',
    'footer.offer': '公开报价',
    'footer.description':
      '通过专业占星术深度自我认知的平台。自动报告和经过验证的专家咨询。',
    'knowledge.faq': '常见问题',
    'knowledge.faq.desc': '平台帮助',
    'ui.learnMore': '了解更多',
    'ui.select': '选择',
    'ui.buy': '购买',
    'ui.profile': '个人资料',
    'ui.order': '订购',
    'ui.search': '搜索',
    'ui.filter': '筛选',
    'ui.reset': '重置',
    'ui.back': '返回',
    'ui.next': '下一步',
    'ui.close': '关闭',
    'ui.loading': '加载中...',

    // Legal Pages
    'legal.lastUpdated': '最后更新：2026年5月15日',
    'legal.backToHome': '主页',
    'legal.contactUs': '有问题？联系我们',

    // Privacy Policy
    'legal.privacy.title': '隐私政策',
    'legal.privacy.s1.title': '1. 一般规定',
    'legal.privacy.s1.p1':
      '本隐私政策（以下简称“政策”）确定了处理和保护Astriqo服务（以下简称“服务”）用户个人数据的程序。我们尊重您数据的隐私，并尽一切努力保护它。',
    'legal.privacy.s2.title': '2. 我们收集哪些数据',
    'legal.privacy.s2.p1': '为了提供占星服务，我们收集以下数据：',
    'legal.privacy.s2.l1': '注册数据：姓名，电子邮件地址，电话号码。',
    'legal.privacy.s2.l2':
      '占星数据：出生日期，确切时间和地点（计算本命盘所必需）。',
    'legal.privacy.s2.l3': '技术数据：IP地址，浏览器类型，cookie数据。',
    'legal.privacy.s3.title': '3. 数据的使用',
    'legal.privacy.s3.p1': '我们仅将您的数据用于以下目的：',
    'legal.privacy.s3.l1': '生成个人占星报告。',
    'legal.privacy.s3.l2': '组织与平台占星师的咨询。',
    'legal.privacy.s3.l3': '提高服务质量。',
    'legal.privacy.s3.l4': '发送重要通知（例如，关于报告准备就绪）。',
    'legal.privacy.s4.title': '4. 向第三方传输数据',
    'legal.privacy.s4.p1':
      '我们不会出于营销目的向第三方出售或传输您的个人数据。您的占星数据（出生日期，时间，地点）可能仅为了进行付费咨询而传输给您选择的占星师。',
    'legal.privacy.s5.title': '5. 数据安全',
    'legal.privacy.s5.p1':
      '我们使用现代加密和数据保护方法来防止未经授权的访问。所有付款均通过经过认证的支付系统的安全网关进行处理。',
    'legal.privacy.s6.title': '6. 您的权利',
    'legal.privacy.s6.p1': '您随时有权：',
    'legal.privacy.s6.l1': '请求存储在服务中的所有数据的副本。',
    'legal.privacy.s6.l2': '通过您的个人帐户设置编辑或删除您的数据。',
    'legal.privacy.s6.l3': '撤回对处理个人数据的同意。',
    'legal.privacy.s7.title': '7. 联系方式',
    'legal.privacy.s7.p1':
      '如果您对本隐私政策有任何疑问，请通过以下方式联系我们：privacy@astriqo.com。',

    // Terms of Service
    'legal.terms.title': '服务条款',
    'legal.terms.s1.title': '1. 协议主题',
    'legal.terms.s1.p1':
      '本用户协议（以下简称“协议”）规范Astriqo服务（以下简称“服务”）与互联网用户（以下简称“用户”）之间因使用服务而产生的关系。',
    'legal.terms.s2.title': '2. 提供服务的性质',
    'legal.terms.s2.p1':
      '服务提供占星领域的信息和娱乐服务。所有生成的报告，预测和咨询本质上都是建议性的。服务不对用户根据收到的信息做出的决定负责。',
    'legal.terms.s2.p2':
      '重要提示：占星咨询和报告不能取代专业的医疗，心理，法律或财务援助。',
    'legal.terms.s3.title': '3. 注册和帐户',
    'legal.terms.s3.p1':
      '要访问服务的全部功能，用户必须完成注册程序。用户承诺提供有关自己的可靠信息，并负责其登录数据（登录名和密码）的安全。',
    'legal.terms.s4.title': '4. 付款和退款',
    'legal.terms.s4.p1':
      '服务（高级报告，咨询）的付款以100％预付款为基础。由于自动报告是数字内容，可立即提供访问权限，因此默认情况下不提供退款（根据消费者保护法）。',
    'legal.terms.s4.p2':
      '如果在预定时间前至少24小时取消，则可以退还与占星师咨询的费用。',
    'legal.terms.s5.title': '5. 平台行为准则',
    'legal.terms.s5.p1': '禁止用户：',
    'legal.terms.s5.l1': '在与占星师或支持人员沟通时使用淫秽语言和侮辱。',
    'legal.terms.s5.l2':
      '未经书面许可，复制，分发或转售服务材料（报告，文章）。',
    'legal.terms.s5.l3': '使用自动脚本（机器人）从服务中收集信息。',
    'legal.terms.s6.title': '6. 责任限制',
    'legal.terms.s6.p1':
      '服务按"原样"提供。我们不保证服务将满足您的要求，将连续，快速，可靠且无错误地提供。',
    'legal.terms.s7.title': '7. 条款变更',
    'legal.terms.s7.p1':
      '服务管理部门保留随时更改本协议的权利。在进行更改后继续使用服务意味着用户同意新版本的协议。',

    // Public Offer
    'legal.offer.title': '公开报价',
    'legal.offer.s1.title': '1. 一般规定',
    'legal.offer.s1.p1':
      '本文件是Astriqo服务（以下简称"承包商"）的公开报价，包含提供信息和咨询服务的所有基本条件。',
    'legal.offer.s2.title': '2. 报价主题',
    'legal.offer.s2.p1':
      '承包商承诺为客户提供在Astriqo平台上访问占星报告和咨询的服务，客户承诺为这些服务付款。',
    'legal.offer.s3.title': '3. 提供服务的程序',
    'legal.offer.s3.p1':
      '服务通过客户在网站上的个人帐户以电子方式提供。付款后自动提供对数字商品（报告）的访问权限。',
    'legal.offer.s4.title': '4. 费用和付款程序',
    'legal.offer.s4.p1':
      '服务费用在网站上注明。付款通过集成支付系统以银行转账方式进行。',
    'legal.offer.s5.title': '5. 双方的权利和义务',
    'legal.offer.s5.p1':
      '承包商承诺按时高效地提供服务。客户承诺提供可靠的计算数据（出生日期，时间，地点）。',
    'legal.offer.s6.title': '6. 双方的责任',
    'legal.offer.s6.p1': '双方根据现行法律对未履行义务负责。',
    'legal.offer.s7.title': '7. 不可抗力',
    'legal.offer.s7.p1':
      '如果由于不可抗力情况导致未履行本协议项下的义务，双方免除对部分或全部未履行义务的责任。',
    'legal.offer.s8.title': '8. 争议解决',
    'legal.offer.s8.p1':
      '所有争议和分歧均通过谈判解决。如果未达成协议，争议将提交法院。',

    // Encyclopedia page
    'enc.badge': '知识库',
    'enc.title': '占星百科全书',
    'enc.subtitle':
      '关于占星术的一切：从基础到高级技术。教育文章、本命盘解读和我们的服务 — {count} 篇文章。',
    'enc.searchPlaceholder': '例如：金星在金牛座、上升在狮子座...',
    'enc.popular': '热门：',
    'enc.results': '结果 ({count})',
    'enc.resetFilters': '重置筛选',
    'enc.notFound': '未找到结果',
    'enc.notFoundHint': '请尝试更改搜索词或选择其他字母。',
    'enc.showCategories': '显示所有分类',
    'enc.readMore': '阅读更多',
    'enc.learnMore': '了解更多',
    'enc.articles': '篇文章',
    'enc.backToList': '返回列表',
    'enc.alphabetTitle': '字母索引',
    'enc.keyFacts': '关键事实',
    'enc.commonMyths': '常见误区',
    'enc.astrologerTip': '占星师建议',
    'enc.strengths': '优势',
    'enc.challenges': '挑战',
    'enc.relatedArticles': '相关文章',
    'enc.defaultTip':
      '这个位置的能量需要有意识的方法。依靠你的优势，注意负面模式。成长始于自我意识。',
    'enc.ctaEncTitle': '将知识付诸实践',
    'enc.ctaIntTitle': '看看这在你的星盘中如何运作',
    'enc.ctaEncDesc':
      '占星术最好通过自己的例子来学习。订购个人报告，了解关于自己的一切。',
    'enc.ctaIntDesc':
      '一个位置只是全貌的一部分。订购完整报告，看看行星如何在你独特的星盘中互动。',
    'enc.defaultCta': '计算你的本命盘',
    'enc.basicsTitle': '占星基础',
    'enc.basicsCard': '基本概念',
    'enc.basicsDesc': '什么是占星术、本命盘、星座、行星和宫位。适合初学者。',
    'enc.advancedCard': '高级主题',
    'enc.advancedDesc':
      '行运、太阳回归、合盘、月亮交点和逆行行星。适合想深入了解的人。',
    'enc.interpretationsTitle': '解读参考',
    'enc.planetsSignsCard': '行星在星座',
    'enc.planetsSignsDesc':
      '10颗行星（太阳到冥王星）在12星座中的能量表现。性格的基本构建块。',
    'enc.ascendantCard': '上升星座',
    'enc.ascendantDesc': '你的社交面具、第一印象和感知过滤器。别人如何看你。',
    'enc.housesCard': '行星在宫位',
    'enc.housesDesc':
      '行星在哪些生活领域实现其潜力。太阳、月亮、水星、金星和火星在12宫。',
    'enc.aspectsCard': '相位',
    'enc.aspectsDesc': '行星间的互动：合相、三分相、四分相、对分相和六分相。',
    'enc.servicesTitle': '我们的服务',
    'enc.popularSearch1': '什么是本命盘',
    'enc.popularSearch2': '太阳在狮子座',
    'enc.popularSearch3': '占星术中的相位',

    // Free Chart Page
    'chart.title': '占星图计算',
    'chart.subtitle':
      '选择服务，输入出生数据，获取精确的行星位置计算或详细的PDF报告。',
    'chart.breadcrumb': '星盘计算',
    'chart.rating': '4.9 评分',
    'chart.calculations': '50,000+ 计算',
    'chart.freeBeta': '免费Beta计算',
    'chart.step.service': '服务',
    'chart.step.package': '套餐',
    'chart.step.data': '数据',
    'chart.step.result': '结果',
    'chart.step1.title': '选择服务',
    'chart.step1.subtitle': '您想进行什么计算？',
    'chart.popular': '热门',
    'chart.new': '新品',
    'chart.fromPrice': '从 2,990 ₽',
    'chart.freePremium': '免费 + Premium',
    'chart.step2.titleConsultation': '选择格式',
    'chart.step2.titleReport': '选择套餐',
    'chart.step2.forService': '服务「{name}」',
    'chart.tier.free.title': 'Beta（基础）',
    'chart.tier.free.badge': '免费',
    'chart.tier.free.desc': '主要指标的简要计算，无文字解读',
    'chart.tier.pro.title': 'Premium（单次）',
    'chart.tier.pro.badge': '推荐',
    'chart.tier.pro.desc': '20-40页完整PDF报告，含详细分析和建议',
    'chart.tier.package.title': 'Premium（套餐）',
    'chart.tier.package.badge': '超值',
    'chart.tier.package.desc': '折扣报告套餐，可用于平台任何服务',
    'chart.tier.package.fromPrice': '从 1,590 ₽/个',
    'chart.bundle.hit': '热销',
    'chart.bundle.reports': '{count} 份报告',
    'chart.consult.short.title': '短期咨询',
    'chart.consult.short.desc': '30分钟 • 1个具体问题',
    'chart.consult.short.badge': '快速',
    'chart.consult.standard.title': '标准咨询',
    'chart.consult.standard.desc': '60分钟 • 完整星盘解读',
    'chart.consult.standard.badge': '推荐',
    'chart.consult.extended.title': '深度咨询',
    'chart.consult.extended.desc': '90分钟 • 录音+详细建议',
    'chart.consult.extended.badge': '最大化',
    'chart.step3.title': '输入数据',
    'chart.step3.subtitle': '计算服务「{name}」',
    'chart.field.name': '姓名（可选）',
    'chart.field.namePlaceholder': '例如，安娜',
    'chart.field.date': '出生日期 *',
    'chart.field.time': '出生时间 *',
    'chart.field.timeHint': '如不知道，请输入12:00',
    'chart.field.city': '出生城市 *',
    'chart.field.cityPlaceholder': '例如，莫斯科',
    'chart.field.person1': '第一位伴侣',
    'chart.field.person2': '第二位伴侣',
    'chart.field.forecastYear': '预测年份 *',
    'chart.field.forecastPeriod': '预测周期 *',
    'chart.period.3months': '3个月',
    'chart.period.6months': '6个月',
    'chart.period.1year': '1年',
    'chart.btn.calculate': '计算',
    'chart.btn.book': '预约',
    'chart.btn.back': '返回',
    'chart.btn.next': '下一步',
    'chart.calculating.title': '正在分析数据...',
    'chart.calculating.subtitle': '正在计算精确的行星位置',
    'chart.result.fullReport': '完整报告',
    'chart.result.basicReport': '基础报告',
    'chart.result.newCalc': '新计算',
    'chart.result.planets': '行星位置',
    'chart.result.planet': '行星',
    'chart.result.sign': '星座',
    'chart.result.degree': '度数',
    'chart.result.briefInterpretation': '简要解读',
    'chart.result.wantFull': '想要完整分析吗？',
    'chart.result.fullDesc':
      '获取30-40页详细PDF报告，深度解读所有指标、业力任务和个人建议。',
    'chart.result.getPremium': '获取Premium报告',
    'chart.result.questions': '还有疑问？',
    'chart.result.consultDesc': '预约与占星师的实时咨询',
    'chart.result.chooseAstrologer': '选择占星师',
    'chart.trust.accurate.title': '精确计算',
    'chart.trust.accurate.desc':
      '我们使用专业的Swiss Ephemeris算法以确保最高精度。',
    'chart.trust.privacy.title': '隐私保护',
    'chart.trust.privacy.desc': '您的出生数据受到保护，仅用于星盘计算。',
    'chart.trust.instant.title': '即时结果',
    'chart.trust.instant.desc': '几秒内获取基础计算和行星位置。',
    'chart.planet.sun': '太阳',
    'chart.planet.moon': '月亮',
    'chart.planet.mercury': '水星',
    'chart.planet.venus': '金星',
    'chart.planet.mars': '火星',
    'chart.planet.jupiter': '木星',
    'chart.planet.saturn': '土星',
    'chart.sign.aries': '白羊座',
    'chart.sign.taurus': '金牛座',
    'chart.sign.gemini': '双子座',
    'chart.sign.pisces': '双鱼座',
    'chart.sign.sagittarius': '射手座',
    'chart.sign.capricorn': '摩羯座',

    // Checkout page
    'checkout.title': '结账',
    'checkout.breadcrumb.home': '首页',
    'checkout.breadcrumb.marketplace': '服务市场',
    'checkout.breadcrumb.title': '结账',
    'checkout.step.product': '商品',
    'checkout.step.checkout': '结账',
    'checkout.step.payment': '支付',
    'checkout.addons.title': '附加服务',
    'checkout.addons.subtitle': '用额外选项增强您的订单',
    'checkout.addons.reports': '报告',
    'checkout.addons.consultations': '咨询',
    'checkout.addons.packages': '套餐',
    'checkout.addons.natal': '本命盘 Premium',
    'checkout.addons.compatibility': '合盘 Premium',
    'checkout.addons.solar': '太阳回归 Premium',
    'checkout.addons.forecast': '预测（行运）Premium',
    'checkout.addons.combo': '组合：本命盘+太阳回归',
    'checkout.addons.consult30': '咨询 30 分钟',
    'checkout.addons.consult60': '咨询 60 分钟',
    'checkout.addons.consult90': '咨询 90 分钟',
    'checkout.addons.pkg20': '20份报告套餐',
    'checkout.addons.pkg50': '50份报告套餐',
    'checkout.addons.pkg100': '100份报告套餐',
    'checkout.auth.title': '登录以下单',
    'checkout.auth.desc':
      '需要注册才能接收报告并保存数据。您的出生数据和报告将在个人面板中可用。',
    'checkout.auth.login': '登录',
    'checkout.auth.register': '注册',
    'checkout.auth.hint': '通过电子邮件或社交网络快速注册',
    'checkout.payment.title': '支付方式',
    'checkout.payment.card': '银行卡',
    'checkout.payment.sbp': 'SBP',
    'checkout.card.number': '卡号',
    'checkout.card.expiry': '有效期',
    'checkout.card.cvc': 'CVC',
    'checkout.card.name': '持卡人姓名',
    'checkout.sbp.hint': '在您的银行应用中扫描二维码',
    'checkout.sbp.qr': '支付二维码',
    'checkout.contact.title': '联系方式',
    'checkout.contact.name': '姓名',
    'checkout.contact.namePlaceholder': '您的姓名',
    'checkout.contact.email': '电子邮件',
    'checkout.contact.phone': '电话（可选）',
    'checkout.contact.phonePlaceholder': '+86 (999) 123-4567',
    'checkout.contact.privacy': '您的数据受到安全保护，不会与第三方共享',
    'checkout.order.title': '您的订单',
    'checkout.order.addMore': '添加其他服务',
    'checkout.order.addonsLabel': '附加项',
    'checkout.order.empty': '购物车为空',
    'checkout.order.autoGeneration': '自动生成',
    'checkout.promo.placeholder': '优惠码（如 ASTRO10）',
    'checkout.promo.apply': '应用',
    'checkout.promo.applied': '✓',
    'checkout.promo.success': '优惠码 ASTRO10 已应用！折扣 10%',
    'checkout.total.service': '主要服务',
    'checkout.total.services': '服务 ({count})',
    'checkout.total.addons': '附加项 ({count})',
    'checkout.total.discount': '折扣',
    'checkout.total.total': '合计',
    'checkout.payment.hint':
      '购物车中的所有商品通过单次付款支付。付款后您将立即获得所有服务的访问权限。',
    'checkout.payment.hintBold': '单次付款',
    'checkout.agree.text': '我同意',
    'checkout.agree.terms': '服务条款',
    'checkout.agree.and': '和',
    'checkout.agree.privacy': '隐私政策',
    'checkout.pay.processing': '处理中...',
    'checkout.pay.loginRequired': '登录以支付',
    'checkout.pay.button': '支付 {price}',
    'checkout.trust.ssl': '安全支付。256位SSL加密。',
    'checkout.trust.refund': '14天退款保证',
    'checkout.trust.noSave': '卡片信息不存储在服务器上',
    'checkout.mobile.total': '合计',
    'checkout.mobile.pay': '支付',
    'checkout.login.required': '请登录或注册',
    'checkout.agree.required': '请接受条款以继续',
    'checkout.success': '支付成功！订单已下达。',
    'checkout.successMobile': '支付成功！',
    'checkout.back': '返回',
    'checkout.delete': '删除',

    // Auth page
    'auth.login.title': '欢迎回来',
    'auth.login.desc': '登录以继续',
    'auth.register.title': '创建账户',
    'auth.register.desc': '加入 Astriqo 平台',
    'auth.tab.login': '登录',
    'auth.tab.register': '注册',
    'auth.name': '姓名',
    'auth.name.placeholder': '您的姓名',
    'auth.email': '电子邮件',
    'auth.email.placeholder': 'mail@example.com',
    'auth.password': '密码',
    'auth.password.placeholder': '••••••••',
    'auth.forgot': '忘记密码？',
    'auth.agree.text': '我同意',
    'auth.agree.terms': '使用条款',
    'auth.agree.and': '和',
    'auth.agree.privacy': '隐私政策',
    'auth.btn.login': '登录',
    'auth.btn.register': '注册',
    'auth.social.title': '或通过以下方式继续',
    'auth.success.login': '登录成功！',
    'auth.success.register': '注册成功！',

    // Order Confirmation
    'order.success.title': '支付成功！',
    'order.success.desc':
      '感谢您的订单。我们已将收据和订单详情发送到您的电子邮件。',
    'order.details.number': '订单号',
    'order.details.date': '订单日期',
    'order.details.title': '订单详情',
    'order.details.astrologer': '占星师：{name}',
    'order.details.total': '实付总额',
    'order.next.title': '下一步是什么？',

    'order.next.pkg.1.title': '套餐已激活',
    'order.next.pkg.1.desc': '报告已添加到您的个人面板。您可以随时使用它们。',
    'order.next.pkg.2.title': '创建您的第一份报告',
    'order.next.pkg.2.desc':
      '转到服务部分，选择所需的分析，并在结账时应用套餐中的报告。',

    'order.next.report.1.title': '报告生成中',
    'order.next.report.1.desc':
      '我们的系统正在分析您的数据。这需要几分钟时间。',
    'order.next.report.2.title': '检查您的电子邮件',
    'order.next.report.2.desc':
      '我们将把 PDF 文件发送到您的电子邮件，并保存在您的个人面板中。',

    'order.next.consult.1.title': '占星师联系',
    'order.next.consult.1.desc':
      '在 24 小时内，{name} 将在平台聊天中给您写信，以商定通话时间。',
    'order.next.consult.2.title': '数据准备',
    'order.next.consult.2.desc':
      '请准备好您的确切出生时间和地点——这是绘制星盘所必需的。',

    'order.btn.dashboard': '前往个人面板',
    'order.btn.catalog': '返回目录',

    // Admin Analytics Dashboard
    'admin.analytics.title': '平台分析',
    'admin.analytics.subtitle': '关键指标和数据概览',
    'admin.analytics.editing': '编辑模式',
    'admin.analytics.overview': '概览',
    'admin.analytics.saveDashboard': '保存布局',
    'admin.analytics.customize': '自定义仪表板',
    'admin.analytics.layoutSaved': '仪表板布局已保存',
    'admin.analytics.hiddenWidgets': '隐藏的小部件',
    'admin.analytics.emptyTitle': '仪表板为空',
    'admin.analytics.emptyDesc': '所有分析小部件已隐藏。',
    'admin.analytics.period.7d': '7天',
    'admin.analytics.period.30d': '30天',
    'admin.analytics.period.3m': '3个月',
    'admin.analytics.period.1y': '年',
    'admin.widget.tracking': '计数器和集成',
    'admin.widget.tracking.desc': '添加分析代码以跟踪流量',
    'admin.widget.tracking.connected': '已连接：{count}/{total}',
    'admin.widget.tracking.active': '{count} 活跃',
    'admin.widget.tracking.check': '检查连接',
    'admin.widget.kpi': '关键绩效指标 (KPI)',
    'admin.widget.kpi.revenue': '总收入',
    'admin.widget.kpi.users': '活跃用户',
    'admin.widget.kpi.orders': '总订单',
    'admin.widget.kpi.conversion': '订单转化率',
    'admin.widget.revenue': '月收入',
    'admin.widget.revenue.desc': '过去六个月的趋势',
    'admin.widget.revenue.detail': '详情',
    'admin.widget.revenue.period': '+67.7% 对于该期间',
    'admin.widget.funnel': '转化漏斗',
    'admin.widget.funnel.desc': '用户旅程',
    'admin.widget.traffic': '流量来源',
    'admin.widget.traffic.desc': '用户来自哪里',
    'admin.widget.traffic.tooltip':
      '数据自动从连接的计数器和订单中的UTM标签收集。',
    'admin.widget.traffic.settings': '跟踪设置',
    'admin.widget.geo': '地理位置',
    'admin.widget.geo.desc': '热门城市',
    'admin.widget.geo.tooltip': '基于访客IP地址和订单数据自动收集。',
    'admin.widget.services': '按服务订单',
    'admin.widget.services.all': '所有服务',
    'admin.widget.subs': '订阅',
    'admin.widget.subs.manage': '管理',
    'admin.widget.subs.pro': 'Pro套餐',
    'admin.widget.subs.plus': 'Plus套餐',
    'admin.widget.subs.free': '免费',
    'admin.widget.subs.ltv': '平均客单价 (LTV)',
    'admin.widget.astrologers': '顶级占星师',
    'admin.widget.astrologers.catalog': '占星师目录',
    'admin.widget.metrics.avgTime': '平均停留时间',
    'admin.widget.metrics.bounceRate': '跳出率',
    'admin.widget.metrics.repeatPurchase': '复购率',
    'admin.widget.metrics.nps': 'NPS（忠诚度）',
    'admin.widget.metrics.vsPrev': '较上期',
    'admin.widget.realtime': '当前在线',
    'admin.widget.realtime.users': '在线用户',
    'admin.widget.realtime.views': '{count} 浏览/分钟',
    'admin.widget.realtime.tooltip': '基于计数器的活跃会话实时更新。',
    'admin.widget.alerts': '通知',
    'admin.widget.alerts.desc': '重要事件和异常',
    'admin.widget.alerts.new': '{count} 条新消息',
    'admin.widget.devices': '设备',
    'admin.widget.devices.desc': '设备类型',
    'admin.widget.devices.tooltip':
      '来自Yandex.Metrica / Google Analytics的数据。需要连接计数器。',
    'admin.widget.devices.mobile': '移动端',
    'admin.widget.devices.desktop': '桌面端',
    'admin.widget.devices.tablet': '平板',
    'admin.widget.pages': '热门页面',
    'admin.widget.pages.desc': '按浏览量排名',
    'admin.widget.pages.seo': 'SEO报告',
    'admin.widget.content': '内容分析',
    'admin.widget.content.desc': '博客统计',
    'admin.widget.content.manage': '管理博客',
    'admin.widget.content.views': '浏览量',
    'admin.widget.content.avgRead': '平均阅读',
    'admin.widget.reviews': '评价和评分',
    'admin.widget.reviews.desc': '用户反馈',
    'admin.widget.reviews.moderation': '审核',
    'admin.widget.reviews.count': '{count} 条评价',
    'admin.widget.reviews.positive': '好评',
    'admin.widget.reviews.neutral': '中评',
    'admin.widget.reviews.negative': '差评',
    'admin.widget.email': '邮件营销',
    'admin.widget.email.desc': '活动统计',
    'admin.widget.email.manage': '邮件活动',
    'admin.widget.email.opens': '打开率',
    'admin.widget.email.clicks': '点击率',
    'admin.widget.email.sent': '已发送：{count}',
    'admin.widget.email.unsubs': '退订：{count}',
    'admin.widget.cac': '获客成本 (CAC)',
    'admin.widget.cac.avg': '平均CAC：{value} ₽',
    'admin.widget.cac.marketing': '营销',
    'admin.widget.cac.free': '免费',
    'admin.widget.cac.orders': '{count} 订单',

    // Astrologer Dashboard — Shell
    'astrodash.title': '占星师控制台',
    'astrodash.group.main': '主要',
    'astrodash.group.products': '产品',
    'astrodash.group.business': '业务',
    'astrodash.group.settings': '设置',
    'astrodash.tab.overview': '概览',
    'astrodash.tab.orders': '订单',
    'astrodash.tab.consultations': '咨询',
    'astrodash.tab.chat': '客户聊天',
    'astrodash.tab.services': '服务',
    'astrodash.tab.marketplace': '课程',
    'astrodash.tab.calendar': '日程',
    'astrodash.tab.finances': '财务',
    'astrodash.tab.promotion': '推广',
    'astrodash.tab.reviews': '评价',
    'astrodash.tab.stats': '统计',
    'astrodash.tab.profile': '公开资料',
    'astrodash.tab.settings': '设置与安全',
    'astrodash.tab.subscription': '订阅',
    'astrodash.logout': '退出',
    'astrodash.backToSite': '返回网站',
    'astrodash.menu': '菜单',
    'astrodash.welcome': '你好，{name}！',
    'astrodash.role': '占星师',

    // Astrologer Dashboard — Overview Tab
    'astrodash.overview.title': '概览',
    'astrodash.overview.subtitle': '今日关键指标',
    'astrodash.overview.todayIncome': '今日收入',
    'astrodash.overview.newOrders': '新订单',
    'astrodash.overview.activeConsultations': '活跃咨询',
    'astrodash.overview.profileViews': '资料浏览',
    'astrodash.overview.rating': '评分',
    'astrodash.overview.position': '目录排名',
    'astrodash.overview.quickActions': '快捷操作',
    'astrodash.overview.recentOrders': '最近订单',
    'astrodash.overview.viewAll': '查看全部',
    'astrodash.overview.noOrders': '暂无订单',
    'astrodash.overview.schedule': '今日日程',
    'astrodash.overview.noSchedule': '今日无预约',
    'astrodash.overview.tips': '改进建议',
    'astrodash.overview.fillProfile': '完善资料至100%',
    'astrodash.overview.addServices': '添加更多服务',
    'astrodash.overview.enablePromo': '开启推广',

    // Astrologer Dashboard — Promotion Tab
    'astrodash.promo.title': '资料推广',
    'astrodash.promo.subtitle': '增加订单和收入',
    'astrodash.promo.budget': '广告预算',
    'astrodash.promo.available': '可用',
    'astrodash.promo.spentMonth': '本月已花费',
    'astrodash.promo.remaining': '剩余',
    'astrodash.promo.topUp': '充值余额',
    'astrodash.promo.hint': '广告从广告预算中扣除。如果资金不足，请充值余额。',
    'astrodash.promo.tariff': '套餐',
    'astrodash.promo.position': '排名',
    'astrodash.promo.views': '浏览量',
    'astrodash.promo.conversion': '转化率',
    'astrodash.promo.adIncome': '广告收入',
    'astrodash.promo.adOrders': '广告订单',
    'astrodash.promo.howToIncrease': '如何增加收入',
    'astrodash.promo.proAdvice':
      '升级到PRO套餐，获得目录优先展示。统计显示，PRO占星师的订单量多40%。',
    'astrodash.promo.upgradePro': '升级到PRO',
    'astrodash.promo.enableAds': '开启广告',
    'astrodash.promo.launchAd': '投放广告',
    'astrodash.promo.boostProfile': '提升资料',
    'astrodash.promo.enableMailing': '开启邮件推送',
    'astrodash.promo.upgradeTariff': '升级套餐',
    'astrodash.promo.myAdOrders': '我的广告订单',
    'astrodash.promo.howAdsWork': '广告如何运作？',
    'astrodash.promo.hideHelp': '隐藏帮助',
    'astrodash.promo.newOrder': '新订单',
    'astrodash.promo.step1.title': '提交与支付',
    'astrodash.promo.step1.desc': '选择格式，上传素材，从广告余额支付。',
    'astrodash.promo.step2.title': '审核（1-12小时）',
    'astrodash.promo.step2.desc': '管理员将审核素材。结果将在系统通知中显示。',
    'astrodash.promo.step3.title': '上线与统计',
    'astrodash.promo.step3.desc':
      '审核通过后，广告将自动上线。在此跟踪浏览量和点击量。',
    'astrodash.promo.service': '服务',
    'astrodash.promo.type': '类型',
    'astrodash.promo.status': '状态',
    'astrodash.promo.period': '周期',
    'astrodash.promo.cost': '费用',
    'astrodash.promo.result': '效果',
    'astrodash.promo.clicks': '次点击',
    'astrodash.promo.orders': '个订单',
    'astrodash.promo.banner': '横幅',
    'astrodash.promo.mailing': '邮件推送',
    'astrodash.promo.pin': '置顶',
    'astrodash.promo.statusActive': '活跃',
    'astrodash.promo.statusCompleted': '已完成',
    'astrodash.promo.statusModeration': '审核中',
    'astrodash.promo.statusPayment': '待支付',
    'astrodash.promo.statusApproved': '已通过',
    'astrodash.promo.statusRejected': '已拒绝',
    'astrodash.promo.onModeration': '订单审核中',
    'astrodash.promo.awaitingPayment': '待支付',
    'astrodash.promo.rejected': '订单被拒绝',
    'astrodash.promo.checkingAd':
      '您的广告正在由管理员审核。通常需要1-12小时。',
    'astrodash.promo.moderationPassed': '审核通过！请支付订单以启动广告。',
    'astrodash.promo.moderationFailed': '审核未通过。请修改问题后重新提交。',
    'astrodash.promo.rejectionReason': '拒绝原因',
    'astrodash.promo.stepCreated': '已创建',
    'astrodash.promo.stepModeration': '审核',
    'astrodash.promo.stepPayment': '支付',
    'astrodash.promo.stepLaunch': '上线',
    'astrodash.promo.inProgress': '进行中...',
    'astrodash.promo.awaitingPaymentShort': '待支付',
    'astrodash.promo.awaiting': '等待中',
    'astrodash.promo.paid': '已支付',
    'astrodash.promo.payBtn': '支付 {price} ₽',
    'astrodash.promo.fixAndResend': '修改并重新提交',
    'astrodash.promo.repeat': '重复',
    'astrodash.promo.extend': '延期',
    'astrodash.promo.income': '收入',
    'astrodash.promo.createAd': '创建广告',
    'astrodash.promo.selectSlot': '选择广告位。每种格式显示在网站的不同位置。',
    'astrodash.promo.format': '格式',
    'astrodash.promo.materials': '素材',
    'astrodash.promo.payment': '支付',
    'astrodash.promo.next': '下一步',
    'astrodash.promo.back': '返回',
    'astrodash.promo.reach': '覆盖',
    'astrodash.promo.requirements': '要求',
    'astrodash.promo.adTitle': '广告标题',
    'astrodash.promo.adTitlePlaceholder': '例如：专业星盘解读',
    'astrodash.promo.adDesc': '描述',
    'astrodash.promo.adDescPlaceholder': '简要描述您的服务',
    'astrodash.promo.adLink': '链接',
    'astrodash.promo.adImage': '上传图片',
    'astrodash.promo.replaceImage': '替换图片',
    'astrodash.promo.orderSummary': '订单摘要',
    'astrodash.promo.total': '应付金额',
    'astrodash.promo.adBalance': '广告余额',
    'astrodash.promo.insufficientFunds': '余额不足。请充值 {amount} ₽',
    'astrodash.promo.remainingAfter': '支付后剩余',
    'astrodash.promo.payAndSubmit': '支付并提交',
    'astrodash.promo.orderPaid': '订单已支付并提交审核。您将收到状态通知。',
    'astrodash.promo.cancel': '取消',

    // Common astrologer dashboard
    'astrodash.common.save': '保存',
    'astrodash.common.cancel': '取消',
    'astrodash.common.delete': '删除',
    'astrodash.common.edit': '编辑',
    'astrodash.common.add': '添加',
    'astrodash.common.search': '搜索',
    'astrodash.common.filter': '筛选',
    'astrodash.common.reset': '重置',
    'astrodash.common.noData': '暂无数据',
    'astrodash.common.loading': '加载中...',
    'astrodash.common.confirm': '确认',
    'astrodash.common.close': '关闭',
    'astrodash.common.viewAll': '查看全部',
    'astrodash.common.today': '今天',
    'astrodash.common.week': '本周',
    'astrodash.common.month': '本月',
    'astrodash.common.year': '本年',
    'astrodash.common.from': '从',
    'astrodash.common.to': '到',
    'astrodash.common.total': '合计',
    'astrodash.common.status': '状态',
    'astrodash.common.actions': '操作',
    'astrodash.common.details': '详情',
    'astrodash.common.back': '返回',
    'astrodash.common.next': '下一步',
  },

  es: {
    'nav.services': 'Servicios',
    'nav.astrologers': 'Astrólogos',
    'nav.knowledge': 'Conocimiento',
    'nav.pricing': 'Precios',
    'nav.marketplace': 'Marketplace',
    'nav.freeChart': 'Carta gratis',
    'nav.freeChartFull': 'Cálculo gratuito de carta',
    'nav.login': 'Iniciar sesión',
    'nav.cart': 'Carrito',
    'services.natal': 'Carta natal',
    'services.natal.desc': 'Análisis profundo de personalidad',
    'services.solar': 'Revolución solar',
    'services.solar.desc': 'Tendencias de tu año',
    'services.compatibility': 'Compatibilidad',
    'services.compatibility.desc': 'Sinastría para parejas',
    'services.combo': 'Combo: Natal + Solar',
    'services.combo.desc': 'Análisis completo + pronóstico anual',
    'services.forecast': 'Pronóstico (Tránsitos)',
    'services.forecast.desc': 'Períodos y fechas clave',
    'services.consultation': 'Consulta',
    'services.consultation.desc': 'Charla en vivo con astrólogo',
    'services.marketplace': 'Marketplace',
    'services.marketplace.desc': 'Todos los servicios',
    'knowledge.interpretations': 'Interpretaciones',
    'knowledge.interpretations.desc': 'Base de conocimiento',
    'knowledge.calendar': 'Calendario astral',
    'knowledge.calendar.desc': 'Eventos celestes clave',
    'knowledge.blog': 'Blog',
    'knowledge.blog.desc': 'Artículos y pronósticos',
    'profile.dashboard': 'Mi panel',
    'profile.astrologer': 'Panel astrólogo',
    'profile.logout': 'Cerrar sesión',
    'profile.admin': 'Panel admin',
    'mobile.services': 'Servicios',
    'mobile.services.desc': 'Informes, pronósticos, consultas',
    'mobile.knowledge.desc': 'Calendarios, interpretaciones, blog',
    'mobile.platform': 'Plataforma',
    'mobile.account': 'Cuenta',
    'mobile.language': 'Idioma',
    'mobile.client': 'Cliente',
    'mobile.astrologer': 'Astrólogo',
    'footer.services': 'Servicios',
    'footer.platform': 'Plataforma',
    'footer.partners': 'Socios',
    'footer.natal': 'Carta natal',
    'footer.solar': 'Revolución solar',
    'footer.compatibility': 'Compatibilidad',
    'footer.pricingPackages': 'Precios y paquetes',
    'footer.astrologersCatalog': 'Catálogo de astrólogos',
    'footer.interpretations': 'Interpretaciones',
    'footer.calendar': 'Calendario astral',
    'footer.blog': 'Blog',
    'footer.forAstrologers': 'Para astrólogos',
    'footer.advertising': 'Publicidad',
    'footer.astrologerDashboard': 'Panel astrólogo',
    'footer.combo': 'Combo análisis',
    'footer.forecast': 'Pronóstico de tránsitos',
    'footer.consultation': 'Consulta',
    'footer.marketplace': 'Marketplace',
    'footer.free': 'Gratis',
    'footer.astroCalendar': 'Calendario astral',
    'footer.freeChart': 'Carta gratuita',
    'footer.natalDemo': 'Carta natal online',
    'footer.reportPreview': 'Ejemplos de informes',
    'footer.premiumReport': 'Informe Premium',
    'footer.knowledge': 'Conocimiento',
    'footer.faq': 'Preguntas frecuentes',
    'footer.company': 'Empresa',
    'footer.about': 'Sobre nosotros',
    'footer.contact': 'Contacto',
    'footer.newsletter.title': 'Suscríbete al boletín astral',
    'footer.newsletter.desc':
      'Pronósticos semanales, artículos útiles y descuentos exclusivos.',
    'footer.newsletter.placeholder': 'Tu email',
    'footer.newsletter.button': 'Suscribirse',
    'footer.newsletter.success': '¡Suscrito! Espera nuestros correos.',
    'footer.securePayment': 'Pago seguro',
    'footer.copyright': '© {year} Astriqo. Todos los derechos reservados.',
    'footer.privacy': 'Política de privacidad',
    'footer.terms': 'Términos de uso',
    'footer.offer': 'Oferta Pública',
    'footer.description':
      'Plataforma de autoconocimiento profundo a través de la astrología profesional. Informes automáticos y consultas con expertos verificados.',
    'knowledge.faq': 'Preguntas frecuentes',
    'knowledge.faq.desc': 'Ayuda de la plataforma',
    'ui.learnMore': 'Más info',
    'ui.select': 'Seleccionar',
    'ui.buy': 'Comprar',
    'ui.profile': 'Perfil',
    'ui.order': 'Pedir',
    'ui.search': 'Buscar',
    'ui.filter': 'Filtros',
    'ui.reset': 'Restablecer',
    'ui.back': 'Atrás',
    'ui.next': 'Siguiente',
    'ui.close': 'Cerrar',
    'ui.loading': 'Cargando...',

    // Legal Pages
    'legal.lastUpdated': 'Última actualización: 15 de mayo de 2026',
    'legal.backToHome': 'Inicio',
    'legal.contactUs': '¿Tienes preguntas? Contáctanos',

    // Privacy Policy
    'legal.privacy.title': 'Política de Privacidad',
    'legal.privacy.s1.title': '1. Disposiciones Generales',
    'legal.privacy.s1.p1':
      'Esta Política de Privacidad (en adelante, la "Política") determina el procedimiento para procesar y proteger los datos personales de los usuarios del servicio Astriqo (en adelante, el "Servicio"). Respetamos la privacidad de sus datos y hacemos todo lo posible para protegerlos.',
    'legal.privacy.s2.title': '2. Qué datos recopilamos',
    'legal.privacy.s2.p1':
      'Para proporcionar servicios astrológicos, recopilamos los siguientes datos:',
    'legal.privacy.s2.l1':
      'Datos de registro: nombre, dirección de correo electrónico, número de teléfono.',
    'legal.privacy.s2.l2':
      'Datos astrológicos: fecha, hora exacta y lugar de nacimiento (necesarios para calcular la carta natal).',
    'legal.privacy.s2.l3':
      'Datos técnicos: dirección IP, tipo de navegador, datos de cookies.',
    'legal.privacy.s3.title': '3. Uso de datos',
    'legal.privacy.s3.p1':
      'Utilizamos sus datos exclusivamente para los siguientes propósitos:',
    'legal.privacy.s3.l1': 'Generación de informes astrológicos personales.',
    'legal.privacy.s3.l2':
      'Organización de consultas con astrólogos de la plataforma.',
    'legal.privacy.s3.l3': 'Mejora de la calidad del Servicio.',
    'legal.privacy.s3.l4':
      'Envío de notificaciones importantes (por ejemplo, sobre la disponibilidad del informe).',
    'legal.privacy.s4.title': '4. Transferencia de datos a terceros',
    'legal.privacy.s4.p1':
      'No vendemos ni transferimos sus datos personales a terceros con fines de marketing. Sus datos astrológicos (fecha, hora, lugar de nacimiento) pueden ser transferidos a su astrólogo elegido únicamente con el propósito de realizar una consulta pagada.',
    'legal.privacy.s5.title': '5. Seguridad de los datos',
    'legal.privacy.s5.p1':
      'Utilizamos métodos modernos de encriptación y protección de datos para evitar el acceso no autorizado. Todos los pagos se procesan a través de pasarelas seguras de sistemas de pago certificados.',
    'legal.privacy.s6.title': '6. Sus derechos',
    'legal.privacy.s6.p1': 'Tiene derecho en cualquier momento a:',
    'legal.privacy.s6.l1':
      'Solicitar una copia de todos sus datos almacenados en el Servicio.',
    'legal.privacy.s6.l2':
      'Editar o eliminar sus datos a través de la configuración de su Cuenta Personal.',
    'legal.privacy.s6.l3':
      'Retirar el consentimiento para el procesamiento de datos personales.',
    'legal.privacy.s7.title': '7. Contactos',
    'legal.privacy.s7.p1':
      'Si tiene alguna pregunta sobre esta Política de Privacidad, contáctenos en: privacy@astriqo.com.',

    // Terms of Service
    'legal.terms.title': 'Términos de Servicio',
    'legal.terms.s1.title': '1. Objeto del acuerdo',
    'legal.terms.s1.p1':
      'Este Acuerdo de Usuario (en adelante, el "Acuerdo") regula la relación entre el servicio Astriqo (en adelante, el "Servicio") y el usuario de Internet (en adelante, el "Usuario") que surge del uso del Servicio.',
    'legal.terms.s2.title': '2. Naturaleza de los servicios prestados',
    'legal.terms.s2.p1':
      'El Servicio proporciona servicios informativos y de entretenimiento en el campo de la astrología. Todos los informes, pronósticos y consultas generados son de naturaleza consultiva. El Servicio no se hace responsable de las decisiones tomadas por el Usuario en base a la información recibida.',
    'legal.terms.s2.p2':
      'Importante: Las consultas e informes astrológicos no reemplazan la asistencia médica, psicológica, legal o financiera profesional.',
    'legal.terms.s3.title': '3. Registro y cuenta',
    'legal.terms.s3.p1':
      'Para acceder a la funcionalidad completa del Servicio, el Usuario debe completar el procedimiento de registro. El Usuario se compromete a proporcionar información confiable sobre sí mismo y es responsable de la seguridad de sus datos de inicio de sesión (nombre de usuario y contraseña).',
    'legal.terms.s4.title': '4. Pagos y reembolsos',
    'legal.terms.s4.p1':
      'El pago de los servicios (Informes Premium, consultas) se realiza sobre una base de prepago del 100%. Dado que los informes automáticos son contenido digital, cuyo acceso se proporciona instantáneamente, los reembolsos por ellos no se proporcionan por defecto (de acuerdo con la legislación de protección al consumidor).',
    'legal.terms.s4.p2':
      'Los reembolsos por una consulta con un astrólogo son posibles previa cancelación a más tardar 24 horas antes de la hora programada.',
    'legal.terms.s5.title': '5. Reglas de conducta en la plataforma',
    'legal.terms.s5.p1': 'Se prohíbe al Usuario:',
    'legal.terms.s5.l1':
      'Usar lenguaje obsceno e insultos al comunicarse con astrólogos o soporte.',
    'legal.terms.s5.l2':
      'Copiar, distribuir o revender materiales del Servicio (informes, artículos) sin permiso por escrito.',
    'legal.terms.s5.l3':
      'Usar scripts automatizados (bots) para recopilar información del Servicio.',
    'legal.terms.s6.title': '6. Limitación de responsabilidad',
    'legal.terms.s6.p1':
      'El Servicio se proporciona "tal cual". No garantizamos que el Servicio cumpla con sus requisitos, se proporcione de forma continua, rápida, confiable y sin errores.',
    'legal.terms.s7.title': '7. Cambios en los términos',
    'legal.terms.s7.p1':
      'La Administración del Servicio se reserva el derecho de realizar cambios en este Acuerdo en cualquier momento. El uso continuo del Servicio después de que se realicen los cambios significa que el Usuario acepta la nueva versión del Acuerdo.',

    // Public Offer
    'legal.offer.title': 'Oferta Pública',
    'legal.offer.s1.title': '1. Disposiciones Generales',
    'legal.offer.s1.p1':
      'Este documento es una oferta pública del servicio Astriqo (en adelante, el "Contratista") y contiene todas las condiciones esenciales para la prestación de servicios de información y consultoría.',
    'legal.offer.s2.title': '2. Objeto de la oferta',
    'legal.offer.s2.p1':
      'El Contratista se compromete a proporcionar al Cliente servicios para proporcionar acceso a informes y consultas astrológicas en la plataforma Astriqo, y el Cliente se compromete a pagar por estos servicios.',
    'legal.offer.s3.title': '3. Procedimiento para la prestación de servicios',
    'legal.offer.s3.p1':
      'Los servicios se proporcionan electrónicamente a través de la cuenta personal del Cliente en el sitio. El acceso a los bienes digitales (informes) se proporciona automáticamente después del pago.',
    'legal.offer.s4.title': '4. Costo y procedimiento de pago',
    'legal.offer.s4.p1':
      'El costo de los servicios se indica en el sitio. El pago se realiza mediante transferencia bancaria a través de sistemas de pago integrados.',
    'legal.offer.s5.title': '5. Derechos y obligaciones de las partes',
    'legal.offer.s5.p1':
      'El Contratista se compromete a proporcionar servicios de manera eficiente y a tiempo. El Cliente se compromete a proporcionar datos confiables para los cálculos (fecha, hora, lugar de nacimiento).',
    'legal.offer.s6.title': '6. Responsabilidad de las partes',
    'legal.offer.s6.p1':
      'Las partes son responsables del incumplimiento de las obligaciones de acuerdo con la legislación vigente.',
    'legal.offer.s7.title': '7. Fuerza mayor',
    'legal.offer.s7.p1':
      'Las partes quedan liberadas de responsabilidad por el incumplimiento parcial o total de las obligaciones en virtud de este Acuerdo si este incumplimiento fue el resultado de circunstancias de fuerza mayor.',
    'legal.offer.s8.title': '8. Resolución de disputas',
    'legal.offer.s8.p1':
      'Todas las disputas y desacuerdos se resuelven mediante negociaciones. Si no se llega a un acuerdo, la disputa se somete al tribunal.',

    // Encyclopedia page
    'enc.badge': 'Base de conocimiento',
    'enc.title': 'Enciclopedia Astrológica',
    'enc.subtitle':
      'Todo sobre astrología: desde lo básico hasta técnicas avanzadas. Artículos educativos, interpretaciones de carta natal y nuestros servicios — {count} artículos.',
    'enc.searchPlaceholder': 'Ej.: Venus en Tauro, Ascendente en Leo...',
    'enc.popular': 'Popular:',
    'enc.results': 'Resultados ({count})',
    'enc.resetFilters': 'Restablecer filtros',
    'enc.notFound': 'No se encontraron resultados',
    'enc.notFoundHint': 'Intenta cambiar tu búsqueda o seleccionar otra letra.',
    'enc.showCategories': 'Mostrar todas las categorías',
    'enc.readMore': 'Leer más',
    'enc.learnMore': 'Más info',
    'enc.articles': 'artículos',
    'enc.backToList': 'Volver a la lista',
    'enc.alphabetTitle': 'Índice Alfabético',
    'enc.keyFacts': 'Datos Clave',
    'enc.commonMyths': 'Mitos Comunes',
    'enc.astrologerTip': 'Consejo del Astrólogo',
    'enc.strengths': 'Fortalezas',
    'enc.challenges': 'Desafíos',
    'enc.relatedArticles': 'Artículos Relacionados',
    'enc.defaultTip':
      'La energía de esta posición requiere un enfoque consciente. Apóyate en tus fortalezas y observa los patrones negativos. El crecimiento comienza con la autoconciencia.',
    'enc.ctaEncTitle': 'Aplica estos conocimientos en la práctica',
    'enc.ctaIntTitle': 'Descubre cómo funciona esto en tu carta',
    'enc.ctaEncDesc':
      'La astrología se aprende mejor con tu propio ejemplo. Pide un informe personal y descubre todo sobre ti.',
    'enc.ctaIntDesc':
      'Una posición es solo parte del cuadro. Pide un informe completo para ver cómo interactúan los planetas en tu carta única.',
    'enc.defaultCta': 'Calcular tu carta natal',
    'enc.basicsTitle': 'Fundamentos de Astrología',
    'enc.basicsCard': 'Conceptos Básicos',
    'enc.basicsDesc':
      'Qué es la astrología, carta natal, signos del zodíaco, planetas y casas. Perfecto para principiantes.',
    'enc.advancedCard': 'Temas Avanzados',
    'enc.advancedDesc':
      'Tránsitos, revolución solar, sinastría, nodos lunares y planetas retrógrados. Para quienes quieren saber más.',
    'enc.interpretationsTitle': 'Referencia de Interpretaciones',
    'enc.planetsSignsCard': 'Planetas en Signos',
    'enc.planetsSignsDesc':
      'Cómo se manifiestan las energías de 10 planetas (Sol a Plutón) en 12 signos. Los bloques de la personalidad.',
    'enc.ascendantCard': 'Ascendente en Signos',
    'enc.ascendantDesc':
      'Tu máscara social, primera impresión y filtro de percepción. Cómo te ven los demás.',
    'enc.housesCard': 'Planetas en Casas',
    'enc.housesDesc':
      'En qué áreas de la vida los planetas realizan su potencial. Sol, Luna, Mercurio, Venus y Marte en 12 casas.',
    'enc.aspectsCard': 'Aspectos',
    'enc.aspectsDesc':
      'Interacciones planetarias: conjunciones, trígonos, cuadraturas, oposiciones y sextiles.',
    'enc.servicesTitle': 'Nuestros Servicios',
    'enc.popularSearch1': 'Qué es una carta natal',
    'enc.popularSearch2': 'Sol en Leo',
    'enc.popularSearch3': 'Aspectos en astrología',
    // Free Chart Page
    'chart.title': 'Cálculo de carta astrológica',
    'chart.subtitle':
      'Elige un servicio, ingresa tus datos de nacimiento y obtén un cálculo preciso de posiciones planetarias o un informe PDF detallado.',
    'chart.breadcrumb': 'Cálculo de carta',
    'chart.rating': '4.9 calificación',
    'chart.calculations': '50,000+ cálculos',
    'chart.freeBeta': 'Cálculo Beta gratuito',
    'chart.step.service': 'Servicio',
    'chart.step.package': 'Paquete',
    'chart.step.data': 'Datos',
    'chart.step.result': 'Resultado',
    'chart.step1.title': 'Elige un servicio',
    'chart.step1.subtitle': '¿Qué cálculo deseas realizar?',
    'chart.popular': 'Popular',
    'chart.new': 'Nuevo',
    'chart.fromPrice': 'desde 2,990 ₽',
    'chart.freePremium': 'Gratis + Premium',
    'chart.step2.titleConsultation': 'Elige un formato',
    'chart.step2.titleReport': 'Elige un plan',
    'chart.step2.forService': 'Para el servicio «{name}»',
    'chart.tier.free.title': 'Beta (básico)',
    'chart.tier.free.badge': 'Gratis',
    'chart.tier.free.desc':
      'Cálculo breve de indicadores clave sin interpretación textual',
    'chart.tier.pro.title': 'Premium (único)',
    'chart.tier.pro.badge': 'Recomendado',
    'chart.tier.pro.desc':
      'Informe PDF completo de 20-40 páginas con análisis detallado y recomendaciones',
    'chart.tier.package.title': 'Premium (paquete)',
    'chart.tier.package.badge': 'Mejor valor',
    'chart.tier.package.desc':
      'Paquete de informes con descuento. Válido para cualquier servicio de la plataforma',
    'chart.tier.package.fromPrice': 'desde 1,590 ₽/ud',
    'chart.bundle.hit': 'Éxito',
    'chart.bundle.reports': '{count} informes',
    'chart.consult.short.title': 'Consulta corta',
    'chart.consult.short.desc': '30 min • 1 pregunta específica',
    'chart.consult.short.badge': 'Exprés',
    'chart.consult.standard.title': 'Consulta estándar',
    'chart.consult.standard.desc': '60 min • Lectura completa de carta',
    'chart.consult.standard.badge': 'Recomendado',
    'chart.consult.extended.title': 'Consulta extendida',
    'chart.consult.extended.desc':
      '90 min • Grabación + recomendaciones detalladas',
    'chart.consult.extended.badge': 'Máximo',
    'chart.step3.title': 'Ingresa tus datos',
    'chart.step3.subtitle': 'Para calcular el servicio «{name}»',
    'chart.field.name': 'Nombre (opcional)',
    'chart.field.namePlaceholder': 'Ej., Ana',
    'chart.field.date': 'Fecha de nacimiento *',
    'chart.field.time': 'Hora de nacimiento *',
    'chart.field.timeHint': 'Si no lo sabes, ingresa 12:00',
    'chart.field.city': 'Ciudad de nacimiento *',
    'chart.field.cityPlaceholder': 'Ej., Madrid',
    'chart.field.person1': 'Primera persona',
    'chart.field.person2': 'Segunda persona',
    'chart.field.forecastYear': 'Año de pronóstico *',
    'chart.field.forecastPeriod': 'Período de pronóstico *',
    'chart.period.3months': '3 meses',
    'chart.period.6months': '6 meses',
    'chart.period.1year': '1 año',
    'chart.btn.calculate': 'Calcular',
    'chart.btn.book': 'Reservar',
    'chart.btn.back': 'Atrás',
    'chart.btn.next': 'Siguiente',
    'chart.calculating.title': 'Analizando datos...',
    'chart.calculating.subtitle': 'Calculando posiciones exactas de planetas',
    'chart.result.fullReport': 'Informe completo',
    'chart.result.basicReport': 'Informe básico',
    'chart.result.newCalc': 'Nuevo cálculo',
    'chart.result.planets': 'Posiciones de planetas',
    'chart.result.planet': 'Planeta',
    'chart.result.sign': 'Signo',
    'chart.result.degree': 'Grado',
    'chart.result.briefInterpretation': 'Interpretación breve',
    'chart.result.wantFull': '¿Quieres un análisis completo?',
    'chart.result.fullDesc':
      'Obtén un informe PDF detallado de 30-40 páginas con interpretación profunda de todos los indicadores, tareas kármicas y recomendaciones personales.',
    'chart.result.getPremium': 'Obtener informe Premium',
    'chart.result.questions': '¿Tienes preguntas?',
    'chart.result.consultDesc': 'Reserva una consulta en vivo con un astrólogo',
    'chart.result.chooseAstrologer': 'Elegir astrólogo',
    'chart.trust.accurate.title': 'Cálculos precisos',
    'chart.trust.accurate.desc':
      'Usamos algoritmos profesionales Swiss Ephemeris para máxima precisión.',
    'chart.trust.privacy.title': 'Privacidad',
    'chart.trust.privacy.desc':
      'Tus datos de nacimiento están protegidos y se usan solo para el cálculo de la carta.',
    'chart.trust.instant.title': 'Resultado instantáneo',
    'chart.trust.instant.desc':
      'Obtén el cálculo básico y posiciones de planetas en segundos.',
    'chart.planet.sun': 'Sol',
    'chart.planet.moon': 'Luna',
    'chart.planet.mercury': 'Mercurio',
    'chart.planet.venus': 'Venus',
    'chart.planet.mars': 'Marte',
    'chart.planet.jupiter': 'Júpiter',
    'chart.planet.saturn': 'Saturno',
    'chart.sign.aries': 'Aries',
    'chart.sign.taurus': 'Tauro',
    'chart.sign.gemini': 'Géminis',
    'chart.sign.pisces': 'Piscis',
    'chart.sign.sagittarius': 'Sagitario',
    'chart.sign.capricorn': 'Capricornio',

    // Checkout page
    'checkout.title': 'Finalizar pedido',
    'checkout.breadcrumb.home': 'Inicio',
    'checkout.breadcrumb.marketplace': 'Marketplace',
    'checkout.breadcrumb.title': 'Finalizar pedido',
    'checkout.step.product': 'Producto',
    'checkout.step.checkout': 'Pedido',
    'checkout.step.payment': 'Pago',
    'checkout.addons.title': 'Servicios adicionales',
    'checkout.addons.subtitle': 'Mejora tu pedido con opciones extra',
    'checkout.addons.reports': 'Informes',
    'checkout.addons.consultations': 'Consultas',
    'checkout.addons.packages': 'Paquetes',
    'checkout.addons.natal': 'Carta natal Premium',
    'checkout.addons.compatibility': 'Compatibilidad Premium',
    'checkout.addons.solar': 'Revolución solar Premium',
    'checkout.addons.forecast': 'Pronóstico (Tránsitos) Premium',
    'checkout.addons.combo': 'Combo: Natal + Solar',
    'checkout.addons.consult30': 'Consulta 30 min',
    'checkout.addons.consult60': 'Consulta 60 min',
    'checkout.addons.consult90': 'Consulta 90 min',
    'checkout.addons.pkg20': 'Paquete de 20 informes',
    'checkout.addons.pkg50': 'Paquete de 50 informes',
    'checkout.addons.pkg100': 'Paquete de 100 informes',
    'checkout.auth.title': 'Inicia sesión para finalizar el pedido',
    'checkout.auth.desc':
      'Se requiere registro para recibir informes y guardar tus datos. Tus datos de nacimiento e informes estarán disponibles en tu panel.',
    'checkout.auth.login': 'Iniciar sesión',
    'checkout.auth.register': 'Registrarse',
    'checkout.auth.hint': 'Registro rápido por email o redes sociales',
    'checkout.payment.title': 'Método de pago',
    'checkout.payment.card': 'Tarjeta bancaria',
    'checkout.payment.sbp': 'SBP',
    'checkout.card.number': 'Número de tarjeta',
    'checkout.card.expiry': 'Fecha de vencimiento',
    'checkout.card.cvc': 'CVC',
    'checkout.card.name': 'Nombre en la tarjeta',
    'checkout.sbp.hint': 'Escanea el código QR en la app de tu banco',
    'checkout.sbp.qr': 'Código QR para pago',
    'checkout.contact.title': 'Datos de contacto',
    'checkout.contact.name': 'Nombre',
    'checkout.contact.namePlaceholder': 'Tu nombre',
    'checkout.contact.email': 'Email',
    'checkout.contact.phone': 'Teléfono (opcional)',
    'checkout.contact.phonePlaceholder': '+34 (999) 123-456',
    'checkout.contact.privacy':
      'Tus datos están protegidos y no se comparten con terceros',
    'checkout.order.title': 'Tu pedido',
    'checkout.order.addMore': 'Agregar otro servicio',
    'checkout.order.addonsLabel': 'Extras',
    'checkout.order.empty': 'El carrito está vacío',
    'checkout.order.autoGeneration': 'Generación automática',
    'checkout.promo.placeholder': 'Código promo (ej. ASTRO10)',
    'checkout.promo.apply': 'Aplicar',
    'checkout.promo.applied': '✓',
    'checkout.promo.success': '¡Código ASTRO10 aplicado! Descuento del 10%',
    'checkout.total.service': 'Servicio principal',
    'checkout.total.services': 'Servicios ({count})',
    'checkout.total.addons': 'Extras ({count})',
    'checkout.total.discount': 'Descuento',
    'checkout.total.total': 'Total',
    'checkout.payment.hint':
      'Todos los artículos del carrito se pagan en un solo pago. Tendrás acceso a todos los servicios inmediatamente después del pago.',
    'checkout.payment.hintBold': 'un solo pago',
    'checkout.agree.text': 'Acepto los',
    'checkout.agree.terms': 'términos del servicio',
    'checkout.agree.and': 'y la',
    'checkout.agree.privacy': 'política de privacidad',
    'checkout.pay.processing': 'Procesando...',
    'checkout.pay.loginRequired': 'Inicia sesión para pagar',
    'checkout.pay.button': 'Pagar {price}',
    'checkout.trust.ssl': 'Pago seguro. Cifrado SSL de 256 bits.',
    'checkout.trust.refund': 'Garantía de devolución de 14 días',
    'checkout.trust.noSave':
      'Los datos de la tarjeta no se guardan en el servidor',
    'checkout.mobile.total': 'Total',
    'checkout.mobile.pay': 'Pagar',
    'checkout.login.required': 'Por favor inicia sesión o regístrate',
    'checkout.agree.required': 'Acepta los términos para continuar',
    'checkout.success': '¡Pago exitoso! Pedido realizado.',
    'checkout.successMobile': '¡Pago exitoso!',
    'checkout.back': 'Atrás',
    'checkout.delete': 'Eliminar',

    // Auth page
    'auth.login.title': 'Bienvenido de nuevo',
    'auth.login.desc': 'Inicia sesión para continuar',
    'auth.register.title': 'Crear una cuenta',
    'auth.register.desc': 'Únete a la plataforma Astriqo',
    'auth.tab.login': 'Iniciar sesión',
    'auth.tab.register': 'Registrarse',
    'auth.name': 'Nombre',
    'auth.name.placeholder': 'Tu nombre',
    'auth.email': 'Email',
    'auth.email.placeholder': 'mail@example.com',
    'auth.password': 'Contraseña',
    'auth.password.placeholder': '••••••••',
    'auth.forgot': '¿Olvidaste tu contraseña?',
    'auth.agree.text': 'Acepto los',
    'auth.agree.terms': 'términos de uso',
    'auth.agree.and': 'y la',
    'auth.agree.privacy': 'política de privacidad',
    'auth.btn.login': 'Iniciar sesión',
    'auth.btn.register': 'Registrarse',
    'auth.social.title': 'O continuar con',
    'auth.success.login': '¡Inicio de sesión exitoso!',
    'auth.success.register': '¡Registro exitoso!',

    // Order Confirmation
    'order.success.title': '¡Pago exitoso!',
    'order.success.desc':
      'Gracias por tu pedido. Hemos enviado el recibo y los detalles del pedido a tu correo electrónico.',
    'order.details.number': 'Número de pedido',
    'order.details.date': 'Fecha del pedido',
    'order.details.title': 'Detalles del pedido',
    'order.details.astrologer': 'Astrólogo: {name}',
    'order.details.total': 'Total pagado',
    'order.next.title': '¿Qué sigue?',

    'order.next.pkg.1.title': 'Paquete activado',
    'order.next.pkg.1.desc':
      'Los informes se han añadido a tu panel. Puedes usarlos en cualquier momento.',
    'order.next.pkg.2.title': 'Crea tu primer informe',
    'order.next.pkg.2.desc':
      'Ve a la sección de servicios, elige el análisis deseado y aplica un informe del paquete durante el pago.',

    'order.next.report.1.title': 'El informe se está generando',
    'order.next.report.1.desc':
      'Nuestro sistema está analizando tus datos. Esto tomará unos minutos.',
    'order.next.report.2.title': 'Revisa tu correo electrónico',
    'order.next.report.2.desc':
      'Enviaremos el archivo PDF a tu correo electrónico y también lo guardaremos en tu panel.',

    'order.next.consult.1.title': 'Contacto con el astrólogo',
    'order.next.consult.1.desc':
      'En un plazo de 24 horas, {name} te escribirá en el chat de la plataforma para acordar la hora de la llamada.',
    'order.next.consult.2.title': 'Preparación de datos',
    'order.next.consult.2.desc':
      'Por favor, prepara la hora y lugar exactos de tu nacimiento — esto es necesario para construir la carta.',

    'order.btn.dashboard': 'Ir al panel',
    'order.btn.catalog': 'Volver al catálogo',

    // Admin Analytics Dashboard
    'admin.analytics.title': 'Analítica de la plataforma',
    'admin.analytics.subtitle': 'Resumen de indicadores y métricas clave',
    'admin.analytics.editing': 'Modo de edición',
    'admin.analytics.overview': 'Resumen',
    'admin.analytics.saveDashboard': 'Guardar diseño',
    'admin.analytics.customize': 'Personalizar panel',
    'admin.analytics.layoutSaved': 'Diseño del panel guardado',
    'admin.analytics.hiddenWidgets': 'Widgets ocultos',
    'admin.analytics.emptyTitle': 'Panel vacío',
    'admin.analytics.emptyDesc':
      'Todos los widgets de analítica están ocultos.',
    'admin.analytics.period.7d': '7 días',
    'admin.analytics.period.30d': '30 días',
    'admin.analytics.period.3m': '3 meses',
    'admin.analytics.period.1y': 'Año',
    'admin.widget.tracking': 'Contadores e integraciones',
    'admin.widget.tracking.desc':
      'Añade códigos de analítica para rastrear el tráfico',
    'admin.widget.tracking.connected': 'Conectados: {count} de {total}',
    'admin.widget.tracking.active': '{count} activos',
    'admin.widget.tracking.check': 'Verificar conexión',
    'admin.widget.kpi': 'Indicadores clave (KPI)',
    'admin.widget.kpi.revenue': 'Ingresos totales',
    'admin.widget.kpi.users': 'Usuarios activos',
    'admin.widget.kpi.orders': 'Total de pedidos',
    'admin.widget.kpi.conversion': 'Conversión a pedido',
    'admin.widget.revenue': 'Ingresos por mes',
    'admin.widget.revenue.desc': 'Tendencia de los últimos seis meses',
    'admin.widget.revenue.detail': 'Detalles',
    'admin.widget.revenue.period': '+67.7% para el periodo',
    'admin.widget.funnel': 'Embudo de conversión',
    'admin.widget.funnel.desc': 'Recorrido del usuario',
    'admin.widget.traffic': 'Fuentes de tráfico',
    'admin.widget.traffic.desc': 'De dónde vienen los usuarios',
    'admin.widget.traffic.tooltip':
      'Los datos se recopilan automáticamente de los contadores conectados y las etiquetas UTM de los pedidos.',
    'admin.widget.traffic.settings': 'Configuración de seguimiento',
    'admin.widget.geo': 'Geografía',
    'admin.widget.geo.desc': 'Principales ciudades',
    'admin.widget.geo.tooltip':
      'Datos recopilados automáticamente basados en las direcciones IP de los visitantes y datos de pedidos.',
    'admin.widget.services': 'Pedidos por servicio',
    'admin.widget.services.all': 'Todos los servicios',
    'admin.widget.subs': 'Suscripciones',
    'admin.widget.subs.manage': 'Gestionar',
    'admin.widget.subs.pro': 'Plan Pro',
    'admin.widget.subs.plus': 'Plan Plus',
    'admin.widget.subs.free': 'Gratuito',
    'admin.widget.subs.ltv': 'Ticket medio (LTV)',
    'admin.widget.astrologers': 'Top astrólogos',
    'admin.widget.astrologers.catalog': 'Catálogo de astrólogos',
    'admin.widget.metrics.avgTime': 'Tiempo medio en el sitio',
    'admin.widget.metrics.bounceRate': 'Tasa de rebote',
    'admin.widget.metrics.repeatPurchase': 'Compras repetidas',
    'admin.widget.metrics.nps': 'NPS (Lealtad)',
    'admin.widget.metrics.vsPrev': 'vs período anterior',
    'admin.widget.realtime': 'En línea ahora',
    'admin.widget.realtime.users': 'Usuarios en línea',
    'admin.widget.realtime.views': '{count} vistas/min',
    'admin.widget.realtime.tooltip':
      'Se actualiza en tiempo real basado en sesiones activas de los contadores.',
    'admin.widget.alerts': 'Notificaciones',
    'admin.widget.alerts.desc': 'Eventos importantes y anomalías',
    'admin.widget.alerts.new': '{count} nuevas',
    'admin.widget.devices': 'Dispositivos',
    'admin.widget.devices.desc': 'Tipos de dispositivos',
    'admin.widget.devices.tooltip':
      'Datos de Yandex.Metrica / Google Analytics. Requiere conexión de contador.',
    'admin.widget.devices.mobile': 'Móvil',
    'admin.widget.devices.desktop': 'Escritorio',
    'admin.widget.devices.tablet': 'Tabletas',
    'admin.widget.pages': 'Páginas populares',
    'admin.widget.pages.desc': 'Top por visitas del período',
    'admin.widget.pages.seo': 'Informe SEO',
    'admin.widget.content': 'Analítica de contenido',
    'admin.widget.content.desc': 'Estadísticas del blog',
    'admin.widget.content.manage': 'Gestionar blog',
    'admin.widget.content.views': 'Vistas',
    'admin.widget.content.avgRead': 'Lectura media',
    'admin.widget.reviews': 'Reseñas y valoración',
    'admin.widget.reviews.desc': 'Opiniones de usuarios',
    'admin.widget.reviews.moderation': 'Moderación',
    'admin.widget.reviews.count': '{count} reseñas',
    'admin.widget.reviews.positive': 'Positivas',
    'admin.widget.reviews.neutral': 'Neutrales',
    'admin.widget.reviews.negative': 'Negativas',
    'admin.widget.email': 'Campañas de email',
    'admin.widget.email.desc': 'Estadísticas de campañas',
    'admin.widget.email.manage': 'Campañas',
    'admin.widget.email.opens': 'Aperturas',
    'admin.widget.email.clicks': 'Clics',
    'admin.widget.email.sent': 'Enviados: {count}',
    'admin.widget.email.unsubs': 'Bajas: {count}',
    'admin.widget.cac': 'Coste de adquisición (CAC)',
    'admin.widget.cac.avg': 'CAC medio: {value} ₽',
    'admin.widget.cac.marketing': 'Marketing',
    'admin.widget.cac.free': 'Gratis',
    'admin.widget.cac.orders': '{count} pedidos',

    // Astrologer Dashboard — Shell
    'astrodash.title': 'Panel del astrólogo',
    'astrodash.group.main': 'Principal',
    'astrodash.group.products': 'Productos',
    'astrodash.group.business': 'Negocio',
    'astrodash.group.settings': 'Configuración',
    'astrodash.tab.overview': 'Resumen',
    'astrodash.tab.orders': 'Pedidos',
    'astrodash.tab.consultations': 'Consultas',
    'astrodash.tab.chat': 'Chat con clientes',
    'astrodash.tab.services': 'Servicios',
    'astrodash.tab.marketplace': 'Cursos',
    'astrodash.tab.calendar': 'Horario',
    'astrodash.tab.finances': 'Finanzas',
    'astrodash.tab.promotion': 'Promoción',
    'astrodash.tab.reviews': 'Reseñas',
    'astrodash.tab.stats': 'Estadísticas',
    'astrodash.tab.profile': 'Perfil público',
    'astrodash.tab.settings': 'Configuración y seguridad',
    'astrodash.tab.subscription': 'Suscripción',
    'astrodash.logout': 'Salir',
    'astrodash.backToSite': 'Volver al sitio',
    'astrodash.menu': 'Menú',
    'astrodash.welcome': '¡Hola, {name}!',
    'astrodash.role': 'Astrólogo',

    // Astrologer Dashboard — Overview Tab
    'astrodash.overview.title': 'Resumen',
    'astrodash.overview.subtitle': 'Indicadores clave de hoy',
    'astrodash.overview.todayIncome': 'Ingresos de hoy',
    'astrodash.overview.newOrders': 'Nuevos pedidos',
    'astrodash.overview.activeConsultations': 'Consultas activas',
    'astrodash.overview.profileViews': 'Visitas al perfil',
    'astrodash.overview.rating': 'Valoración',
    'astrodash.overview.position': 'Posición en catálogo',
    'astrodash.overview.quickActions': 'Acciones rápidas',
    'astrodash.overview.recentOrders': 'Pedidos recientes',
    'astrodash.overview.viewAll': 'Ver todos',
    'astrodash.overview.noOrders': 'Aún no hay pedidos',
    'astrodash.overview.schedule': 'Horario de hoy',
    'astrodash.overview.noSchedule': 'No hay citas para hoy',
    'astrodash.overview.tips': 'Consejos para mejorar',
    'astrodash.overview.fillProfile': 'Complete su perfil al 100%',
    'astrodash.overview.addServices': 'Agregue más servicios',
    'astrodash.overview.enablePromo': 'Active la promoción',

    // Astrologer Dashboard — Promotion Tab
    'astrodash.promo.title': 'Promoción del perfil',
    'astrodash.promo.subtitle': 'Aumente pedidos e ingresos',
    'astrodash.promo.budget': 'Presupuesto publicitario',
    'astrodash.promo.available': 'disponible',
    'astrodash.promo.spentMonth': 'Gastado este mes',
    'astrodash.promo.remaining': 'Restante',
    'astrodash.promo.topUp': 'Recargar saldo',
    'astrodash.promo.hint':
      'La publicidad se cobra del presupuesto publicitario. Si no hay fondos suficientes, recargue el saldo.',
    'astrodash.promo.tariff': 'Tarifa',
    'astrodash.promo.position': 'Posición',
    'astrodash.promo.views': 'Visitas',
    'astrodash.promo.conversion': 'Conversión',
    'astrodash.promo.adIncome': 'Ingresos por publicidad',
    'astrodash.promo.adOrders': 'Pedidos por publicidad',
    'astrodash.promo.howToIncrease': 'Cómo aumentar ingresos',
    'astrodash.promo.proAdvice':
      'Cambie a la tarifa PRO y obtenga prioridad en el catálogo. Según estadísticas, los astrólogos PRO reciben un 40% más de pedidos.',
    'astrodash.promo.upgradePro': 'Cambiar a PRO',
    'astrodash.promo.enableAds': 'Activar publicidad',
    'astrodash.promo.launchAd': 'Lanzar publicidad',
    'astrodash.promo.boostProfile': 'Impulsar perfil',
    'astrodash.promo.enableMailing': 'Activar mailing',
    'astrodash.promo.upgradeTariff': 'Mejorar tarifa',
    'astrodash.promo.myAdOrders': 'Mis pedidos publicitarios',
    'astrodash.promo.howAdsWork': '¿Cómo funciona la publicidad?',
    'astrodash.promo.hideHelp': 'Ocultar ayuda',
    'astrodash.promo.newOrder': 'Nuevo pedido',
    'astrodash.promo.step1.title': 'Solicitud y pago',
    'astrodash.promo.step1.desc':
      'Elija el formato, suba materiales y pague con el saldo publicitario.',
    'astrodash.promo.step2.title': 'Moderación (1-12 horas)',
    'astrodash.promo.step2.desc':
      'El administrador revisará los materiales. El resultado llegará a las notificaciones del sistema.',
    'astrodash.promo.step3.title': 'Lanzamiento y estadísticas',
    'astrodash.promo.step3.desc':
      'Tras la aprobación, la publicidad se lanzará automáticamente. Siga las visitas y clics aquí.',
    'astrodash.promo.service': 'Servicio',
    'astrodash.promo.type': 'Tipo',
    'astrodash.promo.status': 'Estado',
    'astrodash.promo.period': 'Período',
    'astrodash.promo.cost': 'Coste',
    'astrodash.promo.result': 'Resultado',
    'astrodash.promo.clicks': 'clics',
    'astrodash.promo.orders': 'pedidos',
    'astrodash.promo.banner': 'Banner',
    'astrodash.promo.mailing': 'Mailing',
    'astrodash.promo.pin': 'Fijado',
    'astrodash.promo.statusActive': 'Activo',
    'astrodash.promo.statusCompleted': 'Completado',
    'astrodash.promo.statusModeration': 'En moderación',
    'astrodash.promo.statusPayment': 'Pendiente de pago',
    'astrodash.promo.statusApproved': 'Aprobado',
    'astrodash.promo.statusRejected': 'Rechazado',
    'astrodash.promo.onModeration': 'Pedido en moderación',
    'astrodash.promo.awaitingPayment': 'Pendiente de pago',
    'astrodash.promo.rejected': 'Pedido rechazado',
    'astrodash.promo.checkingAd':
      'Su publicidad está siendo revisada por el administrador. Normalmente tarda 1-12 horas.',
    'astrodash.promo.moderationPassed':
      '¡Moderación aprobada! Pague el pedido para lanzar la publicidad.',
    'astrodash.promo.moderationFailed':
      'Moderación no aprobada. Corrija las observaciones y envíe de nuevo.',
    'astrodash.promo.rejectionReason': 'Motivo del rechazo',
    'astrodash.promo.stepCreated': 'Creado',
    'astrodash.promo.stepModeration': 'Moderación',
    'astrodash.promo.stepPayment': 'Pago',
    'astrodash.promo.stepLaunch': 'Lanzamiento',
    'astrodash.promo.inProgress': 'En proceso...',
    'astrodash.promo.awaitingPaymentShort': 'Pendiente de pago',
    'astrodash.promo.awaiting': 'Pendiente',
    'astrodash.promo.paid': 'Pagado',
    'astrodash.promo.payBtn': 'Pagar {price} ₽',
    'astrodash.promo.fixAndResend': 'Corregir y enviar',
    'astrodash.promo.repeat': 'Repetir',
    'astrodash.promo.extend': 'Extender',
    'astrodash.promo.income': 'Ingresos',
    'astrodash.promo.createAd': 'Crear publicidad',
    'astrodash.promo.selectSlot':
      'Seleccione un espacio publicitario. Cada formato se muestra en diferentes partes del sitio.',
    'astrodash.promo.format': 'Formato',
    'astrodash.promo.materials': 'Materiales',
    'astrodash.promo.payment': 'Pago',
    'astrodash.promo.next': 'Siguiente',
    'astrodash.promo.back': 'Atrás',
    'astrodash.promo.reach': 'Alcance',
    'astrodash.promo.requirements': 'Requisitos',
    'astrodash.promo.adTitle': 'Título del anuncio',
    'astrodash.promo.adTitlePlaceholder':
      'Ej: Análisis profesional de carta natal',
    'astrodash.promo.adDesc': 'Descripción',
    'astrodash.promo.adDescPlaceholder': 'Describa brevemente su oferta',
    'astrodash.promo.adLink': 'Enlace',
    'astrodash.promo.adImage': 'Subir imagen',
    'astrodash.promo.replaceImage': 'Reemplazar imagen',
    'astrodash.promo.orderSummary': 'Resumen del pedido',
    'astrodash.promo.total': 'Total a pagar',
    'astrodash.promo.adBalance': 'Saldo publicitario',
    'astrodash.promo.insufficientFunds':
      'Fondos insuficientes. Recargue el saldo en {amount} ₽',
    'astrodash.promo.remainingAfter': 'Restante después del pago',
    'astrodash.promo.payAndSubmit': 'Pagar y enviar',
    'astrodash.promo.orderPaid':
      'Pedido pagado y enviado a moderación. Recibirá una notificación sobre el estado.',
    'astrodash.promo.cancel': 'Cancelar',

    // Common astrologer dashboard
    'astrodash.common.save': 'Guardar',
    'astrodash.common.cancel': 'Cancelar',
    'astrodash.common.delete': 'Eliminar',
    'astrodash.common.edit': 'Editar',
    'astrodash.common.add': 'Agregar',
    'astrodash.common.search': 'Buscar',
    'astrodash.common.filter': 'Filtrar',
    'astrodash.common.reset': 'Restablecer',
    'astrodash.common.noData': 'Sin datos',
    'astrodash.common.loading': 'Cargando...',
    'astrodash.common.confirm': 'Confirmar',
    'astrodash.common.close': 'Cerrar',
    'astrodash.common.viewAll': 'Ver todos',
    'astrodash.common.today': 'Hoy',
    'astrodash.common.week': 'Semana',
    'astrodash.common.month': 'Mes',
    'astrodash.common.year': 'Año',
    'astrodash.common.from': 'Desde',
    'astrodash.common.to': 'Hasta',
    'astrodash.common.total': 'Total',
    'astrodash.common.status': 'Estado',
    'astrodash.common.actions': 'Acciones',
    'astrodash.common.details': 'Detalles',
    'astrodash.common.back': 'Atrás',
    'astrodash.common.next': 'Siguiente',
  },
}

export { translations }
